// Core donation checkout flow — shared by widget iframe and donate page
import './checkout.css';
import { loadStripe, Stripe, StripePaymentElement } from '@stripe/stripe-js';
import {
  fetchDesignations,
  fetchOrgBranding,
  fetchPaymentMethods, fetchPaymentIntentStatus,
  createPaymentIntent,
  generateIdempotencyKey,
  createRecurringDonation,
  createPayPalOrder,
  createPayPalSubscription,
  capturePayPalOrder,
  recordDonation,
  fetchReturningDonor,
  fetchWidgetOptions,
  fetchCapturePrompt,
  trackFunnel,
  fetchManualPaymentAccounts,
  submitManualDonation,
  ManualPaymentAccountPublic,
  CampaignOption,
  CapturePromptResponse,
  CascadedDonationSettings,
  Designation,
  DonatePageContextLite,
  DesignationOption,
  OrgBranding,
  PaymentMethodsResult,
  ReturningDonorResult,
} from './api';
import {
  localePhoneCountry,
  normalizePhoneInput,
  phoneInputMarkup,
  phoneValidationMessage,
  resolvePhoneCountry,
} from './phone-input';
import type { CountryCode } from 'libphonenumber-js';
import {
  effectiveAllowRecurring as computeEffectiveAllowRecurring,
  isNoPaymentPledgeFlow,
  shouldShowIntentStep,
  shouldShowPledgeStep,
  type DonorIntent,
} from './cascade-routing';
import { autoCorrectSelectionForRecurring, computeMethodPickerEntries, payByLine } from './payment-method-filter';
import { providerMark } from './provider-marks';
import { donorContactPayload } from './donor';
// CHG-3220 / ITER-905957 — per-currency minor-unit precision. The checkout
// previously hardcoded `* 100` / `/ 100` for ALL currencies, 100x-overcharging
// zero-decimal currencies (JPY/KRW) and 10x-undercharging three-decimal ones
// (BHD/KWD). These mirror src/api/app/core/currency.py; route every
// major<->minor conversion through the toMinorUnits/toMajorUnits helpers below.
import { currencyExponent, majorToMinorUnits, minorUnitsToMajor } from './currency-precision';
// CHG-1657 / ITER-3766 (W3.2) — unified envelope client mirror. The
// envelope ships on /api/v1/public/widget/{widgetId}/options post-CHG-1637;
// helpers below honor context_lock + allowed_designations + amount_bounds
// + provider_capabilities + recurring/pledge_allowed + off_season_action.
// Server-side authoritative gate at
// src/api/app/services/render_constraints.py (mirror, never trust client).
import {
  filterAllowedDesignations,
  filterPaymentMethodsByCapabilities,
  formatLockedTargetConfirmation,
  getMaxMinorUnits,
  getMinMinorUnits,
  getOffSeasonDirective,
  getSuggestedAmountsMinorUnits,
  hasContextLock,
  isPledgeAllowed,
  isRecurringAllowed,
  validateSubmission,
  type UnifiedEnvelope,
  type EnvelopeContextLock,
} from './envelope-constraints';
import {
  checkoutReturnTargetOrigin,
  cleanCheckoutReturnParamsFromCurrentUrl,
  postCheckoutReturnConsumed,
  readCheckoutReturnParams,
} from './iframe-return-bridge';

/**
 * CHG-3240 — Resolve the donor-back `return_url`: the page the donor initiated
 * the donation from. The checkout commonly runs inside the embed iframe served
 * from the API host (`/api/v1/public/widget/{id}/form`, CHG-1782), where
 * `window.location.href` is the API form URL — NOT the host page. The embed
 * loader (`embed.ts`) and platform renderer (`renderer.ts mountCheckoutIframe`)
 * pass the host page URL via the `parent_url` query param. Precedence per
 * CONT-PUBLIC-URL-001 §3 "Gateway donor-back redirect":
 *   1. `parent_url` query param (set by the loader/renderer),
 *   2. `document.referrer` when running inside an iframe,
 *   3. `window.location.href` for the top-level (server-rendered) donate page.
 * Returns with its query stripped, except for explicitly allowlisted transient
 * continuity parameters (the pledge contribution token on an exact checkout).
 */
export function resolveDonorReturnUrl(preserveParams: string[] = []): string {
  const strip = (u: string): string => {
    const withoutFragment = u.split('#')[0];
    if (preserveParams.length === 0) return withoutFragment.split('?')[0];
    try {
      const parsed = new URL(withoutFragment, window.location.href);
      const kept = new URLSearchParams();
      preserveParams.forEach((name) => {
        const value = parsed.searchParams.get(name);
        if (value) kept.set(name, value);
      });
      parsed.search = kept.toString();
      return parsed.toString();
    } catch {
      return withoutFragment.split('?')[0];
    }
  };
  try {
    const inIframe = window.self !== window.top;
    if (inIframe) {
      const fromParam = new URLSearchParams(window.location.search).get('parent_url');
      if (fromParam) return strip(fromParam);
      if (document.referrer) return strip(document.referrer);
    }
  } catch {
    // Cross-origin access to window.top can throw — that itself means we are
    // framed by another origin; fall back to referrer if available.
    if (typeof document !== 'undefined' && document.referrer) {
      return strip(document.referrer);
    }
  }
  return strip(window.location.href);
}

const CHECKOUT_TRANSIENT_SHARE_PARAMS = [
  'payment',
  'intent',
  'provider',
  'payment_id',
  'paymentId',
  'paypal',
  'token',
  'PayerID',
  'payer_id',
  'payment_intent',
  'payment_intent_client_secret',
  'redirect_status',
  'setup_intent',
  'setup_intent_client_secret',
  'pledge_contribution',
];

export function cleanDonationShareUrl(rawUrl: string): string {
  const fallbackBase = typeof window !== 'undefined' && window.location
    ? window.location.href
    : String(rawUrl || '');
  try {
    const url = fallbackBase
      ? new URL(rawUrl || fallbackBase, fallbackBase)
      : new URL(rawUrl);
    CHECKOUT_TRANSIENT_SHARE_PARAMS.forEach((param) => url.searchParams.delete(param));
    return url.toString();
  } catch {
    return String(rawUrl || fallbackBase).split('#')[0].split('?')[0];
  }
}

export interface SessionProofConfig {
  status?: string;
  raised_minor_units?: number | null;
  goal_amount_minor_units?: number | null;
  formatted_raised?: string | null;
  formatted_goal?: string | null;
  progress_pct?: number | null;
  donation_count?: number | null;
  pledge_count?: number | null;
  contributor_count?: number | null;
  participant_count?: number | null;
  currency?: string | null;
  locale?: string | null;
}

export interface PaymentReturnConfig {
  payment?: string | null;
  intent?: string | null;
  status?: string | null;
  amount_minor_units?: number | null;
  currency?: string | null;
  designation_id?: string | null;
  campaign_id?: string | null;
  checkout_attempt_id?: string | null;
  retry_of_payment_intent_id?: string | null;
}

export interface CheckoutConfig {
  organizationId: string;
  campaignId?: string;
  sessionId?: string;
  sessionProof?: SessionProofConfig | null;
  showSessionProof?: boolean;
  paymentReturn?: PaymentReturnConfig | null;
  /** CHG-3553 / BUG-1278 — non-PII authority for a reminder-email payment.
   * The token resolves donor + exact pledge server-side; the client may only
   * render the locked amount/currency and let the donor choose a provider. */
  pledgeContribution?: {
    token: string;
    amount_minor_units: number;
    currency: string;
  } | null;
  // CHG-0463 (CHG-0438 Step 4 cleanup) — was ``channel``; the widget
  // surface now identifies itself via ``source`` (entry path).
  source: 'widget' | 'donate-page' | 'session';
  placement?: 'embedded' | 'full'; // embedded = org's own site, full = third-party with full branding
  // CHG-0171 W4 — preset amounts no longer come from the widget config.
  // They cascade from the selected Campaign / Designation's
  // suggested_amounts (via the backend /options endpoint's cascade
  // block). The field below remains only as a defensive default used
  // when no entity has been selected yet (e.g. a blank embed renderer).
  presetAmounts?: number[]; // Minor currency units (cents). DEPRECATED.
  // Hosted sessions treat presetAmounts as authoritative, including an empty list.
  presetAmountSource?: 'inherit_target' | 'custom' | 'platform_default' | 'none';
  currency?: string;
  thankYouMessage?: string;
  theme?: 'light' | 'dark' | 'brand';
  primaryColor?: string;
  stripePublishableKey?: string;
  // Connected Stripe account (acct_*) — required when the org connects
  // via Stripe Connect on-behalf-of so the PI client_secret unlocks
  // under the correct account context. Auto-populated from the
  // /public/payment-methods response when not supplied by the host page.
  stripeConnectAccountId?: string;
  mountSelector?: string; // defaults to '#donation-checkout'
  headerText?: string;
  // Org brand identity (populated by backend for full placement)
  orgBrand?: {
    name?: string;
    logo_url?: string;
    tagline?: string;
    primary_color?: string;
    secondary_color?: string;
    heading_font?: string;
    body_font?: string;
    button_radius?: string;
  };
  minAmount?: number; // Minor currency units (cents). DRIFT-1181: consumed as minor by effectiveMinMinorUnits(); default 1 = a 1-cent (inert) floor.
  maxAmount?: number; // Minor currency units (cents). DRIFT-1181: consumed as minor by effectiveMaxMinorUnits(); default 0 = no maximum.
  // CHG-0171 W2/W4 — widget-level narrow-only overrides. The backend
  // already applies these into the per-entity `cascade` block, so the
  // widget frontend should read cascade.allow_recurring /
  // cascade.allow_pledge rather than checking these flags again.
  // They are kept on CheckoutConfig only for the embed-parameter
  // path (embed.js → DC({...}) constructor) where the cascade
  // isn't available until the selected entity is known.
  disableRecurring?: boolean;
  disablePledge?: boolean;
  /** Positive hosted CAMPAIGN/SESSION root policy. Unlike disablePledge this
   *  is not an embed override: the server resolved entity policy emits it in
   *  checkout_config, including on SESSION pages that have no cascade payload. */
  allowPledges?: boolean;
  /** @deprecated CHG-0171 W2 — replaced by inverted disableRecurring. */
  showFrequency?: boolean;
  showDonorInfo?: boolean;
  showAnonymous?: boolean;
  /** Donor-step layout variant. 'stacked' (default) keeps the historical
   *  one-field-per-row form used by every embed/kiosk/mobile placement. 'grid'
   *  opts into the donate-page v3 mock layout: Mobile + Email side-by-side in a
   *  2-column row and the "Donate anonymously" control rendered as a bordered
   *  switch-row toggle. Set ONLY by the hosted donate-page checkout_config; the
   *  default is unchanged so non-opted orgs and bare embeds are byte-identical.
   *  (donate-page v3 — donor-step parity with template-v3 mocks.) */
  donorLayout?: 'stacked' | 'grid';
  /** Form styling variant for the whole step flow. undefined (default) =
   *  historical widget styling for every embed/kiosk/mobile placement. 'v3'
   *  opts into the donate-page mock parity tweaks: step eyebrows, "Choose your
   *  gift" amount title, payment vertical radio rows. Set ONLY by the hosted
   *  donate-page checkout_config, so bare embeds and other orgs are unchanged.
   *  (donate-page v3 — step parity with template-v3 mocks.) */
  formStyle?: 'v3';
  /** CHG-3147 — prefill the donor-info step for an authenticated donor (the
   *  dashboard "My Giving" portal passes the logged-in account's identity).
   *  Empty/absent on the public donate page, so that flow is unchanged. When
   *  the prefill is complete enough to satisfy the org's contact requirements,
   *  the donor step is auto-skipped to payment (see donorPrefillSatisfiesRequirements). */
  defaultDonorName?: string;
  defaultDonorEmail?: string;
  defaultDonorPhone?: string;
  /** CHG-3188 (Donate-Now S5) — LOCK the donor email to defaultDonorEmail so a
   *  logged-in donor cannot edit it on the donor-info step. The deterministic
   *  account→Donor link (server-side _try_link_account matches the submitted
   *  donor_email to the Account email) depends on the email being EXACTLY the
   *  authenticated account's email; if the donor retyped a different address we
   *  would create an orphan Donor (person_id NULL) and the account would never
   *  flip to a real donor. When true: the Email input renders readonly, the
   *  "Donate anonymously" toggle is suppressed (anonymous nulls donor_email,
   *  which would defeat the link), and saveDonor() re-asserts the locked value.
   *  Only honored when defaultDonorEmail is non-empty. */
  lockDonorEmail?: boolean;
  /** CHG-3188 (Donate-Now S5) — fired exactly once when a donation reaches the
   *  'confirm' (Thank-you) success step, regardless of payment path. The
   *  authenticated Donate-Now hand-off uses it to re-derive the session (the
   *  account has just become a real donor server-side) and route to the now-
   *  populated /my-giving home. Same-JS-context only (DonateOverlay mounts the
   *  bundle in the host window, no iframe), so a direct function callback fires. */
  onSuccess?: () => void;
  fontFamily?: string;
  borderRadius?: number;
  buttonStyle?: 'filled' | 'outlined';
  themeOverrides?: Record<string, string>;
  inheritOrgBranding?: boolean;
  brandFontsUrl?: string;
  brandHeadingFont?: string;
  brandButtonRadius?: string;
  scopeType?: 'all' | 'categories' | 'campaign';
  designationIds?: string[] | null;
  // CHG-0171 W4 — widget identifier so checkout.ts can refresh the
  // /options envelope. Present for widget-embedded checkouts; absent
  // for donate-page and session flows (which don't have a widget row).
  widgetId?: string;
  // CHG-0171 W4 — embedded /options envelope delivered via the
  // /config endpoint; avoids a second round-trip when the widget is
  // bound to a single entity. Refreshed only on picker reopen.
  options?: import('./api').WidgetOptions | null;
  previewMode?: boolean;
  // Phase 1 builder options
  defaultFrequency?: 'one-time' | 'monthly' | 'quarterly';
  // CHG-0047 P6 — server-driven recurring intents (Jumu'ah, Shabbat,
  // Sunday tithe, etc.). When present, the frequency step renders
  // intent cards sourced from GET /api/v1/recurring-intents/public
  // instead of the hardcoded one-time/monthly/quarterly picker. The
  // parent page (donate page or embed host) is expected to fetch and
  // pass the intents via config before the widget mounts. Leaving
  // undefined falls back to the legacy frequency picker.
  recurringIntents?: Array<{
    intent_key: string;
    label: string;
    description?: string;
    eligible_providers: string[];
    attestation_status: 'pending_review' | 'sme_attested' | 'community_validated';
    schedule_preview?: string[];
  }>;
  // CHG-0047 P7 — when true, pending_review intents render the
  // "Cultural guidance under review" ribbon. Defaults true; org admins
  // disable via cultural_settings.hide_unattested_intents.
  culturalHintsEnabled?: boolean;
  coverFeesEnabled?: boolean;
  coverFeesPercent?: number;
  coverFeesFlatMinorUnits?: number; // Flat fee in cents added to percentage (default 30 — Stripe US pricing)
  amountImpacts?: Array<{ amount_minor_units: number; label: string }>;
  showRecurringUpsell?: boolean;
  showShareButtons?: boolean;
  orgName?: string;
  locale?: string;
  /** Initial phone-country hint. Donors remain free to select another country. */
  organizationCountry?: string;
  timezone?: string;
  shareUrl?: string; // Public URL for share buttons (overrides window.location.href in iframe contexts)
  pageId?: string;
  variantId?: string;
  // Phase 2 checkout enhancements
  showConfetti?: boolean;
  showDedication?: boolean;
  showCommentField?: boolean;
  showImpactStatement?: boolean;
  // CHG-0155 P6: vocabulary accepts the full variant overlay (donation,
  // donor, campaign, ...) projected from the OrgBranding response. Widget
  // reads donate_button today; future iterations may consume more keys.
  vocabulary?: { donate_button?: string } & Record<string, string>;
  // CHG-1466 (γ.code.2) — page-context fields passed from the donate-page
  // bootstrap when the public endpoint returns the new envelope. When
  // contextLock is set the designation picker is suppressed and a single-
  // line confirmation is shown instead.
  pageContextType?: string;
  pageContextEntityId?: string;
  designationOptions?: DesignationOption[];
  contextLock?: { kind: 'designation' | 'campaign'; id: string; displayName?: string };
  /** @deprecated CHG-3762 / GH-714. Event is informational and this value is
   * ignored. Use a linked SESSION context + sessionId for fundraising. */
  eventId?: string;
}

interface DonorInfo {
  name: string;
  email: string;
  phone: string;
  anonymous: boolean;
}

function isValidDonorEmailSyntax(value: string): boolean {
  const email = value.trim();
  if (!email) return true;
  if (/\s/.test(email)) return false;
  const at = email.indexOf('@');
  if (at < 1 || email.indexOf('@', at + 1) !== -1) return false;
  const local = email.slice(0, at);
  if (local.startsWith('.') || local.endsWith('.') || local.includes('..')) return false;
  if (/[()<>\[\]:;,"\\\p{C}]/u.test(local)) return false;
  const domain = email.slice(at + 1);
  const labels = domain.split('.');
  return labels.length > 1 && labels.every((label) =>
    /^(?:[a-z0-9-]|[^\x00-\x7f])+$/iu.test(label) && !label.startsWith('-') && !label.endsWith('-'),
  );
}

// ITER-906207 / CHG-3475 moved phone rendering + strict validation into the
// canonical `./phone-input.ts` primitive. `contact-input.ts` retains only the
// email-vs-phone classifier used by bi-channel lookup surfaces.

// Step enum
// CHG-0932 / ITER-C2 — 'pledge' added for the pledge-fallback step
// shown when cascade.effective_allow_pledge is true but
// cascade.effective_allow_recurring is false. Renders between
// amount and donor (the recurring vehicle isn't available to this
// donor; pledge is the alternative commitment shape per program
// plan §5.4).
type Step = 'loading' | 'intent' | 'designation' | 'amount' | 'frequency' | 'pledge' | 'donor' | 'payment' | 'confirm' | 'error';

// CHG-L — widget error recovery. Init-time failures route through
// setError(code) so the error step can render an actionable message
// (retry button for transient failures, contact link as last-resort
// recovery). Prior to CHG-L the error step was a bare emoji + raw
// errorMessage string with no recovery path.
type WidgetErrorCode =
  | 'no_designations'   // org has no donation options configured for this scope
  | 'campaign_inactive' // scope=campaign but the bound campaign no longer accepts donations
  | 'load_failed'       // init() network / API failure — transient, retryable
  | 'unknown';          // anything else that reaches the error step

type PaymentReturnState = {
  payment_intent_id?: string;
  amount?: number;
  total_amount_minor_units?: number | null;
  currency?: string | null;
  cover_fees_checked?: boolean;
  donor?: Partial<DonorInfo> | null;
  designation_id?: string | null;
  campaign_id?: string | null;
  checkout_attempt_id?: string | null;
  retry_of_payment_intent_id?: string | null;
};

const ERROR_MESSAGES: Record<WidgetErrorCode, string> = {
  no_designations:
    'No donation options have been set up yet. Please check back later.',
  campaign_inactive:
    'This campaign is not accepting donations right now. Please check back later.',
  load_failed:
    "We couldn't load the donation form. This is usually temporary — please try again in a moment.",
  unknown: 'Something went wrong. Please try again.',
};

// QA #359 — Chromium matches :focus-visible on a pointer-opened native select.
// Track the last input modality once per document so a Tab that starts outside
// the widget is still observed. The WeakSet prevents checkout remounts from
// accumulating document listeners, and pointerdown covers mouse, touch, and pen.
const focusModalityDocuments = new WeakSet<Document>();

function ensureCheckoutFocusModalityTracking(doc: Document): void {
  if (focusModalityDocuments.has(doc)) return;

  const root = doc.documentElement;
  const setModality = (modality: 'pointer' | 'keyboard') => {
    root.dataset.dkFocusModality = modality;
  };
  const onPointerDown = () => setModality('pointer');
  const onKeyDown = () => setModality('keyboard');
  const onFocusOut = (event: FocusEvent) => {
    if ((event.target as Element | null)?.id === 'dk-offline-account' && event.relatedTarget === null) {
      delete root.dataset.dkFocusModality;
    }
  };

  doc.addEventListener('pointerdown', onPointerDown, { capture: true, passive: true });
  doc.addEventListener('keydown', onKeyDown, { capture: true });
  doc.addEventListener('focusout', onFocusOut, { capture: true });
  focusModalityDocuments.add(doc);
}

export class DonationCheckout {
  private config: Required<CheckoutConfig>;
  private container: HTMLElement;
  // The stable mount element. `container` is temporarily swapped to the
  // #dk-active-step child during the async payment render (renderPaymentInto);
  // render() pins the full-shell rebuild to `rootContainer` so it can never nest
  // a second stepper inside the active step (stacked-stepper defect).
  private rootContainer: HTMLElement;
  private designations: Designation[] = [];
  private selectedDesignation: Designation | null = null;
  // CHG-0171 W4 — per-entity cascade (effective donor-facing values
  // already narrowed by the widget's disable_*/min/max overrides on
  // the backend). Set when a Campaign/Designation is selected.
  // null = no entity selected yet (default-widget initial render).
  private selectedCascade: CascadedDonationSettings | null = null;
  // Available Campaign + Designation options for the default widget
  // picker (scope_type='all'). Populated from the /options endpoint.
  private campaignOptions: CampaignOption[] = [];
  private designationOptions: DesignationOption[] = [];
  private selectedCampaign: CampaignOption | null = null;
  private selectedAmount = 0;
  // Confirmed gateway charge total in MAJOR units. Set after a provider return
  // succeeds so the receipt can recover even when sessionStorage is unavailable,
  // without re-applying cover-fee math to an already-totalled PaymentIntent.
  private confirmedTotalAmount: number | null = null;
  // CHG-MANUALPAY — donor-visible offline payment accounts (bKash/Nagad/bank) for
  // orgs without a gateway. Empty when the org has none configured.
  private manualAccounts: ManualPaymentAccountPublic[] = [];
  private donorInfo: DonorInfo = { name: '', email: '', phone: '', anonymous: false };
  // CHG-3147 — true when defaultDonor* prefilled a complete identity (name +
  // at least one contact). Gates the donor-step auto-skip; only set by an
  // authenticated portal donate, never by the public donate page.
  private donorPrefilled = false;
  // CHG-0168 P1 — capture-prompt decision for this donor/origin/country.
  // Null until fetched (or on fetch failure — we fail-open to the
  // pre-P1 hardcoded email+phone optional contract).
  private capturePrompt: CapturePromptResponse | null = null;
  private selectedFrequency: 'one-time' | 'monthly' | 'quarterly' | 'annual' = 'one-time';
  // CHG-0047 P6 — when config.recurringIntents is set, the frequency
  // step renders intent cards and the donor's selection is recorded
  // here. Null = one-time (no recurring). Set alongside selectedFrequency
  // for backward compat with server code that still reads frequency.
  private selectedIntentKey: string | null = null;
  // CHG-0932 / ITER-C2 — pledge-step state. Set when the donor
  // commits a pledge via POST /api/v1/public/pledges; threaded into
  // the cycle-0 PaymentIntent's metadata so donation-engine's
  // auto-match wiring can apply the donation as the first
  // PledgePayment (pre-existing path; no new backend wiring).
  // Per program plan §5.4 the cycle-0 charge is a one-time donation
  // (NOT a recurring mandate), and the pledge persists independent
  // of the payment outcome — if the payment fails, reminder cron
  // re-engages the donor.
  private pledgeId: string | null = null;
  private pledgeDonorId: string | null = null;
  // CHG-3053 / DRIFT-1165 — snapshot of the MATERIAL values the persisted
  // pledge was created with (the amount + cadence sent to POST /public/pledges).
  // Captured on a successful submitPledge() and compared at the post-create
  // material-edit points (amount-step commit, cadence change). If the donor goes
  // back and edits either to a value that diverges from this snapshot, the
  // committed pledge is SUPERSEDED (pledgeId cleared → isPledgeFlow() flips back
  // to true → the donor-step "Continue" re-runs submitPledge() with a fresh
  // idempotency key) so the persisted pledge can never silently diverge from the
  // cycle-0 charge built off selectedAmount. Null when no pledge exists.
  private pledgedAmountMinorUnits: number | null = null;
  private pledgedFrequency: 'one-time' | 'monthly' | 'quarterly' | 'annual' | null = null;
  private pledgeSubmitting = false;
  private pledgeError: string | null = null;
  // CHG-3410 — null means the intent fork was not shown, preserving the
  // capability-fallback contract. "later" is the donor-elected no-charge path.
  private donorIntent: DonorIntent = null;
  private intentVetoNotice = '';
  private pledgeStartDate = '';
  private pledgeDueDate = '';
  private pledgeNextReminderAt = '';
  // CHG-3027 / PLG-R01a (pledge-management-remediation §PLG-F01) — STABLE
  // per-pledge-attempt idempotency key. Minted lazily on the first submit of an
  // attempt and reused across retries of the SAME pledge (network blip /
  // transient 5xx) so the public route dedupes (once it honors the header in
  // R01c) instead of creating a second Pledge. Cleared on a material payload
  // reset (amount re-commit / cadence change) and on a successful create, so a
  // genuinely different pledge mints a fresh key. Empty string ('') == "no
  // attempt in flight"; the `||=` mint site treats '' as unset.
  private pledgeAttemptKey = '';
  private stripe: Stripe | null = null;
  private paymentElement: StripePaymentElement | null = null;
  private readonly donorReturnUrl: string;
  private paymentIntentId = '';
  // CHG-2813 (DRIFT-1084a) — STABLE per-donation-attempt idempotency key.
  // Minted ONCE (lazily, on entry to the Stripe/sandbox payment flow) and
  // reused across createPaymentIntent retries of the SAME charge, so a
  // network-blip retry dedupes at Stripe instead of minting a SECOND
  // PaymentIntent (double-charge). Cleared by the #dk-restart "donate again"
  // handler so a genuinely new gift mints a fresh key — distinct donations
  // never collide and silently dedupe. Empty string ('') == "no attempt in
  // flight"; the `||=` mint sites below treat '' as unset.
  private donationAttemptKey = '';
  // Stable across replacement PaymentIntents for one logical checkout. The
  // idempotency key above is intentionally reset when a failed provider intent
  // is replaced; this correlation id is not.
  private checkoutAttemptId = '';
  private retryOfPaymentIntentId = '';
  private step: Step = 'loading';
  // CHG-3478 — hosted SESSION pages may retain a terminal gateway receipt
  // while authoritative lifecycle acceptance is paused/closed. Receipt
  // visibility and permission to start another contribution are deliberately
  // separate and both the rendered control and its handler enforce this flag.
  private contributionsAccepted = true;
  // CHG-3225 (fix #264) — tracks the step the enter-transition last played for,
  // so the active step's fade/slide-in animates only on a genuine step change,
  // not on the in-step re-renders (error/submitting states, method toggles).
  private lastAnimatedStep: Step | null = null;
  private paymentMethods: PaymentMethodsResult | null = null;
  private selectedPaymentMethod: string = 'stripe';
  private errorMessage = ERROR_MESSAGES.unknown;
  // CHG-L — classification code for the error step; drives whether
  // the "Try again" retry button renders (retryable codes only).
  private errorCode: WidgetErrorCode | null = null;
  // CHG-L — stored so the error step can render a support-contact
  // link when OrgBranding.contact_email is populated.
  private branding: OrgBranding | null = null;
  // CHG-P — populated from OrgBranding.supported_currencies. Drives the
  // amount-step currency dropdown when length > 1; null/length≤1 hides
  // the picker (legacy single-currency UX).
  private supportedCurrencies: string[] | null = null;
  private phoneCountry: CountryCode;
  private coverFeesChecked = false;
  private returningDonor: ReturningDonorResult | null = null;
  private dedication: { honoree: string; notifyEmail: string } = { honoree: '', notifyEmail: '' };
  private dedicationEnabled = false;
  private donorComment = '';
  // CHG-1657 (W3.2) — unified envelope from /options (or /donate-pages/
  // public-by-id/{id} when the donate-page bootstrap passed it through).
  // Null until /options resolves. Populated alongside campaignOptions /
  // designationOptions in init(); helpers in envelope-constraints.ts
  // accept null and fall through to legacy behavior so non-envelope
  // (pre-CHG-1637 backend) deployments still render correctly.
  private envelope: UnifiedEnvelope | null = null;
  // CHG-1657 (W3.2) — resolved context lock. Prefers envelope's
  // context_lock when present; otherwise falls back to
  // config.contextLock (donate-page bootstrap path from CHG-1466).
  // Both shapes carry {kind, id, display_name}; we project to a single
  // canonical shape so all rendering sites consume the same field.
  private effectiveContextLock: EnvelopeContextLock | null = null;

  constructor(config: CheckoutConfig) {
    this.config = {
      placement: 'embedded',
      orgBrand: {},
      presetAmounts: [2500, 5000, 10000, 25000],
      currency: 'USD',
      locale: config.locale || (typeof navigator !== 'undefined' ? navigator.language : 'en-US') || 'en-US',
      timezone: config.timezone || (typeof Intl !== 'undefined' ? Intl.DateTimeFormat().resolvedOptions().timeZone : 'UTC'),
      thankYouMessage: 'Thank you for your generous donation!',
      theme: 'light',
      primaryColor: '#0F766E',
      stripePublishableKey: '',
      mountSelector: '#donation-checkout',
      headerText: '',
      minAmount: 1,
      maxAmount: 0,
      showFrequency: true,
      showDonorInfo: true,
      showAnonymous: true,
      donorLayout: 'stacked',
      formStyle: undefined,
      fontFamily: '',
      borderRadius: 8,
      buttonStyle: 'filled' as const,
      themeOverrides: {},
      inheritOrgBranding: true,
      previewMode: false,
      defaultFrequency: 'one-time',
      coverFeesEnabled: false,
      coverFeesPercent: 3.0,
      coverFeesFlatMinorUnits: 30,
      amountImpacts: [],
      showRecurringUpsell: false,
      showShareButtons: false,
      orgName: '',
      shareUrl: '',
      pageId: '',
      variantId: '',
      sessionProof: null,
      showSessionProof: true,
      paymentReturn: null,
      pledgeContribution: null,
      showConfetti: false,
      showDedication: false,
      showCommentField: false,
      showImpactStatement: false,
      vocabulary: { donate_button: 'Donate' },
      pageContextType: '',
      ...config,
    } as Required<CheckoutConfig>;

    this.phoneCountry = resolvePhoneCountry(
      config.organizationCountry,
      localePhoneCountry(this.config.locale),
    );

    const el = document.querySelector<HTMLElement>(this.config.mountSelector);
    if (!el) throw new Error(`Mount element "${this.config.mountSelector}" not found`);
    this.container = el;
    this.rootContainer = el;
    ensureCheckoutFocusModalityTracking(el.ownerDocument);
    // Capture once before return-param cleanup. Redirect-provider retry flows can
    // otherwise lose parent_url and fall back to the API-hosted iframe form URL.
    this.donorReturnUrl = resolveDonorReturnUrl(
      this.config.pledgeContribution ? ['pledge_contribution'] : [],
    );
    this.container.classList.add('dk-checkout', `dk-theme-${this.config.theme}`);
    // donate-page v3 form scope — lets checkout.css restyle steps to the
    // template-v3 mock (payment vertical radio rows, etc.) ONLY for opted-in
    // hosted donate pages. Bare embeds/kiosk/other orgs never get this class.
    if (this.config.formStyle === 'v3') {
      this.container.classList.add('dk-form-v3');
    }
    if (this.config.theme === 'brand' && this.config.primaryColor) {
      this.container.style.setProperty('--dk-accent', this.config.primaryColor);
    }

    // Apply per-widget theme overrides
    if (this.config.themeOverrides) {
      Object.entries(this.config.themeOverrides).forEach(([key, value]) => {
        this.container.style.setProperty(`--dk-${key}`, value);
      });
    }
    if (this.config.fontFamily) {
      this.container.style.setProperty('--dk-font', this.config.fontFamily);
    }
    if (this.config.borderRadius != null) {
      this.container.style.setProperty('--dk-radius', `${this.config.borderRadius}px`);
      this.container.style.setProperty('--dk-radius-lg', `${this.config.borderRadius + 4}px`);
    }

    // Apply default frequency from config
    if (this.config.defaultFrequency && this.config.defaultFrequency !== 'one-time') {
      this.selectedFrequency = this.config.defaultFrequency;
    }
    if (Array.isArray(this.config.designationOptions)) {
      this.designationOptions = this.config.designationOptions;
    }

    // CHG-1657 (W3.2) — seed effectiveContextLock from the legacy
    // config.contextLock (CHG-1466 donate-page bootstrap path) so the
    // donate-page flow continues to work even when no envelope is fetched
    // (source='donate-page' widgets don't always go through /options).
    // captureEnvelope() overrides this when the unified envelope is
    // present.
    if (this.config.contextLock) {
      this.effectiveContextLock = {
        kind: this.config.contextLock.kind,
        id: this.config.contextLock.id,
        display_name: this.config.contextLock.displayName || '',
      };
    }

    // CHG-3147 — prefill the donor identity from the authenticated portal.
    // Runs after the config merge so this.config holds the defaultDonor* values.
    const _pn = (this.config.defaultDonorName || '').trim();
    const _pe = (this.config.defaultDonorEmail || '').trim();
    const _pp = (this.config.defaultDonorPhone || '').trim();
    if (_pn || _pe || _pp) {
      const normalizedPrefill = _pp ? normalizePhoneInput(_pp, this.phoneCountry) : null;
      if (normalizedPrefill) this.phoneCountry = normalizedPrefill.country;
      this.donorInfo = {
        name: _pn,
        email: _pe,
        phone: normalizedPrefill?.e164 || _pp,
        anonymous: false,
      };
      // "complete" = a name AND at least one contact channel.
      // An invalid phone-only prefill must not bypass the donor step.
      this.donorPrefilled = !!(_pn && (_pe || normalizedPrefill));
    }

    // Expose instance for preview mode only (avoid globalThis leak in production)
    if (this.config.previewMode) {
      (globalThis as any).__checkoutInstance = this;
    }
  }

  async init(): Promise<void> {
    this.render();
    // CHG-3147 — when the donor is prefilled, fetch the org contact-policy
    // up front so the amount→donor transition can decide whether to auto-skip
    // the donor step (the prompt is otherwise fetched lazily on that step).
    if (this.donorPrefilled) void this.fetchCapturePromptIfNeeded();
    try {
      const [designations, branding, methods, manualAccounts] = await Promise.all([
        fetchDesignations(this.config.organizationId, this.config.scopeType === 'categories' ? this.config.designationIds : undefined),
        this.config.inheritOrgBranding !== false
          ? fetchOrgBranding(this.config.organizationId).catch((): OrgBranding => ({ name: '' }))
          : Promise.resolve({ name: '' } as OrgBranding),
        fetchPaymentMethods(this.config.organizationId).catch((): PaymentMethodsResult => ({
          methods: [],
          stripe: !!this.config.stripePublishableKey,
          paypal: false,
        })),
        // CHG-MANUALPAY — offline accounts (already swallows errors → []).
        fetchManualPaymentAccounts(this.config.organizationId),
      ]);
      this.designations = designations;
      this.paymentMethods = methods;
      this.manualAccounts = manualAccounts;
      this.branding = branding;
      const orgPhoneCountry = resolvePhoneCountry(branding.country, this.phoneCountry);
      const prefilledInternationalPhone = this.donorInfo.phone.trim().startsWith('+')
        ? normalizePhoneInput(this.donorInfo.phone, orgPhoneCountry)
        : null;
      this.phoneCountry = prefilledInternationalPhone?.country ?? orgPhoneCountry;
      // CHG-1657 (W3.2) — provider_capabilities filter is wired here
      // (post-method-load, pre-envelope-application below). Note that
      // envelope.constraints.provider_capabilities is still null on the
      // current backend (deferred — γ.code.3 / resolver step 3 per
      // services/widget_options.py:601); when that resolver lands, this
      // call will silently begin filtering the array. Pre-resolver-step-3,
      // it's a no-op because the helper short-circuits on null.

      // CHG-0171 W4 — seed cascade options from the embedded /options
      // envelope when the backend delivered it via /config. Keeps the
      // widget one round-trip lighter; refreshes via fetchWidgetOptions
      // only if the donor reopens the picker.
      //
      // CHG-1657 (W3.2) — also capture the top-level unified envelope
      // (page_context + constraints + context_lock) from the same
      // payload. The envelope is the authoritative gate; legacy
      // designationIds/min/max/disable_* config remain as fallbacks for
      // pre-CHG-1637 backends.
      if (this.config.options) {
        this.campaignOptions = this.config.options.campaigns ?? [];
        this.designationOptions = this.config.options.designations ?? [];
        this.captureEnvelope(this.config.options);
      } else if (this.config.widgetId) {
        // No embedded options but widget id is available — fetch lazily.
        // Failure is non-fatal; falls back to legacy config flags.
        try {
          const opts = await fetchWidgetOptions(this.config.widgetId);
          this.campaignOptions = opts.campaigns ?? [];
          this.designationOptions = opts.designations ?? [];
          this.captureEnvelope(opts);
        } catch {
          /* fall through; effective* helpers handle missing cascade */
        }
      }

      // CHG-1657 (W3.2) — off_season_action handling. 'reject' renders a
      // closed-for-season message in place of the donation form;
      // 'redirect' navigates the iframe parent to the configured URL
      // (best-effort: top-frame navigation may be blocked by sandbox /
      // X-Frame-Options on the embedding page, in which case the child
      // iframe location changes instead — both are acceptable degrade
      // paths). 'accept' (default) falls through to normal flow.
      if (this.envelope) {
        const directive = getOffSeasonDirective(this.envelope);
        if (directive.kind === 'reject') {
          this.renderOffSeasonClosed();
          return;
        }
        if (directive.kind === 'redirect') {
          this._navigateOut(directive.url);
          return;
        }
      }

      // CHG-1657 (W3.2) — allowed_designations narrows the picker. Server
      // is authoritative; this is a UX-quality client mirror so the donor
      // never sees a designation they would then be blocked from picking.
      // Empty allowed_designations means unconstrained (helper no-ops).
      this.designations = filterAllowedDesignations(this.designations, this.envelope);

      // CHG-1657 (W3.2) — provider_capabilities narrows the payment
      // method list. No-op when envelope.constraints.provider_capabilities
      // is null (current backend default; will activate when resolver
      // step 3 lands). The legacy `org_provider_capabilities` capability-
      // token shape is also honored upstream by the cascade resolver
      // (CHG-0905 / ITER-A2); this filter is the explicit per-method
      // gate the donor surface needs to render visually.
      if (this.paymentMethods?.methods) {
        this.paymentMethods = {
          ...this.paymentMethods,
          methods: filterPaymentMethodsByCapabilities(
            this.paymentMethods.methods,
            this.envelope,
          ),
        };
      }

      // Use stripe publishable key from payment methods if not provided in config
      if (methods.stripe_publishable_key && !this.config.stripePublishableKey) {
        this.config.stripePublishableKey = methods.stripe_publishable_key;
      }
      // Pull through the connected Stripe account ID so loadStripe() can
      // pass ``stripeAccount`` and Elements operates in the org's
      // connected-account context (PI client_secret unlocks correctly).
      if (methods.stripe_connect_account_id && !this.config.stripeConnectAccountId) {
        this.config.stripeConnectAccountId = methods.stripe_connect_account_id;
      }

      // donate-page v3: do NOT pre-select a method — the donor picks one on the
      // Payment step (no provider auto-inits / auto-redirects). Other surfaces
      // keep the historical "default to first available" behavior.
      if (this.config.formStyle === 'v3') {
        this.selectedPaymentMethod = '';
      } else {
        // Default to first available non-manual method
        const available = methods.methods?.filter(m => m.type !== 'manual') ?? [];
        if (available.length > 0) {
          const defaultMethod = available.find(m => m.is_default) || available[0];
          this.selectedPaymentMethod = defaultMethod.provider_key;
        } else if (methods.paypal && !methods.stripe) {
          this.selectedPaymentMethod = 'paypal';
        } else {
          this.selectedPaymentMethod = 'stripe';
        }
      }

      // For full placement, always apply brand theme and use orgBrand data
      if (this.config.placement === 'full') {
        this.container.classList.remove('dk-theme-light', 'dk-theme-dark');
        this.container.classList.add('dk-theme-brand');
        const ob = this.config.orgBrand || {};
        if (ob.primary_color) {
          this.container.style.setProperty('--dk-accent', ob.primary_color);
        }
        if (ob.heading_font) {
          this.container.style.setProperty('--dk-heading-font', `'${ob.heading_font}', system-ui, sans-serif`);
        }
        if (ob.body_font) {
          this.container.style.setProperty('--dk-font', `'${ob.body_font}', system-ui, sans-serif`);
        }
        if (ob.button_radius) {
          const px = parseInt(ob.button_radius, 10);
          if (!isNaN(px)) {
            this.container.style.setProperty('--dk-radius', `${px}px`);
            this.container.style.setProperty('--dk-radius-lg', `${px + 4}px`);
          }
        }
        // Set org name for thank-you messages
        if (ob.name && !this.config.orgName) {
          this.config.orgName = ob.name;
        }
      }

      // Apply org branding: color, fonts, radius, locale, currency
      if (this.config.theme === 'brand' && branding.primary_color) {
        this.container.style.setProperty('--dk-accent', branding.primary_color);
      }
      // Inject brand template fonts + radius from org public API
      const fontsUrl = (branding as any).google_fonts_url || this.config.brandFontsUrl;
      if (fontsUrl && !document.querySelector(`link[href="${fontsUrl}"]`)) {
        const link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = fontsUrl;
        document.head.appendChild(link);
      }
      const bodyFont = (branding as any).body_font || this.config.fontFamily;
      if (bodyFont && !this.config.fontFamily) {
        this.container.style.setProperty('--dk-font', `'${bodyFont}', system-ui, sans-serif`);
      }
      const brandRadius = (branding as any).button_radius || this.config.brandButtonRadius;
      if (brandRadius && this.config.borderRadius == null) {
        const px = parseInt(brandRadius, 10);
        if (!isNaN(px)) {
          this.container.style.setProperty('--dk-radius', `${px}px`);
          this.container.style.setProperty('--dk-radius-lg', `${px + 4}px`);
        }
      }
      if (branding.locale && this.config.locale === ((typeof navigator !== 'undefined' ? navigator.language : 'en-US') || 'en-US')) {
        this.config.locale = branding.locale;
      }
      if (branding.default_currency && this.config.currency === 'USD') {
        this.config.currency = branding.default_currency;
      }

      // CHG-P — capture the org's allowed currency set so the amount step
      // can render a dropdown. Skip when only one currency is enabled
      // (legacy UX). Defensive type guard — backend may serve null /
      // missing field on older deployments.
      const sc = (branding as any).supported_currencies;
      if (Array.isArray(sc) && sc.length > 1) {
        this.supportedCurrencies = sc.map((c) => String(c).toUpperCase());
        // If the donor's pre-set currency isn't in the allowed list,
        // fall back to the org default. Prevents a 400 on payment-intent
        // create from public_payments.py's currency-validation gate.
        if (!this.supportedCurrencies.includes(this.config.currency.toUpperCase())) {
          this.config.currency = this.supportedCurrencies[0]!;
        }
      }

      // CHG-0155 P6: pick up variant vocabulary from org branding response
      // so Mohseen widgets render "Sadaqah" and AloraChurch widgets render
      // "Gift" without needing the embed host to pre-supply vocabulary.
      const fetchedVocab = (branding as any).vocabulary;
      if (fetchedVocab && typeof fetchedVocab === 'object') {
        this.config.vocabulary = {
          ...(fetchedVocab as Record<string, string>),
          ...(this.config.vocabulary || {}),  // embed host override wins
        };
      }

      // Returning donor personalization (donate-page only, same-origin cookie)
      if (this.config.source === 'donate-page' && this.config.pageId) {
        try {
          const cookies = document.cookie.split(';');
          let agDonor = '';
          for (const c of cookies) {
            const trimmed = c.trim();
            if (trimmed.startsWith('ag_donor=')) {
              agDonor = trimmed.substring('ag_donor='.length);
              break;
            }
          }
          if (agDonor) {
            const rd = await fetchReturningDonor(
              this.config.pageId,
              agDonor,
              this.config.organizationId,
            ).catch(() => null);
            if (rd && rd.recognized && rd.suggested_amounts?.length) {
              this.config.presetAmounts = rd.suggested_amounts;
              this.returningDonor = rd;
            }
          }
        } catch { /* non-fatal */ }
      }

      // CHG-3494 — provider failure/cancel handling returns before the normal
      // first-step routing below. Materialize a context-locked entity before
      // those early exits so retry/back navigation cannot lose the fixed fund.
      this.seedContextLockedSelection();

      // CHG-3553 — establish the immutable pledge amount before provider-return
      // recovery. Donor identity is intentionally absent from browser state;
      // the backend resolves it from the contribution session.
      if (this.config.pledgeContribution) {
        this.config.currency = this.config.pledgeContribution.currency;
        this.selectedAmount = this.toMajorUnits(
          this.config.pledgeContribution.amount_minor_units,
        );
      }

      // Degraded mode: no online payment methods.
      // BUG-65: previously also required designations.length > 0, which
      // meant an org with neither providers NOR designations skipped the
      // friendly fallback and proceeded to render the form, where the
      // user would eventually hit a raw provider 404/502 on Pay. The
      // designations check was a quick-exit optimisation, not a
      // safety condition — drop it so an unconfigured org always shows
      // "Online payments are not yet configured" regardless of whether
      // it has designations seeded yet.
      const onlineMethods = (methods.methods ?? []).filter(m => m.type !== 'manual');
      const hasManual = (methods.methods ?? []).some(m => m.type === 'manual');
      const urlParams = new URLSearchParams(window.location.search);
      const checkoutReturn = readCheckoutReturnParams(window.location.search);
      const isPayPalReturn = urlParams.get('paypal') === 'return';
      if (onlineMethods.length === 0 && !checkoutReturn && !isPayPalReturn) {
        this.renderDegradedMode(branding, hasManual);
        return;
      }

      // Handle PayPal return — capture order after redirect back
      if (isPayPalReturn) {
        const token = urlParams.get('token');
        if (token) {
          await this.handlePayPalReturn(token);
          return;
        }
      }

      // CHG-1355 (BUG-151) — handle provider redirect-back via the
      // canonical ?payment={status}&intent={id} params that the backend
      // webhook handler appends to the return_url. This fires when any
      // redirect-based provider (bKash, Nagad, SSLCommerz) brings the
      // donor back to the donate page or widget page after checkout.
      if (checkoutReturn) {
        const { payment: paymentReturn, intent: intentReturn } = checkoutReturn;
        cleanCheckoutReturnParamsFromCurrentUrl();
        if (paymentReturn === 'success') {
          this.paymentIntentId = intentReturn;
          this.restorePaymentReturnState(intentReturn, { applyConfirmedTotal: true });
          const configuredReturn = this.applyConfiguredPaymentReturn(intentReturn, { applyConfirmedTotal: true });
          const configuredStatus = (this.config.paymentReturn?.status || '').toLowerCase();
          if (configuredReturn && configuredStatus === 'succeeded') {
            this.goTo('confirm');
            postCheckoutReturnConsumed();
            return;
          }
          await this.handleProviderReturn();
          return;
        } else {
          // cancel or failure — show error state directly, no polling needed.
          this.paymentIntentId = intentReturn;
          this.restorePaymentReturnState(intentReturn, { applyConfirmedTotal: false });
          this.applyConfiguredPaymentReturn(intentReturn, { applyConfirmedTotal: false });
          // sessionStorage may be disabled/cleared during the provider hop.
          // Recover only the safe target/correlation snapshot from the
          // tenant-scoped status endpoint; donor identity is never returned.
          try {
            const statusState = await fetchPaymentIntentStatus(
              intentReturn,
              this.config.organizationId,
            );
            this.applyPaymentReturnAllocationState(statusState);
          } catch { /* retry routing will return to target selection */ }
          this.showPaymentFailed(paymentReturn);
          postCheckoutReturnConsumed();
          return;
        }
      }

      // Handle bKash/Nagad/generic provider return after redirect
      // (legacy path — kept for same-origin widget embeds where the
      // widget set ?provider=...&payment_id=... before redirecting).
      const providerReturn = urlParams.get('provider');
      const returnPaymentId = urlParams.get('payment_id');
      if (providerReturn && returnPaymentId) {
        const saved = sessionStorage.getItem('ag_payment_return');
        if (saved) {
          try {
            const state = JSON.parse(saved);
            sessionStorage.removeItem('ag_payment_return');
            this.paymentIntentId = state.payment_intent_id;
            if (typeof state.amount === 'number' && Number.isFinite(state.amount)) {
              this.selectedAmount = state.amount;
            }
            this.applyConfirmedPaymentAmount({
              amount_minor_units: state.total_amount_minor_units,
              currency: state.currency,
            });
            const cleanUrl = window.location.pathname;
            window.history.replaceState({}, '', cleanUrl);
            await this.handleProviderReturn();
            return;
          } catch { /* fall through to normal init */ }
        }
      }

      // CHG-0171 W4b — default-widget empty state. When scope_type is
      // 'all' and neither campaigns nor designations are available,
      // the legacy "No designations configured" copy misleads the
      // donor: the org might have campaigns but no designations, or
      // vice versa. Surface a neutral "no giving options" message
      // covering both entity types.
      const hasCampaignOptions = this.campaignOptions.length > 0;
      // CHG-1657 (W3.2) — use this.designations (post envelope
      // allowed_designations filter) for the empty-state count, not the
      // unfiltered local. If the envelope narrowed the list to zero
      // the donor should see the neutral "no giving options" message.
      const totalOptions = this.designations.length + (
        this.config.scopeType === 'all' ? this.campaignOptions.length : 0
      );

      if (this.config.pledgeContribution) {
        // The hosted page already verified the session against its canonical
        // FUND/CAMPAIGN URL and supplied the corresponding context lock. Skip
        // intent/amount/frequency/donor collection and open at method choice.
        this.seedContextLockedSelection();
        if (!this.selectedDesignation && !this.selectedCampaign) {
          this.setError('no_designations');
          this.goTo('error');
          return;
        }
        this.goTo('payment');
        return;
      }

      // CHG-1466 (γ.code.2) + CHG-1657 (W3.2) — page context lock: entity
      // pre-bound from the donate-page envelope OR the unified
      // /options envelope (post-CHG-1637). Suppress picker, seed the
      // selectedDesignation / selectedCampaign + cascade from the
      // matching option (so amount-step presets + designation_id at
      // submit are populated), and go straight to amount.
      if (this.effectiveContextLock) {
        this.seedContextLockedSelection();
        this.goTo(this.intentStepAvailable() ? 'intent' : 'amount');
      } else {
        // Auto-select single designation. Two cases:
        //   (a) Default widget with 0 campaigns + 1 designation -- the
        //       legacy fast-path. Skip the picker entirely.
        //   (b) CHG-1253 / ITER-3378 -- scope_type='categories' with a
        //       single designation. The widget is explicitly bound to
        //       one fund (e.g. a campaign donate-page URL where the
        //       donate-page row carries designation_id). There is no
        //       choice for the donor to make; the picker step would
        //       only render a one-item radio group. Auto-select.
        // CHG-1657 (W3.2) — use this.designations (post envelope filter)
        // so a single-allowed-designation envelope still triggers the
        // fast-path even when the org has more designations defined.
        const singleDesignationFastPath =
          this.designations.length === 1 && (
            !hasCampaignOptions
            || this.config.scopeType === 'categories'
            || this.config.scopeType === 'campaign'
          );
        const wantsIntentStep = totalOptions > 0 && this.intentStepAvailable();
        if (totalOptions === 0) {
          // Neutral empty-state message whether scope is 'all',
          // 'categories', or 'campaign' with the bound entity gone.
          this.setError(this.config.scopeType === 'campaign' ? 'campaign_inactive' : 'no_designations');
          this.goTo('error');
        } else if (singleDesignationFastPath) {
          this.selectedDesignation = this.designations[0];
          // CHG-0171 W4 — seed cascade from matching designation option
          // (present when /options hydrated it above). Falls through null
          // for legacy pre-W3 paths → effective* helpers use legacy flags.
          const first = this.designations[0];
          if (first) {
            const opt = this.designationOptions.find(o => o.id === first.id);
            this.setSelectedCascade(opt ? opt.cascade : null);
          }
          this.goTo(wantsIntentStep ? 'intent' : 'amount');
        } else {
          this.goTo(wantsIntentStep ? 'intent' : 'designation');
        }
      }
    } catch (err) {
      console.error('[DonationCheckout] init error', err);
      this.setError('load_failed');
      this.goTo('error');
    }
  }

  /**
   * Effective allow_recurring resolution.
   *
   * CHG-0907 / ITER-A4 (CHG-0909): logic extracted to
   * cascade-routing.ts so the four cascade paths (L1 org default,
   * L2 entity rule, L3 widget disable, L4 provider capability) can
   * be unit-tested directly without booting a DonationCheckout
   * instance. The cascade resolved server-side
   * (ref:tier-3-calculation/widget-cascade) is authoritative when
   * set; falls back to the legacy CheckoutConfig flags for embeds
   * that render before /options completes.
   */
  private usesHostedCampaignPolicy(): boolean {
    return (
      this.config.source === 'donate-page'
      && (this.config.pageContextType || '').toUpperCase() === 'CAMPAIGN'
      && Boolean(this.config.campaignId)
    );
  }

  private usesHostedSelectableDesignationPolicy(): boolean {
    const contextType = (this.config.pageContextType || '').toUpperCase();
    return (
      this.config.source === 'donate-page'
      && (contextType === 'PROGRAM' || contextType === 'PROJECT')
    );
  }

  private usesHostedRootPolicy(): boolean {
    const contextType = (this.config.pageContextType || '').toUpperCase();
    return (
      (this.config.source === 'donate-page' || this.config.source === 'session')
      && (contextType === 'CAMPAIGN' || contextType === 'SESSION')
    );
  }

  private effectiveAllowRecurring(): boolean {
    // CHG-1657 (W3.2) — envelope-level recurring_allowed is an AND-in
    // gate. The selected entity's cascade (when present) is still the
    // most-specific layer; envelope narrows the org/widget-default
    // layer when it forbids recurring outright.
    if (!isRecurringAllowed(this.envelope)) return false;
    // PROGRAM/PROJECT are selectable hosted containers. Their root policy
    // describes the page container, while the donor-selected child cascade is
    // the money-policy authority. Do not let root bootstrap masks override it.
    if (this.usesHostedSelectableDesignationPolicy() && this.selectedCascade) {
      return this.selectedCascade.allow_recurring;
    }
    // A hosted Campaign page carries the Campaign cascade in its bootstrap.
    // Its linked Designation is attribution-only and must not replace policy.
    if (this.usesHostedCampaignPolicy()) {
      return this.config.disableRecurring !== true && this.config.showFrequency !== false;
    }
    // Surface-level policy is narrow-only. Hosted SESSION pages can disable
    // recurring independently of the selected designation cascade.
    if (this.config.disableRecurring === true || this.config.showFrequency === false) return false;
    return computeEffectiveAllowRecurring(this.selectedCascade, this.config);
  }

  /** CHG-0171 W4 — effective allow_pledge. Pledge flow itself lands as
   *  a W4b follow-up; this helper is the contract the future pledge
   *  step will consume. Referenced by `_effectiveStateForDebug` so
   *  TS noUnusedLocals doesn't flag it.
   *
   *  CHG-1657 (W3.2) — envelope.constraints.pledge_allowed AND-in. */
  private effectiveAllowPledge(): boolean {
    if (!isPledgeAllowed(this.envelope)) return false;
    if (this.usesHostedSelectableDesignationPolicy() && this.selectedCascade) {
      return this.selectedCascade.allow_pledge;
    }
    if (this.config.disablePledge === true) return false;
    if (
      this.usesHostedRootPolicy()
      && typeof this.config.allowPledges === 'boolean'
    ) {
      return this.config.allowPledges;
    }
    if (this.usesHostedCampaignPolicy()) return true;
    if (this.selectedCascade) return this.selectedCascade.allow_pledge;
    // Preserve the established widget default when no policy signal exists.
    return true;
  }

  /** Whether this checkout has a positive policy signal for the donor intent
   * fork. Locked surfaces consult their selected entity/root policy; unlocked
   * surfaces consult every selectable entity because the fork precedes the
   * picker. */
  private intentStepAvailable(): boolean {
    const cascades = this.effectiveContextLock
      ? [this.selectedCascade]
      : [
          ...this.campaignOptions.map((option) => option.cascade),
          ...this.designationOptions.map((option) => option.cascade),
        ];
    return shouldShowIntentStep(cascades, this.envelope, this.config);
  }

  private shouldShowPledgeStep(): boolean {
    return shouldShowPledgeStep(
      {
        allow_recurring: this.effectiveAllowRecurring(),
        allow_pledge: this.effectiveAllowPledge(),
      },
      this.donorIntent,
    );
  }

  private isNoPaymentPledge(): boolean {
    return isNoPaymentPledgeFlow(
      {
        allow_recurring: this.effectiveAllowRecurring(),
        allow_pledge: this.effectiveAllowPledge(),
      },
      this.donorIntent,
    );
  }

  private anonymousAllowed(): boolean {
    // Donor-elected pay-later requires a reminder channel. The legacy fallback
    // retains anonymous support because its cycle-zero donation is charged now.
    if (this.donorIntent === 'later' && this.isPledgeFlow()) return false;
    return this.config.showAnonymous !== false;
  }

  private donorContactRequiredMessage(): string {
    if (this.donorIntent === 'later') {
      return this.vocab(
        'donor_contact_required_pledge',
        'Enter your email or phone so we can remind you about this pledge.',
      );
    }
    if (!this.anonymousAllowed()) {
      return this.vocab(
        'donor_contact_required_no_anonymous',
        'Enter your email or phone to continue.',
      );
    }
    return this.vocab(
      'donor_contact_required',
      'Enter your email or phone, or choose to give anonymously.',
    );
  }

  /** CHG-0171 W4 — which campaign drives this donation? Prefers the
   *  donor's default-widget picker choice over the widget-config-level
   *  campaignId (which only applies to scope_type='campaign' widgets). */
  private effectiveCampaignId(): string | undefined {
    return this.selectedCampaign?.id ?? this.config.campaignId;
  }

  /** CHG-2702 (DP-001/DP-002, SESSION) — the giving-session id driving this
   *  donation, for SESSION-context donate pages. Threaded as the top-level
   *  `session_id` on createPaymentIntent so webhook-first card donations
   *  record Donation.session_id and the GivingSession counters update (DP-R2).
   *  Undefined for non-SESSION contexts. */
  private effectiveSessionId(): string | undefined {
    return this.config.sessionId || undefined;
  }

  /** Sentinel that references W4 helpers that aren't wired to a UI
   *  surface yet (pledge flow + cascade introspection). Keeps
   *  TS noUnusedLocals quiet without sprinkling // @ts-ignore. */
  private _effectiveStateForDebug(): Record<string, unknown> {
    return {
      allow_recurring: this.effectiveAllowRecurring(),
      allow_pledge: this.effectiveAllowPledge(),
      min_minor_units: this.effectiveMinMinorUnits(),
      max_minor_units: this.effectiveMaxMinorUnits(),
      campaign_id: this.effectiveCampaignId(),
      // CHG-0932 / ITER-C2 — pledge IDs are produced by submitPledge()
      // and will be threaded into the cycle-0 PaymentIntent metadata in
      // a follow-up iteration (program plan §5.4 / DRIFT-601). Surfaced
      // here so TS noUnusedLocals stays quiet until that wiring lands.
      pledge_id: this.pledgeId,
      pledge_donor_id: this.pledgeDonorId,
    };
  }

  /** Effective min amount in cents, combining entity + widget caps.
   *  `null` means "no bound" from this layer.
   *
   *  Resolution order (most-specific first):
   *    1. Hosted Campaign bootstrap for Campaign pages
   *    2. Selected entity cascade (CHG-0171 W4)
   *    3. Envelope amount_bounds.min_minor_units (CHG-1657 W3.2)
   *    4. Legacy widget config minAmount
   */
  private effectiveMinMinorUnits(): number | null {
    if (this.usesHostedCampaignPolicy()) {
      return this.config.minAmount != null ? this.config.minAmount : null;
    }
    if (this.selectedCascade && this.selectedCascade.min_amount_minor_units != null) {
      return this.selectedCascade.min_amount_minor_units;
    }
    const envMin = getMinMinorUnits(this.envelope);
    if (envMin != null) return envMin;
    return this.config.minAmount != null ? this.config.minAmount : null;
  }

  private effectiveMaxMinorUnits(): number | null {
    if (this.usesHostedCampaignPolicy()) {
      return this.config.maxAmount != null ? this.config.maxAmount : null;
    }
    if (this.selectedCascade && this.selectedCascade.max_amount_minor_units != null) {
      return this.selectedCascade.max_amount_minor_units;
    }
    const envMax = getMaxMinorUnits(this.envelope);
    if (envMax != null) return envMax;
    return this.config.maxAmount != null ? this.config.maxAmount : null;
  }

  /** CHG-1657 (W3.2) — capture the unified envelope from a WidgetOptions
   *  payload + project the context_lock from either the envelope or the
   *  legacy config.contextLock onto a single canonical
   *  `effectiveContextLock` field. */
  private captureEnvelope(opts: import('./api').WidgetOptions): void {
    if (opts.page_context && opts.constraints) {
      this.envelope = {
        page_context: opts.page_context,
        constraints: opts.constraints,
        context_lock: opts.context_lock ?? null,
      };
    }
    // Resolve a unified context_lock. Envelope takes precedence (it's
    // the canonical resolver projection); fall back to the donate-page
    // bootstrap config.contextLock (CHG-1466 path) when envelope absent.
    const envLock = hasContextLock(this.envelope) ? this.envelope!.context_lock! : null;
    if (envLock) {
      this.effectiveContextLock = envLock;
    } else if (this.config.contextLock) {
      this.effectiveContextLock = {
        kind: this.config.contextLock.kind,
        id: this.config.contextLock.id,
        display_name: this.config.contextLock.displayName || '',
      };
    } else {
      this.effectiveContextLock = null;
    }
  }

  /** CHG-1657 (W3.2) — submission-gate failure template. Mirrors the
   *  server-side render-constraints gate; reaching this template means
   *  the client caught a violation that would also fail server-side
   *  (e.g. donor tampered with selectedDesignation in dev tools,
   *  envelope mutated mid-flow). Shows a recoverable error rather
   *  than letting the donor hit a generic 4xx at submit.
   */
  private renderSubmissionGateError(
    code:
      | 'target_required'
      | 'context_lock_mismatch'
      | 'designation_not_allowed'
      | 'amount_too_low'
      | 'amount_too_high'
      | 'off_season_closed',
  ): void {
    const messages: Record<string, string> = {
      target_required:
        'Choose a fund or campaign before continuing to payment.',
      context_lock_mismatch:
        "This donation is locked to a specific designation. Please reload the page to start over.",
      designation_not_allowed:
        "The selected fund isn't available on this page. Please pick a different fund.",
      amount_too_low:
        'The donation amount is below the minimum allowed. Please increase the amount.',
      amount_too_high:
        'The donation amount is above the maximum allowed. Please decrease the amount.',
      off_season_closed:
        'Donations are closed for this season. Please check back later.',
    };
    const target =
      code === 'amount_too_low' || code === 'amount_too_high' ? 'amount' : 'designation';
    this.container.innerHTML = `
      <div class="dk-step dk-step-gate-error">
        <h3 class="dk-step-title">Unable to process this donation</h3>
        <p class="dk-error">${this.esc(messages[code])}</p>
        <button class="dk-btn-primary" id="dk-gate-back">Go back</button>
      </div>`;
    this.container.querySelector('#dk-gate-back')?.addEventListener('click', () => {
      this.goTo(target);
    });
    this.notifyResize();
  }

  /** CHG-1657 (W3.2) — closed-for-season template. Same visual shell
   *  as renderDegradedMode but distinct copy: this is an explicit
   *  operator decision (off_season_action='reject'), not a missing-
   *  provider degraded path. */
  private renderOffSeasonClosed(): void {
    const orgName = this.branding?.name || this.config.orgName || 'this organization';
    this.container.innerHTML = `
      <div class="dk-degraded-mode dk-off-season-closed">
        <div style="font-size:2rem;margin-bottom:0.75rem">&#128197;</div>
        <h3 style="font-size:1rem;font-weight:600;color:var(--dk-text);margin:0 0 0.5rem">Donations closed for this season</h3>
        <p style="font-size:0.9rem;color:var(--dk-text-muted);line-height:1.5;margin:0 0 1rem">
          ${this.esc(orgName)} is not accepting donations at this time. Please check back later.
        </p>
      </div>`;
    this.notifyResize();
  }

  /** Sync `selectedCascade` when the donor switches entity. Callers
   *  (designation-pick, campaign-pick) should invoke this before
   *  re-rendering the amount step. */
  private setSelectedCascade(cascade: CascadedDonationSettings | null): void {
    this.selectedCascade = cascade;
  }

  /** Re-materialize the entity selected by the authoritative page/session lock.
   *
   * CHG-3494 — selection objects are transient checkout state, but the context
   * lock survives provider returns and restart/back navigation. Keep the two in
   * sync from one idempotent path so a fixed-fund checkout can never expose the
   * organization-wide picker merely because transient selection was cleared.
   */
  private seedContextLockedSelection(): void {
    const lock = this.effectiveContextLock;
    if (!lock) return;

    if (lock.kind === 'designation') {
      this.selectedDesignation = this.designations.find(d => d.id === lock.id) ?? null;
      this.selectedCampaign = null;
      const option = this.designationOptions.find(o => o.id === lock.id);
      this.setSelectedCascade(option ? option.cascade : null);
      return;
    }

    const option = this.campaignOptions.find(o => o.id === lock.id) ?? null;
    this.selectedCampaign = option;
    this.selectedDesignation = null;
    this.setSelectedCascade(option ? option.cascade : null);
  }

  /** Guard the pre-selection "later" choice against a more-specific entity
   * veto. The donor remains on the picker with an actionable message; we never
   * reinterpret pay-later as permission to charge now. */
  private selectedEntityAcceptsCurrentIntent(): boolean {
    if (this.donorIntent !== 'later' || this.effectiveAllowPledge()) {
      this.intentVetoNotice = '';
      return true;
    }
    this.intentVetoNotice = this.vocab(
      'intent_pledge_unavailable_here',
      'This option does not accept pledges. Choose another option or go back and select Donate Now.',
    );
    return false;
  }

  // CHG-3027 / PLG-R01a (review fix #1) — a pledge is being CAPTURED whenever
  // the cascade routes through the pledge step AND no pledge row exists yet.
  // While this is true the donor step MUST render so its "Continue" runs
  // submitPledge(): a pledge inherently needs a donor identity for cycle
  // reminders, so the `showDonorInfo === false` toggle must NOT be allowed to
  // skip the donor step here — doing so would let the widget reach payment with
  // no pledge created and silently charge a one-time gift instead. Once
  // pledgeId is set the commitment exists and the donor proceeds to the cycle-0
  // charge as an ordinary one-time payment, so the toggle resumes normal force.
  private isPledgeFlow(): boolean {
    return this.shouldShowPledgeStep() && !this.pledgeId;
  }

  private toPledgeWireFrequency(
    frequency: 'one-time' | 'monthly' | 'quarterly' | 'annual',
  ): 'one_time' | 'monthly' | 'quarterly' | 'annual' {
    return frequency === 'one-time' ? 'one_time' : frequency;
  }

  // CHG-3147 — can we safely auto-skip the donor step for a prefilled donor?
  // Only when the prefilled identity already satisfies the org's contact-capture
  // requirements (so we never skip into an invalid/incomplete submit). The
  // capture prompt must be loaded (null ⇒ requirements unknown ⇒ do NOT skip,
  // fall back to showing the prefilled step). Mirrors the donor-step's own
  // required-field logic (emailRequired / phoneRequired + at-least-one-channel).
  private donorPrefillSatisfiesRequirements(): boolean {
    if (!this.donorPrefilled) return false;
    const cp = this.capturePrompt;
    if (!cp) return false;
    const email = this.donorInfo.email.trim();
    const phone = this.donorInfo.phone.trim();
    if (!isValidDonorEmailSyntax(email)) return false;
    if (cp.required.includes('email') && !email) return false;
    if (cp.required.includes('phone') && !phone) return false;
    return !!(email || phone);
  }

  // CHG-3053 / DRIFT-1165 — supersede a committed-but-unpaid pledge when a
  // MATERIAL field (amount / cadence) is edited after create, so the persisted
  // pledge can never silently diverge from the cycle-0 charge.
  //
  // Background: once submitPledge() succeeds, pledgeId is set and isPledgeFlow()
  // returns false, so neither the amount-step "Continue" nor the cadence
  // selection re-invokes submitPledge() and there is no PATCH path. If the donor
  // then navigates back (via a completed-step "Change") and edits the amount or
  // cadence, only local state (selectedAmount / selectedFrequency) moves while
  // the persisted pledge row keeps its ORIGINAL values — yet the payment step
  // builds the cycle-0 charge off selectedAmount. Result: the donor is charged
  // the edited amount while the recorded pledge commitment reflects the pre-edit
  // value (DRIFT-1165).
  //
  // Fix: when the about-to-charge amount/cadence diverges from what the pledge
  // was created with (pledgedAmountMinorUnits / pledgedFrequency snapshot), clear
  // pledgeId so isPledgeFlow() flips back to true. The donor flow routes through
  // the donor step again, whose "Continue" re-runs submitPledge() and creates a
  // FRESH pledge carrying the new values. We also clear pledgeAttemptKey so the
  // re-create mints a brand-new Idempotency-Key (a genuinely different pledge,
  // never a dedupe/replay of the superseded one). No double-charge: the pledge
  // create mints no payment token (program plan §5.4) — the single cycle-0 charge
  // still happens once on the payment step off the (now matching) selectedAmount.
  // The prior committed-but-unpaid pledge is superseded.
  //
  // No-op when no pledge exists yet (first-time forward flow) or when the edited
  // value is unchanged from the snapshot (so a back-and-forth that lands on the
  // same amount/cadence does NOT create a gratuitous duplicate pledge).
  private supersedePledgeIfMaterialEdit(): void {
    if (!this.pledgeId) return;
    const amountDiverged = this.pledgedAmountMinorUnits !== this.selectedAmount;
    const cadenceDiverged = this.pledgedFrequency !== this.selectedFrequency;
    if (!amountDiverged && !cadenceDiverged) return;
    this.pledgeId = null;
    this.pledgeDonorId = null;
    this.pledgedAmountMinorUnits = null;
    this.pledgedFrequency = null;
    // A genuinely different pledge must not dedupe against the superseded one.
    this.pledgeAttemptKey = '';
  }

  private goTo(step: Step): void {
    // CHG-3494 — a context lock is a routing invariant, not merely a visual
    // banner. Restore its transient selection and refuse every route into the
    // organization-wide picker, including retry/back paths after provider exit.
    if (step === 'designation' && this.effectiveContextLock) {
      this.seedContextLockedSelection();
      step = 'amount';
    }

    // Auto-skip frequency when the cascade says recurring is disabled.
    // CHG-0907 / ITER-A4 (CHG-0909): the cascade
    // (ref:tier-3-calculation/widget-cascade) is authoritative for
    // all four layers — L1 org default, L2 entity rule, L3 widget
    // disable, L4 provider capability (added in A2). The legacy
    // defensive AND on `paymentMethods.some(supports_recurring)`
    // that lived here pre-A4 is retired (DRIFT-600 reconciled).
    //
    // CHG-0932 / ITER-C2: when recurring is disabled BUT the cascade
    // allows pledge (alpha-only / non-recurring-capable org with
    // entity.allow_pledge=true), route through the pledge step
    // instead of skipping to donor. Pledge is the canonical
    // recurring-shaped commitment when recurring isn't available
    // (program plan §5.4). cascade-routing.ts:shouldShowPledgeStep
    // is the pure helper.
    if (step === 'frequency' && this.isNoPaymentPledge()) {
      step = 'pledge';
    }
    if (step === 'frequency' && !this.effectiveAllowRecurring()) {
      step = this.shouldShowPledgeStep() ? 'pledge' : 'donor';
    }
    // No-payment pledges are terminal once persisted. Defensive routing keeps
    // every present/future payment call site from charging this branch.
    if (step === 'payment' && this.isNoPaymentPledge()) {
      step = this.pledgeId ? 'confirm' : 'donor';
    }
    // CHG-3027 / PLG-R01a (review fix #1) — never skip the donor step to
    // payment while a pledge is being captured; submitPledge() runs from the
    // donor "Continue", so skipping it would silently turn the pledge into a
    // one-time charge.
    // CHG-3147 — also auto-skip when an authenticated donor is prefilled AND the
    // prefill satisfies the org contact requirements (the public donate page
    // never sets defaultDonor*, so its flow is unchanged).
    if (
      step === 'donor' &&
      !this.isPledgeFlow() &&
      (this.config.showDonorInfo === false || this.donorPrefillSatisfiesRequirements())
    ) {
      const hasContact = !!this.donorInfo.email.trim() || !!this.donorInfo.phone.trim();
      if (this.config.showDonorInfo === false && !hasContact && !this.anonymousAllowed()) {
        // A surface that disallows anonymous giving cannot skip donor identity
        // capture unless it already supplied an email or phone.
        step = 'donor';
      } else {
        step = 'payment';
      }
      // CHG-3229 — when the donor step is auto-skipped (showDonorInfo=false, or a
      // prefill that satisfied requirements) the Continue/Skip handlers never
      // run, so nothing enforces the backend's anonymous-or-contact rule. A
      // public widget with showDonorInfo=false and no prefill keeps the default
      // donorInfo ({email:'', phone:'', anonymous:false}) and would 422 at the
      // gateway/offline submit. Force anonymous ONLY when there is genuinely no
      // contact channel — a prefill that satisfied requirements already carries
      // email/phone, so this guard is a no-op there and that donation stays
      // attributable.
      if (step === 'payment' && !hasContact && this.anonymousAllowed()) {
        this.donorInfo.anonymous = true;
      }
    }
    // CHG-3225 (fix #264) — leaving the payment step tears down any mounted
    // Stripe Payment Element. This used to be done by the payment step's own
    // "← Back" button handler; that button was removed in favour of the stepper
    // pips, so the teardown moves here to cover every exit path (pip click,
    // programmatic goTo). Re-entering payment re-mounts a fresh element.
    if (this.step === 'payment' && step !== 'payment' && this.paymentElement) {
      this.paymentElement.destroy();
      this.paymentElement = null;
    }
    this.step = step;
    this.render();
  }

  // CHG-L — centralized error classification for the error step.
  // Callers pass a code; the render layer resolves a friendly message
  // from ERROR_MESSAGES and decides whether to show the retry button.
  private setError(code: WidgetErrorCode): void {
    this.errorCode = code;
    this.errorMessage = ERROR_MESSAGES[code];
  }

  // CHG-L — re-run init() from the error step. Only invoked for
  // retryable codes (load_failed/unknown); no_designations and
  // campaign_inactive are operator-side gaps where retrying won't help.
  // init() is idempotent — it overwrites all cached state and re-renders
  // the container from scratch, so double-invocation is safe.
  private retryInit(): void {
    this.errorCode = null;
    this.step = 'loading';
    this.render();
    void this.init();
  }

  /**
   * Brand identity header for full-placement widgets.
   *
   * Rendered at the top of every widget state when placement === 'full':
   * the normal step progression AND the empty/error state. Before this
   * was extracted the error state template skipped the header entirely,
   * so a full-placement widget with no donation options rendered
   * visually identical to an embedded-placement widget — "full mode"
   * had no visible effect.
   *
   * Returns '' for embedded placement or when the org has no brand
   * name and no logo (nothing to show).
   */
  private renderBrandHeader(): string {
    if (this.config.placement !== 'full') return '';
    const brand = this.config.orgBrand || {};
    const logoHtml = brand.logo_url
      ? `<img src="${this.esc(brand.logo_url)}" alt="${this.esc(brand.name || '')}" class="dk-brand-logo" />`
      : '';
    const nameHtml = brand.name
      ? `<span class="dk-brand-name">${this.esc(brand.name)}</span>`
      : '';
    const taglineHtml = brand.tagline
      ? `<span class="dk-brand-tagline">${this.esc(brand.tagline)}</span>`
      : '';
    if (!logoHtml && !nameHtml) return '';
    return `
      <div class="dk-brand-header">
        ${logoHtml}
        <div class="dk-brand-text">
          ${nameHtml}
          ${taglineHtml}
        </div>
      </div>`;
  }

  private formatSessionProofMinor(
    amountMinor: number | null | undefined,
    proof: SessionProofConfig,
  ): string {
    const currency = (proof.currency || this.currencyCode() || 'USD').toUpperCase();
    const amount = minorUnitsToMajor(Number(amountMinor) || 0, currency);
    const exp = currencyExponent(currency);
    try {
      return new Intl.NumberFormat(proof.locale || this.config.locale || 'en-US', {
        style: 'currency',
        currency,
        minimumFractionDigits: 0,
        maximumFractionDigits: exp,
      }).format(amount);
    } catch {
      return `${amount.toFixed(exp)} ${currency}`;
    }
  }

  private renderSessionProof(): string {
    const proof = this.config.sessionProof;
    if (!proof || this.config.showSessionProof === false) return '';

    const status = (proof.status || '').toLowerCase();
    const label = status === 'live'
      ? 'Live giving session'
      : status === 'paused'
      ? 'Giving session paused'
      : status === 'ended'
      ? 'Session ended'
      : 'Giving session';
    const raised = proof.formatted_raised ||
      this.formatSessionProofMinor(proof.raised_minor_units, proof);
    const goalMinor = Number(proof.goal_amount_minor_units) || 0;
    const goal = proof.formatted_goal ||
      (goalMinor > 0 ? this.formatSessionProofMinor(goalMinor, proof) : '');
    const rawPct = Number(proof.progress_pct);
    const pct = Number.isFinite(rawPct)
      ? Math.max(0, Math.min(rawPct, 100))
      : 0;
    const count = proof.donation_count;
    const giftCopy = typeof count === 'number'
      ? `<span>${this.esc(String(count))} ${count === 1 ? 'gift' : 'gifts'} so far</span>`
      : '';
    const liveCopy = status === 'live' ? '<span>Recent gifts update live</span>' : '';

    return `
      <div class="dk-session-proof" aria-label="Live session proof">
        <div class="dk-session-proof-top">
          <span class="dk-session-proof-status">${this.esc(label)}</span>
          ${liveCopy}
        </div>
        <div class="dk-session-proof-amount">
          <strong>${this.esc(String(raised))}</strong>
          ${goal ? `<span>of ${this.esc(String(goal))}</span>` : ''}
        </div>
        ${goalMinor > 0 ? `
          <div class="dk-session-proof-track" role="progressbar" aria-valuemin="0" aria-valuemax="100" aria-valuenow="${Math.round(pct)}">
            <span class="dk-session-proof-fill" style="width:${pct}%"></span>
          </div>` : ''}
        <div class="dk-session-proof-meta">
          ${giftCopy}
          <span>Your gift counts toward this live session</span>
        </div>
      </div>`;
  }

  private renderSessionConfirmProof(pending = false): string {
    const proof = this.config.sessionProof;
    if (!proof) return '';
    const text = pending
      ? 'Will count toward the live session after verification'
      : 'Added to the live session total';
    const goalMinor = Number(proof.goal_amount_minor_units) || 0;
    const meta = !pending && goalMinor > 0 && this.config.sessionId
      ? '<span class="dk-session-confirm-proof__meta" id="dk-session-confirm-progress" aria-live="polite"></span>'
      : '';
    return `<div class="dk-session-confirm-proof" role="status"><span>${this.esc(text)}</span>${meta}</div>`;
  }

  private async renderSessionConfirmProgress(): Promise<void> {
    const proof = this.config.sessionProof;
    const sessionId = this.config.sessionId;
    const el = this.container.querySelector<HTMLElement>('#dk-session-confirm-progress');
    if (!proof || !sessionId || !el) return;

    const goalMinor = Number(proof.goal_amount_minor_units) || 0;
    if (goalMinor <= 0) return;

    try {
      const apiUrl = (typeof window !== 'undefined' && (window as any).__DONATION_KIOSK_API_URL__) || window.location.origin;
      const progressUrl = `${apiUrl}/api/v1/giving-sessions/public/${encodeURIComponent(sessionId)}/progress`;
      const poller = (window as any).__AG_SESSION_POLL__;
      const data = poller
        ? await poller.json(progressUrl)
        : await fetch(progressUrl).then((response) => response.ok ? response.json() : null);
      if (!data) return;
      const current = Number(
        data.goal_progress_minor_units ??
        data.total_raised_minor_units ??
        data.credited_total_minor_units ??
        data.total_donated_minor_units ??
        0,
      );
      if (!Number.isFinite(current) || current < 0) return;
      const pct = Math.max(0, Math.min(100, Math.round((current / goalMinor) * 100)));
      el.textContent = current >= goalMinor
        ? this.vocab('session_goal_reached', 'Goal reached')
        : `${pct}% of session goal reached`;
    } catch {
      // Optional confirmation detail; never block the terminal success state.
    }
  }

  /**
   * Live gift bridge (CHG-3211 / donate-page v3 P3). Broadcast the current
   * gift state on every render so the hosted donate page's split-card stub can
   * mirror an animated "Your gift" receipt. ADDITIVE + GUARDED: embedded
   * widgets (third-party iframes) have no listener and simply ignore it, and
   * any failure is swallowed — the form must never break because of the
   * optional receipt. Dispatched on `window` (on the hosted donate page the
   * checkout mounts in the page's own document, so the stub listener receives
   * it; inside the embed iframe the event stays in the iframe, harmlessly).
   */
  private emitGiftChange(): void {
    try {
      if (typeof window === 'undefined' || typeof window.CustomEvent !== 'function') return;
      window.dispatchEvent(new CustomEvent('ag:gift-change', {
        detail: {
          // this.selectedAmount is in MAJOR units (presets are toMajorUnits, the
          // custom field is parseFloat(dollars); fmtCurrency expects MAJOR). The
          // stub receipt listener formats MINOR units (amount/10**exp), so
          // convert here — otherwise it renders 100x too small (CHG-3215: a BDT
          // 500 gift showed "BDT 5" in the stub receipt). CHG-3220: per-currency.
          amountMinor: this.toMinorUnits(this.selectedAmount || 0),
          currency: (this.config.currency || 'USD').toUpperCase(),
          designationName: this.selectedDesignation ? this.selectedDesignation.name : '',
          isPledge: this.isNoPaymentPledge(),
        },
      }));
    } catch {
      /* receipt is decorative — never let it break the form */
    }
  }

  /** v3 "Back" action button — the label routes through vocab (i18n: en/ar/…)
   *  and the arrow is a separate .dk-back-arrow span that mirrors under
   *  [dir="rtl"] (CSS scaleX(-1)) so it points the correct way in RTL locales. */
  private v3BackButton(id: string): string {
    return `<button class="dk-btn-ghost dk-btn-back" id="${id}" type="button">`
      + `<span class="dk-back-arrow" aria-hidden="true">←</span>`
      + `${this.esc(this.vocab('back_button', 'Back'))}</button>`;
  }

  /**
   * Keep the primary action first in DOM/focus order, followed by Back.
   * Desktop CSS uses row-reverse to retain the visual [Back] [Continue] row;
   * mobile switches to a normal column so touch, keyboard, and screen-reader
   * order all encounter the forward action before reverse navigation.
   */
  private v3ActionButtons(backId: string, primaryButton: string): string {
    return primaryButton + this.v3BackButton(backId);
  }

  /**
   * Top stepper bar (CHG-3216 — donate-page v3 form rebuild toward the
   * reference). Renders a segmented progress bar across the visible steps plus
   * a terminal "Done". Completed pips are clickable / keyboard-actionable to
   * jump back (replacing the per-step "Change" buttons of the old progressive
   * accordion). The active step's full form renders below it.
   */
  private renderStepperBar(steps: { key: Step; label: string; past: boolean }[]): string {
    const pips: { key: Step; label: string; done: boolean; active: boolean }[] = [
      ...steps.map(s => ({ key: s.key, label: s.label, done: s.past, active: s.key === this.step })),
      { key: 'confirm' as Step, label: this.vocab('done_step_label', 'Done'), done: false, active: false },
    ];
    const lis = pips.map(p => {
      const cls = p.done ? 'dk-step-done' : (p.active ? 'dk-step-active' : '');
      const tick = p.done ? '<span class="dk-step-tick" aria-hidden="true">✓</span> ' : '';
      const inner = `<span class="dk-step-bar"></span><span class="dk-step-name">${tick}${this.esc(p.label)}</span>`;
      // a11y (aria-required-children): a role="list" may only contain listitem
      // children, so the jump-back control lives on an INNER role="button" span
      // (.dk-step-edit) rather than on the <li> itself — the <li> stays a valid
      // listitem while completed pips remain clickable / keyboard-actionable. The
      // [data-edit] handler binds wherever the attribute is. Active pip carries
      // aria-current on the listitem.
      const body = p.done
        ? `<span class="dk-step-edit" data-edit="${p.key}" role="button" tabindex="0" aria-label="Edit ${this.esc(p.label)}">${inner}</span>`
        : inner;
      const liAttrs = (!p.done && p.active) ? ' aria-current="step"' : '';
      return `<li class="dk-step-pip ${cls}"${liAttrs}>${body}</li>`;
    }).join('');
    return `<ol class="dk-stepper" role="list" aria-label="${this.esc(this.vocab('steps_aria_label', 'Donation steps'))}">${lis}</ol>`;
  }

  private render(): void {
    // Stacked-stepper fix (root-cause guard): render() ALWAYS rebuilds the full
    // shell into the MOUNT ROOT, never into a swapped child. renderPaymentInto()
    // temporarily points this.container at the #dk-active-step child across an
    // async Stripe init; a render() that fires in that window must not write the
    // stepper+shell INTO that child (it would nest a second .dk-progressive with
    // its own stepper — the stacked-stepper defect). Using a local `root` (not
    // mutating this.container) leaves the payment swap undisturbed; normal renders
    // are unaffected because root === this.container outside the swap.
    const root = this.rootContainer;
    root.innerHTML = '';
    this.emitGiftChange();

    if (this.step === 'loading') {
      this.renderLoading();
      this.notifyResize();
      return;
    }
    if (this.step === 'confirm') {
      this.renderConfirm();
      this.notifyResize();
      return;
    }
    if (this.step === 'error') {
      // CHG-L — error step is now recoverable. Transient codes
      // (load_failed / unknown) render a "Try again" button that
      // re-runs init(); operator-side codes (no_designations /
      // campaign_inactive) omit the retry and rely on the org
      // contact-email CTA when available.
      const code = this.errorCode ?? 'unknown';
      const retryable = code === 'load_failed' || code === 'unknown';
      // BUG-479 (HIGH/SECURITY): the org admin's contact_email is no
      // longer surfaced via the public branding endpoint, so even if a
      // cached widget bundle still has this rendering path the value
      // will be null/empty and the support CTA below renders empty.
      // Defense in depth: do not construct a mailto: against any
      // contact_email value the bundle might receive in the future
      // either — render a generic "please try again" line instead so a
      // future regression cannot revive the leak from the client side.
      const supportHtml = '';
      const retryHtml = retryable
        ? `<button type="button" id="dk-error-retry" class="dk-btn-primary" style="margin-top:0.5rem">Try again</button>`
        : '';
      root.innerHTML = `
        ${this.renderBrandHeader()}
        <div class="dk-step dk-step-error" role="alert" aria-live="polite" data-error-code="${this.esc(code)}" style="text-align:center;padding:2rem 1rem">
          <div aria-hidden="true" style="font-size:2rem;margin-bottom:0.75rem">&#9888;&#65039;</div>
          <p style="font-size:0.95rem;color:#64748B;line-height:1.5;margin:0 0 1.25rem">${this.esc(this.errorMessage)}</p>
          ${retryHtml}
          ${supportHtml}
        </div>`;
      if (retryable) {
        root
          .querySelector<HTMLButtonElement>('#dk-error-retry')
          ?.addEventListener('click', () => this.retryInit());
      }
      this.notifyResize();
      return;
    }

    // Progressive layout: show completed steps as summary + active step expanded
    const steps: { key: Step; label: string; summary: () => string; past: boolean }[] = [
      {
        key: 'designation',
        label: 'Fund',
        summary: () => this.selectedDesignation ? this.esc(this.selectedDesignation.name) : '',
        past: this.stepIndex('designation') < this.stepIndex(this.step),
      },
      {
        key: 'amount',
        label: 'Amount',
        summary: () => this.selectedAmount ? this.fmtCurrency(this.selectedAmount) : '',
        past: this.stepIndex('amount') < this.stepIndex(this.step),
      },
      {
        key: 'frequency',
        label: this.vocab('frequency_step_label', 'Frequency'),
        summary: () => this.frequencyLabel(this.selectedFrequency),
        past: this.stepIndex('frequency') < this.stepIndex(this.step),
      },
      {
        key: 'pledge',
        label: this.vocab('pledge_step_label', 'Pledge'),
        summary: () => this.frequencyLabel(this.selectedFrequency),
        past: this.stepIndex('pledge') < this.stepIndex(this.step),
      },
      {
        key: 'donor',
        label: 'Your Info',
        // CHG-1066 — drop "Anonymous" missing-data substitute per doctrine
        // identity-contact-model.md §11. When the donor explicitly checked
        // the anonymous box, the step summary shows the canonical
        // "Anonymous donor" label (stored-flag pattern, identical to the
        // donation row's anonymous badge). Otherwise the cascade is
        // name → email → phone → em-dash; the cascade end is an em-dash
        // placeholder, not the literal "Anonymous" word.
        summary: () =>
          this.donorInfo.anonymous
            ? 'Anonymous donor'
            : (this.donorInfo.name || this.donorInfo.email || this.donorInfo.phone || '—'),
        past: this.stepIndex('donor') < this.stepIndex(this.step),
      },
      {
        key: 'payment',
        label: 'Payment',
        summary: () => '',
        past: false,
      },
    ];

    // Filter steps based on config visibility toggles
    const visibleSteps = steps.filter(s => {
      if (s.key === 'designation' && (this.designations.length <= 1 || this.effectiveContextLock)) return false;
      if (s.key === 'frequency' && !this.effectiveAllowRecurring()) return false;
      if (s.key === 'frequency' && this.isNoPaymentPledge()) return false;
      // CHG-0932 / ITER-C2 — pledge step shows when effective policy allows
      // pledge AND disallows recurring (mutually exclusive with frequency).
      // Normally that is the selected cascade; hosted CAMPAIGN/SESSION pages
      // use their authoritative root masks. goTo() enforces the same predicate.
      if (s.key === 'pledge' && !this.shouldShowPledgeStep()) return false;
      // CHG-3027 / PLG-R01a (review fix #1) — keep the donor step visible while a
      // pledge is being captured even when showDonorInfo is off, so the active
      // step actually renders and submitPledge() can run (goTo() routes here).
      if (s.key === 'donor' && !this.isPledgeFlow() && (this.config.showDonorInfo === false || this.donorPrefillSatisfiesRequirements())) return false; // CHG-3147
      if (s.key === 'payment' && this.isNoPaymentPledge()) return false;
      return true;
    });

    // Brand identity header (full placement mode) — same markup the
    // error-state renderer uses so "full" mode stays visible even when
    // the widget has zero donation options.
    const brandHeaderHtml = this.renderBrandHeader();
    const sessionProofHtml = this.renderSessionProof();

    // Header text
    let headerHtml = '';
    if (this.config.headerText) {
      headerHtml = `<h2 class="dk-header-text">${this.esc(this.config.headerText)}</h2>`;
    }

    // Preview mode banner
    let previewBanner = '';
    if (this.config.previewMode) {
      previewBanner = '<div class="dk-preview-banner">Preview Mode</div>';
    }

    let returningBanner = '';
    if (this.returningDonor?.recognized) {
      const greeting = this.returningDonor.donor_state === 'recurring'
        ? 'Thank you for your ongoing support! Want to make an additional gift today?'
        : this.returningDonor.donor_state === 'lapsed'
        ? 'Welcome back! We\'ve missed you. Your past generosity made a real difference.'
        : 'Welcome back! Thank you for your continued support.';
      returningBanner = `<div class="dk-returning-donor-banner">${this.esc(greeting)}</div>`;
    }

    let contextLockBanner = '';
    if (this.effectiveContextLock) {
      // CHG-1657 (W3.2) — single canonical confirmation line. Uses the
      // envelope-shape (display_name) projected from either source.
      // Helper renders "Donating to {name}" so the donor sees the
      // bound target without a picker. A missing display_name gets complete
      // generic copy; the server still gates with the {kind, id} pair.
      const line = formatLockedTargetConfirmation(this.effectiveContextLock);
      contextLockBanner = `<div class="dk-context-lock-banner">${this.esc(line)}</div>`;
    }

    // v3 form (CHG-3216): a top stepper bar + the single active step, replacing
    // the old progressive accordion (completed-step summary rows). Back-nav is
    // via the stepper's completed pips. The active step's full form renders into
    // #dk-active-step below.
    let html = previewBanner + brandHeaderHtml + sessionProofHtml + returningBanner + headerHtml + contextLockBanner + '<div class="dk-progressive">';
    // The intent fork precedes both possible journeys. Hide the route-specific
    // stepper until the donor chooses, so Pay Later is never paired with a
    // misleading Payment-stage promise.
    if (this.step !== 'intent') html += this.renderStepperBar(visibleSteps);
    html += '<div class="dk-active-step" id="dk-active-step"></div>';
    html += '</div>';
    root.innerHTML = html;

    // Render the active step into the placeholder
    const activeEl = root.querySelector<HTMLElement>('#dk-active-step');
    if (activeEl) {
      switch (this.step) {
        case 'intent': this.renderIntentInto(activeEl); break;
        case 'designation': this.renderDesignationInto(activeEl); break;
        case 'amount': this.renderAmountInto(activeEl); break;
        case 'frequency': this.renderFrequencyInto(activeEl); break;
        case 'pledge': this.renderPledgeInto(activeEl); break;
        case 'donor': this.renderDonorInto(activeEl); break;
        case 'payment': void this.renderPaymentInto(activeEl); break;
      }
      // CHG-3225 (fix #264) — play the enter-transition only when the step
      // actually changed, so stepping between stages reads as a smooth swap
      // rather than an abrupt snap (the QA report), while in-step re-renders
      // (validation errors, payment-method toggles) stay still. The animation
      // itself is CSS (.dk-step-enter) and is neutralised under
      // prefers-reduced-motion by the existing reduced-motion block.
      if (this.step !== this.lastAnimatedStep) {
        activeEl.classList.add('dk-step-enter');
        this.lastAnimatedStep = this.step;
      }
      // donate-page v3: pin the actions row and let the rest of the step scroll
      // (mock parity — fixed-height card, .step-body scrolls, .actions pinned).
      // Done centrally here so every stepper-based step gets it without editing
      // each renderer. Payment renders into this.container directly and handles
      // its own scroll/pin, so it's excluded. v3-gated → embeds unaffected.
      if (this.config.formStyle === 'v3' && this.step !== 'payment') {
        this.applyV3StepScroll(activeEl);
      }
    }

    // Back-navigation via the stepper's completed pips (click + keyboard).
    root.querySelectorAll<HTMLElement>('[data-edit]').forEach(el => {
      const go = (e: Event) => {
        e.stopPropagation();
        const target = el.dataset['edit'] as Step;
        if (target) this.goTo(target);
      };
      el.addEventListener('click', go);
      el.addEventListener('keydown', (e) => {
        const k = (e as KeyboardEvent).key;
        if (k === 'Enter' || k === ' ') { e.preventDefault(); go(e); }
      });
    });

    this.notifyResize();
  }

  private notifyResize(): void {
    requestAnimationFrame(() => {
      const targetOrigin = checkoutReturnTargetOrigin() || '*';
      window.parent.postMessage(
        { type: 'ag-widget-resize', height: this.container.scrollHeight + 48 },
        targetOrigin
      );
    });
  }

  private stepIndex(step: Step): number {
    const order: Step[] = ['loading', 'intent', 'designation', 'amount', 'frequency', 'pledge', 'donor', 'payment', 'confirm', 'error'];
    return order.indexOf(step);
  }

  /**
   * donate-page v3 — restructure a freshly-rendered step so its content scrolls
   * and the actions row stays pinned, matching the template-v3 mock
   * (.step-body { flex:1; overflow-y:auto } + pinned .actions). Moves the
   * trailing .dk-actions out, wraps the remaining children in a .dk-step-body
   * scroll container, then re-appends .dk-actions as a flex-shrink:0 footer.
   * Idempotent: skips if already wrapped. CSS in checkout.css (.dk-form-v3).
   */
  private applyV3StepScroll(stepEl: HTMLElement): void {
    if (stepEl.querySelector(':scope > .dk-step-body')) return; // already wrapped
    const actions = stepEl.querySelector<HTMLElement>(':scope > .dk-actions');
    const body = document.createElement('div');
    body.className = 'dk-step-body';
    // Move every direct child except the actions row into the scroll body.
    const children = Array.from(stepEl.children) as HTMLElement[];
    for (const child of children) {
      if (child === actions) continue;
      body.appendChild(child);
    }
    // Body first (scrolls), actions last (pinned).
    stepEl.insertBefore(body, actions ?? null);
  }

  // ── Step renderers ─────────────────────────────────────────────────

  private renderIntentInto(el: HTMLElement): void {
    const title = this.vocab('intent_title', 'How would you like to give?');
    const subtitle = this.vocab(
      'intent_subtitle',
      'Donate today, or make a pledge and pay later.',
    );
    el.innerHTML = `
      <div class="dk-give-intent">
        <h3 class="dk-step-title">${this.esc(title)}</h3>
        <p class="dk-step-sub">${this.esc(subtitle)}</p>
        <div class="dk-give-intent-options">
          <button class="dk-give-intent-option" id="dk-give-intent-now" type="button">
            <span class="dk-give-intent-label">${this.esc(this.vocab('intent_donate_now', 'Donate Now'))}</span>
            <span class="dk-give-intent-hint">${this.esc(this.vocab('intent_donate_now_hint', 'Make a secure payment today.'))}</span>
          </button>
          <button class="dk-give-intent-option" id="dk-give-intent-later" type="button">
            <span class="dk-give-intent-label">${this.esc(this.vocab('intent_donate_later', 'Pledge Now, Pay Later'))}</span>
            <span class="dk-give-intent-hint">${this.esc(this.vocab('intent_donate_later_hint', 'Choose a due date. Nothing is charged today.'))}</span>
          </button>
        </div>
      </div>`;

    const select = (intent: Exclude<DonorIntent, null>): void => {
      this.donorIntent = intent;
      this.intentVetoNotice = '';
      this.pledgeError = null;
      this.pledgeId = null;
      this.pledgeDonorId = null;
      this.pledgeStartDate = '';
      this.pledgeDueDate = '';
      this.pledgeNextReminderAt = '';
      this.selectedFrequency = 'one-time';
      if (this.effectiveContextLock) this.seedContextLockedSelection();
      // An unlocked multi-target checkout must re-offer its picker every time
      // the donor enters a newly selected intent. A previous journey's target
      // remains preselected for convenience, but cannot make the picker
      // unreachable (QA #387). Locked and already-seeded single-target flows
      // keep their direct path to Amount.
      const pickerVisible = !this.effectiveContextLock
        && (this.designations.length > 1 || this.campaignOptions.length > 0);
      const target = pickerVisible
        ? 'designation'
        : (this.effectiveContextLock || this.selectedDesignation || this.selectedCampaign
            ? 'amount'
            : 'designation');
      this.goTo(target);
    };
    el.querySelector('#dk-give-intent-now')?.addEventListener('click', () => select('now'));
    el.querySelector('#dk-give-intent-later')?.addEventListener('click', () => select('later'));
  }

  private renderDesignationInto(el: HTMLElement): void {
    // CHG-0171 W4 — default-widget picker (Option C): when scope_type
    // is 'all' and there are active+public campaigns, render them as
    // cards with progress bars at the top of the picker, above the
    // designations list. Clicking a campaign sets selectedCampaign
    // + selectedCascade; clicking a designation sets
    // selectedDesignation + selectedCascade. Either path advances
    // to 'amount'.
    const campaignsForPicker =
      this.config.scopeType === 'all' ? this.campaignOptions : [];

    // Group designations by type for visual hierarchy
    const groups: Record<string, Designation[]> = {};
    for (const cat of this.designations) {
      const type = cat.designation_type || 'fund';
      if (!groups[type]) groups[type] = [];
      groups[type].push(cat);
    }
    const typeLabels: Record<string, string> = { cause: 'Causes', fund: 'Funds', program: 'Programs', project: 'Projects', milestone: 'Milestones' };
    const typeOrder = ['cause', 'fund', 'program', 'project', 'milestone'];
    const sortedTypes = Object.keys(groups).sort((a, b) => (typeOrder.indexOf(a) - typeOrder.indexOf(b)) || a.localeCompare(b));

    // Only show group headers if there are multiple types OR if
    // campaigns are also rendered above (reader needs to know which
    // section they're in).
    const showHeaders = sortedTypes.length > 1 || campaignsForPicker.length > 0;

    // CHG-3217 — donate v3 grouped target picker (reference fidelity).
    // Campaigns + each designation type render as uniform reference-style
    // rows: icon tile · name (+optional Featured tag) · "Noun · subtitle"
    // · selected-check. Replaces the old plain .dk-designation-card cards
    // + inline progress bars. Designation icons come from the parallel
    // DesignationOption (W3 data path); Designation itself has no icon, so
    // we fall back to a monogram of the name's first letter.
    const iconById = new Map(this.designationOptions.map(o => [o.id, o.icon]));
    const typeNoun: Record<string, string> = { cause: 'Cause', fund: 'Fund', program: 'Program', project: 'Project', milestone: 'Milestone' };

    const targetRow = (o: {
      idAttr: string; name: string; icon?: string | null; noun: string;
      desc?: string | null; pct?: number | null; hasGoal?: boolean;
      selected: boolean; featured?: boolean;
    }): string => {
      let subtitle = o.noun;
      if (o.hasGoal && o.pct != null) subtitle += ` · ${Math.max(0, Math.min(100, Math.round(o.pct)))}% funded`;
      else if (o.desc) subtitle += ` · ${o.desc}`;
      const icon = this.esc(o.icon ? o.icon : (o.name.trim().charAt(0).toUpperCase() || '?'));
      const monoCls = o.icon ? '' : ' dk-target-ic--mono';
      const tag = o.featured ? `<span class="dk-target-tag">${this.esc(this.vocab('featured_tag', 'Featured'))}</span>` : '';
      return `
          <button class="dk-target" ${o.idAttr} aria-pressed="${o.selected ? 'true' : 'false'}">
            <span class="dk-target-ic${monoCls}" aria-hidden="true">${icon}</span>
            <span class="dk-target-body">
              <span class="dk-target-name">${this.esc(o.name)}${tag}</span>
              <span class="dk-target-desc">${this.esc(subtitle)}</span>
            </span>
            <span class="dk-target-check" aria-hidden="true"></span>
          </button>`;
    };

    let html = `<h3 class="dk-step-title">${this.esc(this.vocab('designation_step_title', 'What would you like to support?'))}</h3>`;
    if (showHeaders) {
      html += `<p class="dk-step-sub">${this.esc(this.vocab('designation_step_sub', 'Choose a campaign, fund, or program.'))}</p>`;
    }
    if (this.intentVetoNotice) {
      html += `<div class="dk-give-intent-veto-notice" role="alert">${this.esc(this.intentVetoNotice)}</div>`;
    }
    html += '<div class="dk-target-list">';

    // CHG-0171 W4 — campaigns render above the designation groups; clicking
    // one sets selectedCampaign + cascade (handler below). Forward-compatible
    // Featured tag: rendered only if the backend marks the option.
    if (campaignsForPicker.length > 0) {
      html += `<div class="dk-designation-group-header">${this.esc(this.vocab('campaigns_group', 'Campaigns'))}</div>`;
      for (const camp of campaignsForPicker) {
        const featured = (camp as unknown as { is_featured?: boolean }).is_featured === true;
        html += targetRow({
          idAttr: `data-campaign-id="${this.esc(camp.id)}"`,
          name: camp.name,
          noun: this.vocab('campaign_noun', 'Campaign'),
          desc: camp.description,
          pct: camp.progress_pct,
          hasGoal: camp.goal_amount_minor_units > 0,
          selected: this.selectedCampaign?.id === camp.id,
          featured,
        });
      }
    }

    const designationRow = (cat: Designation, name = cat.name): string => {
      const type = cat.designation_type || 'fund';
      return targetRow({
        idAttr: `data-id="${this.esc(cat.id)}"`,
        name,
        icon: iconById.get(cat.id) ?? null,
        noun: typeNoun[type] || (type.charAt(0).toUpperCase() + type.slice(1)),
        desc: cat.description,
        pct: cat.progress_pct,
        hasGoal: !!(cat.goal_amount_minor_units && cat.goal_amount_minor_units > 0),
        selected: this.selectedDesignation?.id === cat.id,
      });
    };

    const pageContextType = (this.config.pageContextType || '').toUpperCase();
    if (pageContextType === 'PROGRAM') {
      const allowedIds = new Set(this.designations.map(d => d.id));
      const renderedMilestones = new Set<string>();
      for (const project of groups['project'] || []) {
        html += `<div class="dk-designation-group-header">${this.esc(project.name)}</div>`;
        html += designationRow(project, this.vocab('whole_project_option', 'Whole project'));
        for (const child of project.children || []) {
          if (child.designation_type !== 'milestone' || !allowedIds.has(child.id)) continue;
          html += designationRow(child);
          renderedMilestones.add(child.id);
        }
      }
      const ungrouped = (groups['milestone'] || []).filter(m => !renderedMilestones.has(m.id));
      if (ungrouped.length > 0) {
        html += `<div class="dk-designation-group-header">${this.esc(this.vocab('milestones_group', 'Milestones'))}</div>`;
        for (const milestone of ungrouped) html += designationRow(milestone);
      }
    } else if (pageContextType === 'PROJECT') {
      const project = (groups['project'] || [])[0];
      if (project) {
        html += `<div class="dk-designation-group-header">${this.esc(this.vocab('whole_project_group', 'Whole project'))}</div>`;
        html += designationRow(project);
      }
      const milestones = groups['milestone'] || [];
      if (milestones.length > 0) {
        html += `<div class="dk-designation-group-header">${this.esc(this.vocab('specific_milestone_group', 'Specific milestone'))}</div>`;
        for (const milestone of milestones) html += designationRow(milestone);
      }
    } else {
      for (const type of sortedTypes) {
        if (showHeaders) {
          html += `<div class="dk-designation-group-header">${this.esc(typeLabels[type] || type.charAt(0).toUpperCase() + type.slice(1) + 's')}</div>`;
        }
        for (const cat of groups[type]) html += designationRow(cat);
      }
    }
    html += '</div>';
    // donate-page v3: the mock's Support step has an explicit Continue (it does
    // not auto-advance on select). Under v3 we render a Continue that's disabled
    // until a target is chosen; non-v3 keeps the historical auto-advance (no
    // button). Gated so embeds/other placements are unchanged.
    const desigV3 = this.config.formStyle === 'v3';
    const hasSelection = !!(this.selectedDesignation || this.selectedCampaign);
    if (desigV3) {
      const continueButton = `<button class="dk-btn-primary" id="dk-designation-next"${hasSelection ? '' : ' disabled'}>${this.esc(this.vocab('continue_button', 'Continue'))}</button>`;
      html += `<div class="dk-actions">
        ${this.intentStepAvailable()
          ? this.v3ActionButtons('dk-designation-back', continueButton)
          : continueButton}
      </div>`;
    } else if (this.intentStepAvailable()) {
      html += `<div class="dk-actions"><button class="dk-btn-secondary" id="dk-designation-back" type="button">${this.esc(this.vocab('back_button', 'Back'))}</button></div>`;
    }
    el.innerHTML = html;

    // CHG-0171 W4 — campaign card clicks set selectedCampaign +
    // selectedCascade (from the option's cascade block). Under v3, selecting
    // only marks the choice (Continue advances); otherwise auto-advance.
    el.querySelectorAll<HTMLButtonElement>('.dk-target[data-campaign-id]').forEach(btn => {
      btn.addEventListener('click', () => {
        const id = btn.dataset['campaignId'];
        const opt = this.campaignOptions.find(o => o.id === id) ?? null;
        this.selectedCampaign = opt;
        this.selectedDesignation = null;
        this.setSelectedCascade(opt ? opt.cascade : null);
        const allowed = this.selectedEntityAcceptsCurrentIntent();
        if (desigV3 || !allowed) { this.render(); } else { this.goTo('amount'); }
      });
    });

    el.querySelectorAll<HTMLButtonElement>('.dk-target[data-id]').forEach(btn => {
      btn.addEventListener('click', () => {
        const id = btn.dataset['id'];
        this.selectedDesignation = this.designations.find(c => c.id === id) ?? null;
        // CHG-0171 W4 — pick up the cascade from the matching option if
        // the default-widget flow loaded it. Legacy designations-only
        // widgets (pre-W3 data path) won't have designationOptions;
        // selectedCascade stays null and effective* falls back to
        // legacy config flags.
        const opt = this.designationOptions.find(o => o.id === id) ?? null;
        this.setSelectedCascade(opt ? opt.cascade : null);
        this.selectedCampaign = null;
        const allowed = this.selectedEntityAcceptsCurrentIntent();
        if (desigV3 || !allowed) { this.render(); } else { this.goTo('amount'); }
      });
    });

    el.querySelector('#dk-designation-back')?.addEventListener('click', () => this.goTo('intent'));
    // v3 Continue → amount (only enabled once a target is selected).
    el.querySelector('#dk-designation-next')?.addEventListener('click', () => {
      if (
        (this.selectedDesignation || this.selectedCampaign)
        && this.selectedEntityAcceptsCurrentIntent()
      ) this.goTo('amount');
    });
    this.notifyResize();
  }

  private renderAmountInto(el: HTMLElement): void {
    // CHG-0171 W4 — non-session amounts come from the selected entity's
    // cascade block (effective suggested_amounts, narrowed by the widget's
    // overrides on the backend). Hosted giving sessions are different:
    // their checkout_config.preset_amounts is the session-level page policy,
    // so it must win over the locked default designation's presets.
    // CHG-1657 (W3.2) — envelope-level suggested_amounts adds a layer
    // between the selected entity cascade (most-specific) and the
    // legacy designation/config fallback. Resolution order:
    //   1. Page policy for Session and hosted Campaign contexts
    //   2. Selected entity cascade.suggested_amounts (CHG-0171 W4)
    //   3. Envelope amount_bounds.suggested_amounts (CHG-1657 W3.2)
    //   4. Legacy selectedDesignation.suggested_amounts (pre-W1 data)
    //   5. Legacy config.presetAmounts (defensive default; DEPRECATED)
    const cascadePresets = this.selectedCascade?.suggested_amounts ?? [];
    const envelopePresets = getSuggestedAmountsMinorUnits(this.envelope);
    const legacyPresets = this.selectedDesignation?.suggested_amounts ?? [];
    const configPresets = this.config.presetAmounts ?? [];
    let source = configPresets;
    if (this.usesHostedCampaignPolicy()) {
      // An empty Campaign policy is still authoritative. Do not leak linked
      // Fund presets into a Campaign page merely because this list is empty.
      source = configPresets;
    } else if (this.config.source === 'session' && this.config.presetAmountSource) {
      source = configPresets;
    } else if (this.config.source === 'session' && configPresets.length) {
      source = configPresets;
    } else if (cascadePresets.length) {
      source = cascadePresets;
    } else if (envelopePresets.length) {
      source = envelopePresets;
    } else if (legacyPresets.length) {
      source = legacyPresets;
    }
    const rawPresets = source.map((c: number) => this.toMajorUnits(c));
    const sym = this.getCurrencySymbol();

    // CHG-P — currency picker, only when org enabled multiple currencies.
    // Sourced from OrgBranding.supported_currencies (CHG-P). The native
    // <select> matches the rest of the widget's form-control styling and
    // works without a date-picker-style dropdown library. When the donor
    // changes currency we re-render the amount step so presets +
    // currency symbol re-format in the new denomination.
    const currencyPickerHtml = this.supportedCurrencies && this.supportedCurrencies.length > 1
      ? `<div class="dk-currency-picker" style="margin-bottom:1rem">
          <label for="dk-currency-select" style="display:block;font-size:0.875rem;margin-bottom:0.25rem;color:var(--dk-text-muted)">Currency</label>
          <select id="dk-currency-select" class="dk-select" style="width:100%;padding:0.5rem;border:1px solid var(--dk-border);border-radius:var(--dk-radius);background:var(--dk-bg);color:var(--dk-text)">
            ${this.supportedCurrencies.map(c => `<option value="${this.esc(c)}"${c === this.config.currency.toUpperCase() ? ' selected' : ''}>${this.esc(c)}</option>`).join('')}
          </select>
        </div>`
      : '';

    // donate-page v3: eyebrow + "Choose your gift" title + sub (mock parity).
    // Gated on formStyle:'v3' so embeds/other orgs keep "Choose an amount".
    const v3 = this.config.formStyle === 'v3';
    const amountEyebrow = v3
      ? `<div class="dk-step-eyebrow">${this.esc(this.vocab('amount_step_eyebrow', 'Your gift'))}</div>`
      : '';
    const amountTitle = v3
      ? this.esc(this.vocab('amount_step_title', 'Choose your gift'))
      : 'Choose an amount';
    const amountSub = v3
      ? `<p class="dk-step-sub">${this.esc(this.vocab('amount_step_sub', "Pick an amount and how often you'd like to give."))}</p>`
      : '';
    el.innerHTML = `
      ${amountEyebrow}
      <h3 class="dk-step-title">${amountTitle}</h3>
      ${amountSub}
      ${currencyPickerHtml}
      ${rawPresets.length > 0 ? `<div class="dk-presets">
        ${rawPresets.map(amt => {
          const impact = this.config.amountImpacts?.find(i => i.amount_minor_units === this.toMinorUnits(amt));
          return `<button class="dk-preset${this.selectedAmount === amt ? ' dk-active' : ''}" data-amount="${amt}"><span class="dk-preset-amount">${this.fmtCurrency(amt)}</span>${impact ? `<span class="dk-impact-label">${this.esc(impact.label)}</span>` : ''}</button>`;
        }).join('')}
      </div>` : ''}
      <div class="dk-custom-amount">
        <label for="dk-custom">${this.esc(this.vocab('amount_other_label', 'Other amount'))}</label>
        <div class="dk-amount-input-wrap">
          <span class="dk-currency-symbol">${sym}</span>
          <input id="dk-custom" type="number" min="1" step="1" placeholder="0" />
        </div>
      </div>
      <p class="dk-error dk-hidden" id="dk-amount-error">Please enter a valid amount (minimum ${this.fmtCurrency(this.toMajorUnits(this.effectiveMinMinorUnits() ?? this.config.minAmount ?? 1))}).</p>${/* CHG-3058 (DRIFT-1181): effectiveMinMinorUnits()/config.minAmount are MINOR units; fmtCurrency expects MAJOR, so toMajorUnits here too. CHG-3220: per-currency exponent. */ ''}
      ${this.config.coverFeesEnabled ? `
      <div class="dk-cover-fees">
        <input type="checkbox" id="cover-fees" ${this.coverFeesChecked ? 'checked' : ''} />
        <label for="cover-fees">Cover processing fees ${this.selectedAmount > 0 ? '(' + this.fmtCurrency(this.computeFeeAmount(this.selectedAmount)) + ')' : ''} so 100% goes to the cause</label>
      </div>` : ''}
      ${(() => {
        // donate-page v3: Continue ALWAYS sits in a pinned .dk-actions row (so it
        // matches every other step's pinned footer). A Back is added only when
        // Amount is NOT the first donor-visible step. An unlocked picker OR the
        // donor-intent fork may precede it. In particular, a locked campaign /
        // session still needs Back after Donate Now or Pledge Now so the donor
        // can revise that explicit choice without restarting checkout.
        const pickerVisible = !this.effectiveContextLock
          && (this.designations.length > 1 || this.campaignOptions.length > 0);
        const amountHasBack = v3 && (pickerVisible || this.intentStepAvailable());
        const contBtn = `<button class="dk-btn-primary${this.config.buttonStyle === 'outlined' ? ' dk-btn-outlined' : ''}" id="dk-amount-next"${v3 ? '' : ' style="margin-top:1.25rem"'}>${this.esc(this.vocab('continue_button', 'Continue'))}</button>`;
        if (!v3) return contBtn;
        const buttons = amountHasBack
          ? this.v3ActionButtons('dk-amount-back', contBtn)
          : contBtn;
        return `<div class="dk-actions" style="margin-top:1.25rem">${buttons}</div>`;
      })()}`;

    // CHG-P — currency picker handler. Updating config.currency re-runs
    // render() so presets + currency symbol re-format in the new currency.
    // selectedAmount stays the same number (e.g. donor selected "100"
    // in USD; switching to BDT keeps "100" selected — they may need to
    // reconsider, but we don't silently change their amount).
    const currencySelect = el.querySelector<HTMLSelectElement>('#dk-currency-select');
    if (currencySelect) {
      currencySelect.addEventListener('change', () => {
        this.config.currency = currencySelect.value;
        this.render();
      });
    }

    // Cover fees checkbox handler
    const coverFeesCheck = el.querySelector<HTMLInputElement>('#cover-fees');
    if (coverFeesCheck) {
      coverFeesCheck.addEventListener('change', () => {
        this.coverFeesChecked = coverFeesCheck.checked;
        this.render();
      });
    }

    const customInput = el.querySelector<HTMLInputElement>('#dk-custom')!;
    el.querySelectorAll<HTMLButtonElement>('.dk-preset').forEach(btn => {
      btn.addEventListener('click', () => {
        el.querySelectorAll('.dk-preset').forEach(b => b.classList.remove('dk-active'));
        btn.classList.add('dk-active');
        customInput.value = '';
        this.selectedAmount = Number(btn.dataset['amount']);
      });
    });
    customInput.addEventListener('input', () => {
      el.querySelectorAll('.dk-preset').forEach(b => b.classList.remove('dk-active'));
      this.selectedAmount = 0;
    });
    // donate-page v3: Amount Back follows the actual predecessor — an unlocked
    // designation/support picker first, otherwise the donor-intent fork.
    el.querySelector('#dk-amount-back')?.addEventListener('click', () => {
      const pickerVisible = !this.effectiveContextLock
        && (this.designations.length > 1 || this.campaignOptions.length > 0);
      this.goTo(pickerVisible ? 'designation' : (this.intentStepAvailable() ? 'intent' : 'designation'));
    });

    el.querySelector('#dk-amount-next')?.addEventListener('click', () => {
      const custom = parseFloat(customInput.value || '0');
      const amount = this.selectedAmount || custom;
      const err = el.querySelector<HTMLElement>('#dk-amount-error')!;
      // CHG-0171 W4 — prefer the narrowed cascade min/max over the
      // legacy widget-only config values.
      // CHG-3058 (DRIFT-1181) — `min`/`max` are MINOR units (cents):
      // effectiveMin/MaxMinorUnits() return cascade.min_amount_minor_units /
      // envelope amount_bounds (both unambiguously minor) and fall back to
      // config.minAmount / config.maxAmount, which are ALSO minor units — the
      // "Effective ... in cents" contract on effectiveMinMinorUnits() consumes
      // them as such, and the default minAmount:1 is a 1-cent (inert) floor.
      // `amount` is MAJOR units (selectedAmount / parseFloat of the dollar
      // input), so scale it to minor units the SAME way the wire does
      // (toMinorUnits, cf. the cycle-0 charge and the CHG-3056 pledge fix)
      // BEFORE comparing, and render the threshold via fmtCurrency(toMajorUnits)
      // since fmtCurrency expects a MAJOR amount. CHG-3220: per-currency
      // exponent so JPY/BHD bounds compare/display correctly.
      const min = this.effectiveMinMinorUnits() ?? this.config.minAmount ?? 1;
      const max = this.effectiveMaxMinorUnits() ?? this.config.maxAmount;
      const amountMinorUnits = this.toMinorUnits(amount);
      if (!amount || amountMinorUnits < min) {
        err.textContent = `Minimum donation is ${this.fmtCurrency(this.toMajorUnits(min))}`;
        err.classList.remove('dk-hidden');
        return;
      }
      if (max && amountMinorUnits > max) {
        err.textContent = `Maximum donation is ${this.fmtCurrency(this.toMajorUnits(max))}`;
        err.classList.remove('dk-hidden');
        return;
      }
      err.classList.add('dk-hidden');
      // CHG-2813 (DRIFT-1084a) — committing the amount defines (or redefines)
      // THIS charge, so mint a FRESH per-attempt idempotency key here. This is
      // the authoritative mint point: a donor who goes back and changes the
      // amount starts a distinct charge and must get a distinct key (reusing a
      // key with a different amount would make Stripe reject the create rather
      // than dedupe). The `||=` fallback in init*Payment only fires for paths
      // that reach payment without re-committing an amount (e.g. restored
      // redirect-return state).
      this.donationAttemptKey = generateIdempotencyKey();
      // CHG-3027 / PLG-R01a — a changed amount is a materially different
      // pledge, so clear the per-attempt pledge key; the next pledge submit
      // mints a fresh one (lazy `||=`). Mirrors the donation key reset above.
      this.pledgeAttemptKey = '';
      this.selectedAmount = amount;
      // CHG-3053 / DRIFT-1165 — if a pledge was already created and the donor
      // came back to change the amount, the new amount diverges from the
      // persisted pledge; supersede it so the donor-step re-runs submitPledge()
      // with the new amount (no silent divergence from the cycle-0 charge).
      this.supersedePledgeIfMaterialEdit();
      this.goTo('frequency');
    });
    this.notifyResize();
  }

  private renderFrequencyInto(el: HTMLElement): void {
    // CHG-0047 P6 — if config.recurringIntents is populated, render
    // intent cards (Jumu'ah, Shabbat, Sunday tithe, etc.) resolved
    // server-side. Otherwise fall back to the legacy one-time/monthly/
    // quarterly picker.
    if (this.config.recurringIntents && this.config.recurringIntents.length > 0) {
      this.renderIntentFrequencyInto(el);
      return;
    }

    // CHG-M — labels route through vocabulary overlay; English defaults
    // preserved for orgs that haven't customized their variant.
    const options = [
      { value: 'one-time' as const, label: this.frequencyLabel('one-time') },
      { value: 'monthly' as const, label: this.frequencyLabel('monthly') },
      { value: 'quarterly' as const, label: this.frequencyLabel('quarterly') },
    ];
    const mostImpactText = this.vocab('frequency_most_impact', 'Most Impact');
    const questionText = this.vocab('frequency_question', 'How often?');
    const continueText = this.vocab('continue_button', 'Continue');
    // CHG — conversion nudge: always flag monthly as the recommended
    // (highest-impact) cadence, not only when the org pre-set monthly as
    // default, and show a one-line "why monthly" benefit under the picker.
    // Both strings route through the vocabulary overlay so a brand can
    // localize or soften the ask. Decorative only — selection logic and
    // the donor's actual choice are unchanged.
    const recurringBenefit = this.vocab(
      'frequency_recurring_benefit',
      'Monthly gifts give sustained support all year — and you can change or cancel anytime.',
    );

    // donate-page v3: pair the Continue with a Back (→ amount) in an actions
    // row, matching the mock. Non-v3 keeps the standalone full-width Continue.
    const freqV3 = this.config.formStyle === 'v3';
    const freqContinueBtn = `<button class="dk-btn-primary${this.config.buttonStyle === 'outlined' ? ' dk-btn-outlined' : ''}" id="dk-frequency-next"${freqV3 ? '' : ' style="margin-top:1.25rem"'}>${this.esc(continueText)}</button>`;
    const freqActions = freqV3
      ? `<div class="dk-actions" style="margin-top:1.25rem">${this.v3ActionButtons('dk-frequency-back', freqContinueBtn)}</div>`
      : freqContinueBtn;
    el.innerHTML = `
      <h3 class="dk-step-title">${this.esc(questionText)}</h3>
      <div class="dk-frequency-options">
        ${options.map(opt => `
          <button class="dk-frequency-option${this.selectedFrequency === opt.value ? ' dk-active' : ''}${opt.value === 'monthly' ? ' dk-recommended' : ''}" data-frequency="${opt.value}">
            ${this.esc(opt.label)}${opt.value === 'monthly' ? `<span class="dk-impact-badge">${this.esc(mostImpactText)}</span>` : ''}
          </button>`).join('')}
      </div>
      <p class="dk-frequency-benefit">${this.esc(recurringBenefit)}</p>
      ${freqActions}`;
    el.querySelector('#dk-frequency-back')?.addEventListener('click', () => this.goTo('amount'));

    el.querySelectorAll<HTMLButtonElement>('.dk-frequency-option').forEach(btn => {
      btn.addEventListener('click', () => {
        el.querySelectorAll('.dk-frequency-option').forEach(b => b.classList.remove('dk-active'));
        btn.classList.add('dk-active');
        this.selectedFrequency = btn.dataset['frequency'] as 'one-time' | 'monthly' | 'quarterly';
        this.selectedIntentKey = null;
      });
    });
    el.querySelector('#dk-frequency-next')?.addEventListener('click', () => this.goTo('donor'));
    this.notifyResize();
  }

  // CHG-0047 P6 — intent-card render for when config.recurringIntents
  // is populated. Always prepends a "One-time" option so donors can
  // still give without commitment. The culturalHintsEnabled flag
  // (default true) controls the "Cultural guidance under review" ribbon
  // on pending_review intents.
  private renderIntentFrequencyInto(el: HTMLElement): void {
    const intents = this.config.recurringIntents ?? [];
    const showHints = this.config.culturalHintsEnabled !== false;

    // CHG-M — one-time card labels route through vocabulary; defaults preserved.
    const oneTimeLabel = this.frequencyLabel('one-time');
    const oneTimeDescription = this.vocab(
      'frequency_one_time_description',
      'A single gift, no recurring schedule.',
    );
    const oneTimeCard = `
      <button class="dk-intent-option${this.selectedIntentKey === null && this.selectedFrequency === 'one-time' ? ' dk-active' : ''}"
              data-intent="__one_time__">
        <div class="dk-intent-label">${this.esc(oneTimeLabel)}</div>
        <div class="dk-intent-description">${this.esc(oneTimeDescription)}</div>
      </button>`;

    const intentCards = intents.map(intent => {
      const ribbon = showHints && intent.attestation_status === 'pending_review'
        ? `<span class="dk-intent-ribbon" title="This cultural option is awaiting subject-matter expert review. You can still select it.">Under review</span>`
        : '';
      const preview = intent.schedule_preview && intent.schedule_preview.length > 0
        ? `<div class="dk-intent-preview">Next: ${intent.schedule_preview.slice(0, 3).map(s => this.formatPreviewDate(s)).join(' · ')}</div>`
        : '';
      const description = intent.description
        ? `<div class="dk-intent-description">${this.escapeHtml(intent.description)}</div>`
        : '';
      const active = this.selectedIntentKey === intent.intent_key ? ' dk-active' : '';
      return `
        <button class="dk-intent-option${active}" data-intent="${this.escapeHtml(intent.intent_key)}">
          <div class="dk-intent-label">${this.escapeHtml(intent.label)}${ribbon}</div>
          ${description}
          ${preview}
        </button>`;
    }).join('');

    // CHG-M — picker heading + confirm button route through vocabulary.
    const questionText = this.vocab('frequency_question', 'How often?');
    const continueText = this.vocab('continue_button', 'Continue');
    // donate-page v3: pair the Continue with a Back (→ amount) in a pinned
    // actions row — same as the legacy frequency step (renderFrequencyInto) so
    // the cultural-intent picker has v3 parity. Non-v3 keeps the standalone
    // full-width Continue. (applyV3StepScroll pins :scope > .dk-actions.)
    const intentV3 = this.config.formStyle === 'v3';
    const intentContinueBtn = `<button class="dk-btn-primary${this.config.buttonStyle === 'outlined' ? ' dk-btn-outlined' : ''}" id="dk-frequency-next"${intentV3 ? '' : ' style="margin-top:1.25rem"'}>${this.esc(continueText)}</button>`;
    const intentActions = intentV3
      ? `<div class="dk-actions" style="margin-top:1.25rem">${this.v3ActionButtons('dk-frequency-back', intentContinueBtn)}</div>`
      : intentContinueBtn;
    el.innerHTML = `
      <h3 class="dk-step-title">${this.esc(questionText)}</h3>
      <div class="dk-intent-options">
        ${oneTimeCard}
        ${intentCards}
      </div>
      ${intentActions}`;
    el.querySelector('#dk-frequency-back')?.addEventListener('click', () => this.goTo('amount'));

    el.querySelectorAll<HTMLButtonElement>('.dk-intent-option').forEach(btn => {
      btn.addEventListener('click', () => {
        el.querySelectorAll('.dk-intent-option').forEach(b => b.classList.remove('dk-active'));
        btn.classList.add('dk-active');
        const intentKey = btn.dataset['intent'] ?? '__one_time__';
        if (intentKey === '__one_time__') {
          this.selectedFrequency = 'one-time';
          this.selectedIntentKey = null;
        } else {
          this.selectedIntentKey = intentKey;
          // Derive a legacy frequency label for server payload
          // back-compat — actual schedule comes from intent_key snapshot.
          const intent = intents.find(i => i.intent_key === intentKey);
          this.selectedFrequency = this.mapIntentToLegacyFrequency(intent);
        }
      });
    });
    el.querySelector('#dk-frequency-next')?.addEventListener('click', () => this.goTo('donor'));
    this.notifyResize();
  }

  // Back-compat: map an intent's RRULE FREQ to the legacy widget
  // frequency enum for any downstream code that still reads it.
  // intent_key is the authoritative value in the POST payload — this
  // is only for display + legacy code paths.
  private mapIntentToLegacyFrequency(
    intent: { schedule_template?: { rrule?: string } } | undefined,
  ): 'one-time' | 'monthly' | 'quarterly' {
    const rrule = intent?.schedule_template?.rrule ?? '';
    if (rrule.includes('FREQ=MONTHLY') && rrule.includes('INTERVAL=3')) return 'quarterly';
    if (rrule.includes('FREQ=WEEKLY')) return 'monthly'; // closest legacy match
    if (rrule.includes('FREQ=MONTHLY')) return 'monthly';
    if (rrule.includes('FREQ=YEARLY')) return 'quarterly'; // closest legacy match
    return 'monthly';
  }

  private formatPreviewDate(iso: string): string {
    try {
      const d = new Date(iso);
      return d.toLocaleDateString(this.config.locale || undefined, {
        month: 'short', day: 'numeric',
      });
    } catch {
      return iso;
    }
  }

  private formatPledgeDate(iso: string): string {
    try {
      const dateOnly = /^\d{4}-\d{2}-\d{2}$/.test(iso);
      const date = new Date(dateOnly ? `${iso}T00:00:00` : iso);
      if (Number.isNaN(date.getTime())) return iso;
      return date.toLocaleDateString(this.config.locale || undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    } catch {
      return iso;
    }
  }

  private escapeHtml(s: string): string {
    return String(s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;')
      .replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  private async fetchCapturePromptIfNeeded(): Promise<void> {
    // CHG-0168 P1 — resolve contact-field requirements per origin+country.
    // Fails open: any error leaves this.capturePrompt null and the
    // form renders with the legacy email+phone optional contract.
    if (this.capturePrompt) return;
    try {
      this.capturePrompt = await fetchCapturePrompt({
        country: this.phoneCountry,
        has_email: !!this.donorInfo.email,
        has_paired_device: false, // widget has no paired-device concept
        origin: 'donation',
      });
    } catch (err) {
      // Silent fail-open — the legacy UI (email + phone optional) is
      // a safe superset of every capture-prompt decision.
      // eslint-disable-next-line no-console
      console.debug('[widget] capture-prompt fetch failed; using legacy form', err);
      this.capturePrompt = null;
    }
  }

  // CHG-0932 / ITER-C2 — pledge step renderer.
  //
  // Spec contract: ref:tier-5-cross-screen/widget-pledge-step
  //
  // Renders when cascade.allow_pledge && !cascade.allow_recurring.
  // Donor picks a recurring-shaped cadence (monthly / quarterly /
  // annual). The cycle-0 charge is handled by the existing
  // payment-step flow as a one-time donation — the pledge create
  // does NOT mint a payment token (program plan §5.4).
  //
  // CHG-3027 / PLG-R01a (pledge-management-remediation §PLG-F01) — this
  // step now only captures the CADENCE and advances to the existing
  // donor contact step; the POST /api/v1/public/pledges call moved to
  // the donor step's "Continue" (submitPledge), so the donor's identity
  // channel is captured + validated BEFORE the request is sent. Pre-fix
  // this step was the only forward transition out of pledge and posted
  // {name:null,email:null,phone:null} — a guaranteed donor-visible 422
  // because the backend requires email or phone.
  //
  // DRIFT-601 tracks polish items: ACI intent-card mode (mirror of
  // renderIntentFrequencyInto), explicit pledge_id metadata
  // threading on the payment-intent create, inline cycle-0 receipt
  // copy, idle/cancellation UX. The minimum viable flow is here so
  // donors at non-recurring-capable orgs have a working commitment
  // path; polish iterations on top.
  private renderPledgeInto(el: HTMLElement): void {
    const donorElected = this.isNoPaymentPledge();
    const options: Array<{
      value: 'one-time' | 'monthly' | 'quarterly' | 'annual';
      label: string;
    }> = donorElected
      ? [
          { value: 'one-time', label: this.frequencyLabel('one-time') },
          { value: 'monthly', label: this.frequencyLabel('monthly') },
          { value: 'quarterly', label: this.frequencyLabel('quarterly') },
          { value: 'annual', label: this.frequencyLabel('annual') },
        ]
      : [
          { value: 'monthly', label: this.frequencyLabel('monthly') },
          { value: 'quarterly', label: this.frequencyLabel('quarterly') },
        ];
    const titleText = donorElected
      ? this.vocab('pledge_title_later', 'Set up your pledge')
      : this.vocab('pledge_title', 'Commit to a pledge');
    const subText = donorElected
      ? this.vocab('pledge_subtitle_later', 'Choose your schedule and due date. Nothing will be charged today.')
      : this.vocab('pledge_subtitle', "We'll send you a reminder for each cycle — no card on file.");
    const continueText = this.vocab('continue_button', 'Continue');
    const submitting = this.pledgeSubmitting;
    const today = new Date().toISOString().slice(0, 10);
    const isRecurringPledge = this.selectedFrequency !== 'one-time';
    const scheduleHtml = donorElected ? `
      <div class="dk-pledge-schedule">
        ${isRecurringPledge ? `
          <div class="dk-field">
            <label for="dk-pledge-start">${this.esc(this.vocab('pledge_start_date', 'Start date'))} <span class="dk-optional">(optional)</span></label>
            <input type="date" id="dk-pledge-start" class="dk-pledge-date-input" min="${today}" value="${this.esc(this.pledgeStartDate)}">
          </div>` : ''}
        <div class="dk-field">
          <label for="dk-pledge-due">${this.esc(isRecurringPledge
            ? this.vocab('pledge_end_date', 'End date')
            : this.vocab('pledge_due_date', 'Give by'))} <span aria-hidden="true">*</span></label>
          <input type="date" id="dk-pledge-due" class="dk-pledge-date-input" min="${today}" value="${this.esc(this.pledgeDueDate)}" required aria-required="true">
        </div>
      </div>` : '';
    const errorHtml = this.pledgeError
      ? `<div class="dk-error" role="alert">${this.esc(this.pledgeError)}</div>`
      : '';
    const backButton = `<button class="dk-btn-secondary" id="dk-pledge-back" type="button">${this.esc(this.vocab('back_button', 'Back'))}</button>`;
    const pledgeContinueButton = `<button class="dk-btn-primary" id="dk-pledge-continue" type="button"${submitting ? ' disabled' : ''}>${submitting ? '…' : this.esc(continueText)}</button>`;
    const pledgeActions = this.config.formStyle === 'v3'
      ? this.v3ActionButtons('dk-pledge-back', pledgeContinueButton)
      : backButton + pledgeContinueButton;

    el.innerHTML = `
      <h3 class="dk-step-title">${this.esc(titleText)}</h3>
      <p class="dk-step-sub">${this.esc(subText)}</p>
      <div class="dk-frequency-options${donorElected ? ' dk-pledge-frequency-options' : ''}">
        ${options
          .map(
            (opt) => `
          <button class="dk-frequency-option${this.selectedFrequency === opt.value ? ' dk-active' : ''}" data-frequency="${opt.value}" type="button">
            ${this.esc(opt.label)}
          </button>`,
          )
          .join('')}
      </div>
      ${scheduleHtml}
      ${errorHtml}
      <div class="dk-actions">
        ${pledgeActions}
      </div>`;

    el.querySelectorAll<HTMLButtonElement>('.dk-frequency-option').forEach((btn) => {
      btn.addEventListener('click', () => {
        const f = btn.getAttribute('data-frequency') as
          | 'one-time'
          | 'monthly'
          | 'quarterly'
          | 'annual'
          | null;
        if (f) {
          this.selectedFrequency = f;
          this.pledgeError = null;
          // CHG-3027 / PLG-R01a — a changed cadence is a materially
          // different pledge; clear the per-attempt idempotency key so the
          // next submit mints a fresh one.
          this.pledgeAttemptKey = '';
          // CHG-3053 / DRIFT-1165 — if the pledge was already created and the
          // donor came back to change the cadence, supersede the committed
          // pledge so the donor-step re-runs submitPledge() with the new cadence
          // (the persisted pledge must never diverge from what's charged).
          this.supersedePledgeIfMaterialEdit();
          this.render();
        }
      });
    });

    el.querySelector<HTMLInputElement>('#dk-pledge-start')?.addEventListener('change', (event) => {
      this.pledgeStartDate = (event.target as HTMLInputElement).value;
    });
    el.querySelector<HTMLInputElement>('#dk-pledge-due')?.addEventListener('change', (event) => {
      this.pledgeDueDate = (event.target as HTMLInputElement).value;
    });
    el.querySelector('#dk-pledge-back')?.addEventListener('click', () => this.goTo('amount'));

    // CHG-3027 / PLG-R01a — the pledge step no longer POSTs. It validates
    // that a recurring cadence is chosen, then routes to the donor contact
    // step where identity is captured + validated before submitPledge() runs.
    el.querySelector<HTMLButtonElement>('#dk-pledge-continue')?.addEventListener(
      'click',
      () => {
        if (this.selectedFrequency === 'one-time' && !donorElected) {
          this.pledgeError = 'Pick a recurring cadence to pledge.';
          this.render();
          return;
        }
        if (donorElected && !this.pledgeDueDate) {
          this.pledgeError = isRecurringPledge
            ? this.vocab('pledge_end_date_required', 'Choose when this pledge should end.')
            : this.vocab('pledge_due_date_required', 'Choose the date you plan to give by.');
          this.render();
          return;
        }
        if (donorElected && this.pledgeDueDate < today) {
          this.pledgeError = this.vocab('pledge_due_date_past', 'Choose today or a future date.');
          this.render();
          return;
        }
        if (
          donorElected
          && this.pledgeStartDate
          && this.pledgeDueDate
          && this.pledgeDueDate < this.pledgeStartDate
        ) {
          this.pledgeError = this.vocab('pledge_date_order_invalid', 'The end date must be on or after the start date.');
          this.render();
          return;
        }
        this.pledgeError = null;
        this.goTo('donor');
      },
    );
  }

  // CHG-0932 / ITER-C2 — pledge submit handler. Maps the widget's
  // selectedFrequency vocabulary ('monthly' | 'quarterly' | …) to
  // the server's PledgeFrequency enum (monthly | quarterly | annual)
  // — the widget's 'one-time' value is never sent here (the pledge
  // step only renders when the donor's headed for a recurring
  // commitment).
  //
  // CHG-3027 / PLG-R01a (pledge-management-remediation §PLG-F01) — this
  // now runs from the donor step's "Continue" (renderDonorInto), AFTER
  // the donor's identity channel is captured + validated, so the request
  // always carries a real email / phone / anonymous selection rather than
  // {name:null,email:null,phone:null}. Changes vs the pre-fix handler:
  //   * Returns boolean (true = created) so the caller owns navigation —
  //     on success the donor handler advances to 'payment'; on failure
  //     the donor stays put with their entered contact intact.
  //   * Normalizes the donor contact through the canonical
  //     donorContactPayload() helper (best-effort E.164 + lowercase
  //     email), the same path the recurring / PayPal / session donation
  //     flows use; donorInfo keeps the display value, the wire carries the
  //     normalized lookup value.
  //   * Sends a STABLE per-attempt Idempotency-Key (pledgeAttemptKey),
  //     minted lazily here and reused across retries of the SAME attempt.
  private async submitPledge(): Promise<boolean> {
    if (this.pledgeSubmitting) return false;
    const desig = this.selectedDesignation;
    const campaign = this.selectedCampaign;
    if (!desig && !campaign) {
      this.pledgeError = 'Please choose what you would like to support first.';
      this.render();
      return false;
    }
    if (this.selectedFrequency === 'one-time' && !this.isNoPaymentPledge()) {
      this.pledgeError = 'Pick a recurring cadence to pledge.';
      this.render();
      return false;
    }
    if (this.isNoPaymentPledge() && !this.pledgeDueDate) {
      this.pledgeError = this.selectedFrequency === 'one-time'
        ? this.vocab('pledge_due_date_required', 'Choose the date you plan to give by.')
        : this.vocab('pledge_end_date_required', 'Choose when this pledge should end.');
      this.goTo('pledge');
      return false;
    }
    if (
      this.isNoPaymentPledge()
      && this.pledgeDueDate < new Date().toISOString().slice(0, 10)
    ) {
      this.pledgeError = this.vocab('pledge_due_date_past', 'Choose today or a future date.');
      this.goTo('pledge');
      return false;
    }
    // CHG-3040 / PLG-R08 — zero-amount pledges are invalid (program
    // decision #1). The amount step already blocks `< min` (min floors at
    // 1), but guard here too so a missing/zeroed amount cannot POST a
    // request the backend now 422s (PledgeCreatePublicRequest ge=1).
    if (!this.selectedAmount || this.selectedAmount < 1) {
      this.pledgeError = 'Please enter a pledge amount.';
      this.render();
      return false;
    }
    // Mint the per-attempt idempotency key lazily — reused across retries of
    // the SAME attempt; a material payload change (amount / cadence) cleared
    // it, so a genuinely different pledge gets a fresh key.
    this.pledgeAttemptKey ||= generateIdempotencyKey();
    this.pledgeSubmitting = true;
    this.pledgeError = null;
    this.render();
    // Canonical donor wire payload — phone normalized to E.164 best-effort,
    // email lowercased, empty fields omitted (anonymous donors send no
    // contact). Matches the donation-path policy (donor.ts).
    const contact = this.donorInfo.anonymous
      ? {}
      : donorContactPayload({
          name: this.donorInfo.name,
          email: this.donorInfo.email,
          phone: this.donorInfo.phone,
        });
    try {
      const { createPublicPledge } = await import('./api');
      const response = await createPublicPledge(
        {
          widget_id: this.config.widgetId,
          organization_id: this.config.organizationId,
          designation_id: desig?.id ?? null,
          campaign_id: this.effectiveCampaignId() ?? null,
          // CHG-3056 — `selectedAmount` is in MAJOR currency units (presets are
          // `toMajorUnits(source)`; the custom input is parseFloat(dollars)), so
          // scale to minor units the SAME way the cycle-0 charge does
          // (renderPaymentOriginal: toMinorUnits(getTotalAmount())). The backend
          // stores this field verbatim as minor units, so sending the raw major
          // value under-recorded the pledge commitment while the donor was still
          // charged the correct amount. CHG-3220: per-currency exponent.
          total_amount_minor_units: this.toMinorUnits(this.selectedAmount),
          currency: this.config.currency || 'USD',
          frequency: this.toPledgeWireFrequency(this.selectedFrequency),
          intent_key: this.selectedIntentKey,
          session_id: this.effectiveSessionId() ?? null,
          start_date: this.isNoPaymentPledge() && this.pledgeStartDate
            ? this.pledgeStartDate
            : undefined,
          due_date: this.isNoPaymentPledge() && this.pledgeDueDate
            ? this.pledgeDueDate
            : undefined,
          donate_page_context: this.buildDonatePageContext(desig?.id),
          donor: {
            name: contact.donor_name ?? null,
            email: contact.donor_email ?? null,
            phone: contact.donor_phone ?? null,
            phone_country: contact.donor_phone_country ?? null,
            anonymous: !!this.donorInfo.anonymous,
          },
        },
        this.pledgeAttemptKey,
      );
      this.pledgeId = response.pledge_id;
      this.pledgeDonorId = response.donor_id;
      this.pledgeNextReminderAt = response.next_reminder_at ?? '';
      // CHG-3053 / DRIFT-1165 — snapshot the material values this pledge was
      // persisted with so a later post-create amount/cadence edit can detect a
      // divergence and supersede the pledge (supersedePledgeIfMaterialEdit).
      this.pledgedAmountMinorUnits = this.selectedAmount;
      this.pledgedFrequency = this.selectedFrequency;
      this.pledgeSubmitting = false;
      // Created — clear the per-attempt key so a subsequent distinct pledge
      // mints a fresh one and never dedupes against this commitment.
      this.pledgeAttemptKey = '';
      return true;
    } catch (err: any) {
      this.pledgeSubmitting = false;
      this.pledgeError =
        err?.message || 'Could not record your pledge. Please try again.';
      this.render();
      return false;
    }
  }

  private renderDonorInto(el: HTMLElement): void {
    // CHG-3027 / PLG-R01a — when the donor is on a pledge path (cascade
    // allows pledge, disallows recurring) and the pledge has not been
    // created yet, this step's "Continue" submits the pledge. The donor's
    // identity channel (anonymous / email / phone) is captured here and
    // validated before the POST, fixing the §PLG-F01 guaranteed-422.
    // (review fix #1) isPledgeFlow() is the single predicate goTo() + the step
    // filter share, so this step always renders for a pledge regardless of the
    // showDonorInfo toggle.
    const pledgeFlow = this.isPledgeFlow();

    // Kick off the capture-prompt fetch; re-render when it lands so the
    // labels/rationale pick up the decision. The existing pre-flight contact
    // guard remains authoritative while the request is in flight or fails.
    if (!this.capturePrompt) {
      void this.fetchCapturePromptIfNeeded().then(() => {
        // Only re-render if we're still on the donor step and the
        // fetch actually produced a decision.
        if (this.step === 'donor' && this.capturePrompt) {
          this.render();
        }
      });
    }

    const cp = this.capturePrompt;
    const emailRequired = !!cp && cp.required.includes('email');
    const phoneRequired = !!cp && cp.required.includes('phone');
    const contactOneOfRequired = !!cp
      && cp.required_one_of?.includes('email')
      && cp.required_one_of?.includes('phone');
    const phoneIsFirst =
      !!cp && cp.fields.indexOf('phone') >= 0 && cp.fields.indexOf('phone') < cp.fields.indexOf('email');
    const rationaleHtml = cp && cp.rationale && this.donorIntent !== 'later'
      ? `<p${contactOneOfRequired ? ' id="dk-contact-requirement"' : ''} class="dk-capture-rationale${contactOneOfRequired ? ' dk-contact-requirement' : ''}" data-testid="capture-rationale">${this.esc(cp.rationale)}</p>`
      : '';

    // a11y: use the .dk-optional token class (var(--dk-text-muted)) instead of a
    // hardcoded #94A3B8 — the latter fails WCAG-AA contrast (2.56:1) on white.
    const optionalLabel = '<span class="dk-optional">(optional)</span>';
    const emailLabelSuffix = emailRequired
      ? '<span style="color:#EF4444;font-weight:600">*</span>'
      : (contactOneOfRequired ? '' : optionalLabel);
    const phoneLabelSuffix = phoneRequired
      ? '<span style="color:#EF4444;font-weight:600">*</span>'
      : (contactOneOfRequired ? '' : optionalLabel);
    const contactDescribedBy = contactOneOfRequired
      ? 'aria-describedby="dk-contact-requirement dk-donor-error"'
      : '';

    // CHG-3188 (Donate-Now S5) — lock the email to the authenticated account's
    // address so the resulting public donation deterministically links to the
    // account (server-side _try_link_account matches donor_email→Account email).
    const emailLocked = !!this.config.lockDonorEmail && !!this.donorInfo.email;
    const anonymousAllowed = this.anonymousAllowed() && !emailLocked;
    const emailLockedAttrs = emailLocked ? 'readonly aria-readonly="true"' : '';
    const emailLockedNote = emailLocked
      ? '<p class="dk-field-hint" style="font-size:12px;color:#64748B;margin:4px 0 0 0;">Using your account email so this gift links to your account.</p>'
      : '';
    const emailFieldHtml = `
      <div class="dk-field" data-testid="dk-email-field">
        <label for="dk-email">Email ${emailLabelSuffix}</label>
        <input id="dk-email" type="email" ${emailRequired ? 'required' : ''} ${contactDescribedBy || 'aria-describedby="dk-donor-error"'} ${emailLockedAttrs} placeholder="you@example.com" value="${this.esc(this.donorInfo.email)}" />
        ${emailLockedNote}
      </div>`;
    const phoneFieldHtml = `
      <div class="dk-field" data-testid="dk-phone-field">
        <label for="dk-phone">Phone ${phoneLabelSuffix}</label>
        ${phoneInputMarkup({
          value: this.donorInfo.phone,
          country: this.phoneCountry,
          locale: this.config.locale,
          required: phoneRequired,
          describedBy: contactDescribedBy,
        })}
      </div>`;
    // donate-page v3: Mobile + Email side-by-side (mock .row2). Only when the
    // hosted page opts in via donorLayout:'grid'; every other placement keeps
    // the historical stacked order. Email-locked donors keep stacked so the
    // lock note / readonly affordance has room.
    const gridLayout = this.config.donorLayout === 'grid' && !emailLocked;
    const orderedContactHtml = phoneIsFirst
      ? phoneFieldHtml + emailFieldHtml
      : emailFieldHtml + phoneFieldHtml;
    const contactFieldsHtml = gridLayout
      ? `<div class="dk-row2">${orderedContactHtml}</div>`
      : orderedContactHtml;

    const dedicationHtml = this.config.showDedication ? `
      <div class="dk-field dk-field-check">
        <input id="dk-dedicate" type="checkbox" ${this.dedicationEnabled ? 'checked' : ''} />
        <label for="dk-dedicate">Dedicate this donation</label>
      </div>
      <div id="dk-dedication-fields" class="dk-dedication-fields${this.dedicationEnabled ? '' : ' dk-hidden'}">
        <div class="dk-field">
          <label for="dk-honoree">In honor of</label>
          <input id="dk-honoree" type="text" placeholder="Honoree name" value="${this.esc(this.dedication.honoree)}" />
        </div>
        <div class="dk-field">
          <label for="dk-notify-email">Notification email <span class="dk-optional">(optional)</span></label>
          <input id="dk-notify-email" type="email" placeholder="Send a notification to…" value="${this.esc(this.dedication.notifyEmail)}" />
        </div>
      </div>` : '';

    const commentHtml = this.config.showCommentField ? `
      <div class="dk-field dk-comment-field">
        <label for="dk-comment">Add a message <span class="dk-optional">(optional)</span></label>
        <textarea id="dk-comment" class="dk-textarea" maxlength="500" rows="3" placeholder="Leave a message with your donation…">${this.esc(this.donorComment)}</textarea>
        <span class="dk-char-count" id="dk-char-count">${this.donorComment.length}/500</span>
      </div>` : '';

    // CHG-0703 (ITER-2645) bug #94 — the section header used to
    // hardcode "(optional)" even when the org required email or phone,
    // contradicting the red asterisk on the Email field below and
    // misleading donors into thinking they could skip the form. Only
    // surface the optional tag when NEITHER email nor phone is required;
    // otherwise the header reads plain "Your information" and the per-
    // field `*` markers carry the requirement signal.
    // CHG-3027 / PLG-R01a — on a pledge path the donor MUST supply an
    // identity channel (or explicitly go anonymous), so the header never
    // reads "(optional)" here; the inline validation enforces the choice.
    const sectionHeaderSuffix =
      pledgeFlow || emailRequired || phoneRequired || contactOneOfRequired
        ? ''
        : ' <span class="dk-optional">(optional)</span>';
    // CHG-3027 / PLG-R01a — pledge-path affordances: a short hint explaining
    // why we ask for contact (reminders), a single inline error region for
    // the identity requirement + any server-side pledge create error, and a
    // submitting state on the primary button. The "Skip" button is suppressed
    // on a pledge path — the anonymous checkbox is the deliberate "no contact"
    // choice, so a pledge always carries an explicit identity decision.
    const pledgeHintDefault = anonymousAllowed
      ? 'Tell us where to send your pledge reminders, or continue anonymously.'
      : 'Tell us where to send your pledge reminders.';
    const pledgeHintHtml = pledgeFlow
      ? `<p class="dk-step-sub">${this.esc(this.vocab('pledge_contact_hint', pledgeHintDefault))}</p>`
      : '';
    // CHG-3229 — the donor-error element now renders for EVERY flow, not just
    // the pledge path. The non-pledge "Continue"/"Skip" handlers need it to
    // surface the email-or-phone-or-anonymous requirement (which the backend
    // enforces for BOTH gateway PublicDonationCreate and offline
    // ManualDonationSubmit) before the donor reaches the payment step — instead
    // of letting the submit round-trip return a 422. Starts hidden for
    // non-pledge; pre-seeds the live pledge error when present.
    const donorErrorHtml =
      `<div id="dk-donor-error" class="dk-error${this.pledgeError ? '' : ' dk-hidden'}" role="alert">${this.pledgeError ? this.esc(this.pledgeError) : ''}</div>`;
    // Skip is suppressed on a pledge path, on the v3 grid layout, and on surfaces
    // where anonymous giving is disabled.
    const skipBtnHtml = (pledgeFlow || this.config.donorLayout === 'grid' || !anonymousAllowed)
      ? ''
      : '<button class="dk-btn-secondary" id="dk-donor-skip">Skip</button>';
    const nextDisabled = pledgeFlow && this.pledgeSubmitting ? ' disabled' : '';
    const nextLabel = pledgeFlow && this.pledgeSubmitting ? '…' : 'Continue';
    const donorNextButton = `<button class="dk-btn-primary" id="dk-donor-next"${nextDisabled}>${nextLabel}</button>`;
    // donate-page v3: "Your details" serif title (mock parity). The grid layout
    // is the donate-page opt-in, so the v3 copy rides with it; the stacked
    // default keeps the historical "Your information" heading. (No eyebrow: the
    // mock's "Step N of M" eyebrow is deferred with the step-fold change so we
    // never print a step number that contradicts the current 5-step flow.)
    const donorTitle = gridLayout
      ? this.esc(this.vocab('donor_step_title', 'Your details'))
      : `Your information${sectionHeaderSuffix}`;
    el.innerHTML = `
      <h3 class="dk-step-title">${donorTitle}</h3>
      ${pledgeHintHtml}
      ${rationaleHtml}
      <div class="dk-field">
        <label for="dk-name">Name</label>
        <input id="dk-name" type="text" placeholder="Your name" value="${this.esc(this.donorInfo.name)}" />
      </div>
      ${contactFieldsHtml}
      ${anonymousAllowed ? (gridLayout
        ? `<label class="dk-switch-row">
        <span class="dk-switch-lab">${this.esc(this.vocab('anonymous_label', 'Donate anonymously'))}<span class="dk-switch-sub">${this.esc(this.vocab('anonymous_sub', "Your name won't appear in the public supporters list."))}</span></span>
        <span class="dk-switch"><input id="dk-anon" type="checkbox" ${this.donorInfo.anonymous ? 'checked' : ''} /><span class="dk-switch-track"></span></span>
      </label>`
        : `<div class="dk-field dk-field-check">
        <input id="dk-anon" type="checkbox" ${this.donorInfo.anonymous ? 'checked' : ''} />
        <label for="dk-anon">Donate anonymously</label>
      </div>`) : ''}
      ${dedicationHtml}
      ${commentHtml}
      ${donorErrorHtml}
      <div class="dk-actions">
        ${gridLayout
          ? (this.config.formStyle === 'v3'
            ? this.v3ActionButtons('dk-donor-back', donorNextButton)
            : this.v3BackButton('dk-donor-back') + donorNextButton)
          : skipBtnHtml + donorNextButton}
      </div>`;

    // donate-page v3: a paired Back on the donor step (mock parity). Goes to the
    // previous VISIBLE step — pledge in a pledge flow (recurring disabled but
    // pledge allowed), else frequency when recurring is allowed, else amount.
    el.querySelector('#dk-donor-back')?.addEventListener('click', () => {
      const prev = this.isPledgeFlow()
        ? 'pledge'
        : (this.effectiveAllowRecurring() ? 'frequency' : 'amount');
      this.goTo(prev);
    });

    // Dedication toggle
    const dedicateCheck = el.querySelector<HTMLInputElement>('#dk-dedicate');
    const dedicationFields = el.querySelector<HTMLElement>('#dk-dedication-fields');
    if (dedicateCheck && dedicationFields) {
      dedicateCheck.addEventListener('change', () => {
        this.dedicationEnabled = dedicateCheck.checked;
        dedicationFields.classList.toggle('dk-hidden', !dedicateCheck.checked);
        this.notifyResize();
      });
    }

    // Comment character count
    const commentArea = el.querySelector<HTMLTextAreaElement>('#dk-comment');
    const charCount = el.querySelector<HTMLElement>('#dk-char-count');
    if (commentArea && charCount) {
      commentArea.addEventListener('input', () => {
        charCount.textContent = `${commentArea.value.length}/500`;
      });
    }

    // CHG-3027 / PLG-R01a (review fix #2) — a donor-contact change (email /
    // phone / anonymous toggle) is a MATERIAL pledge-payload change, exactly
    // like an amount or cadence change. Clear the stable per-attempt
    // Idempotency-Key so a failed submit → fix-contact → retry mints a FRESH key
    // (the key lazily re-mints in submitPledge()). Without this, once R01c
    // honors Idempotency-Key server-side, the retry would dedupe against — and
    // replay — the original wrong-contact attempt. Mirrors the amount-step
    // (line ~1533) and cadence-step (renderPledgeInto) key resets.
    const clearPledgeKeyOnContactChange = (): void => {
      this.pledgeAttemptKey = '';
      const donorError = el.querySelector<HTMLElement>('#dk-donor-error');
      if (donorError) {
        donorError.textContent = '';
        donorError.classList.add('dk-hidden');
      }
    };
    el.querySelector<HTMLInputElement>('#dk-email')?.addEventListener('input', clearPledgeKeyOnContactChange);
    el.querySelector<HTMLInputElement>('#dk-phone')?.addEventListener('input', (event) => {
      clearPledgeKeyOnContactChange();
      (event.currentTarget as HTMLInputElement).removeAttribute('aria-invalid');
      const phoneError = el.querySelector<HTMLElement>('#dk-phone-error');
      if (phoneError) {
        phoneError.textContent = '';
        phoneError.classList.add('dk-hidden');
      }
    });
    el.querySelector<HTMLSelectElement>('#dk-phone-country')?.addEventListener('change', (event) => {
      const countrySelect = event.currentTarget as HTMLSelectElement;
      this.phoneCountry = resolvePhoneCountry(
        countrySelect.value,
        this.phoneCountry,
      );
      const selectedOption = countrySelect.selectedOptions[0];
      const callingCode = selectedOption?.dataset.callingCode || '';
      const collapsedCode = el.querySelector<HTMLElement>('#dk-phone-country-code');
      if (collapsedCode) collapsedCode.textContent = callingCode;
      countrySelect.setAttribute(
        'aria-label',
        `Phone country, ${selectedOption?.textContent?.trim() || this.phoneCountry}`,
      );
      clearPledgeKeyOnContactChange();
      const phoneError = el.querySelector<HTMLElement>('#dk-phone-error');
      if (phoneError) {
        phoneError.textContent = '';
        phoneError.classList.add('dk-hidden');
      }
      el.querySelector<HTMLInputElement>('#dk-phone')?.removeAttribute('aria-invalid');
    });
    el.querySelector<HTMLInputElement>('#dk-anon')?.addEventListener('change', clearPledgeKeyOnContactChange);

    const saveDonor = () => {
      this.donorInfo = {
        name: el.querySelector<HTMLInputElement>('#dk-name')?.value ?? '',
        // CHG-3188 — when locked, ignore the DOM value entirely and re-assert
        // the config email so a tampered readonly input can't break the link.
        email: emailLocked
          ? (this.config.defaultDonorEmail || '').trim()
          : (el.querySelector<HTMLInputElement>('#dk-email')?.value ?? ''),
        phone: (el.querySelector<HTMLInputElement>('#dk-phone')?.value ?? '').trim(),
        // The anonymous toggle is suppressed when the email is locked, so a
        // locked donation is never anonymous (which would null donor_email).
        anonymous: emailLocked ? false : (el.querySelector<HTMLInputElement>('#dk-anon')?.checked ?? false),
      };
      // Save dedication if enabled
      if (this.config.showDedication && this.dedicationEnabled) {
        this.dedication = {
          honoree: el.querySelector<HTMLInputElement>('#dk-honoree')?.value ?? '',
          notifyEmail: el.querySelector<HTMLInputElement>('#dk-notify-email')?.value ?? '',
        };
      } else {
        this.dedication = { honoree: '', notifyEmail: '' };
        this.dedicationEnabled = false;
      }
      // Save comment
      if (this.config.showCommentField) {
        this.donorComment = el.querySelector<HTMLTextAreaElement>('#dk-comment')?.value ?? '';
      }
    };
    el.querySelector('#dk-donor-skip')?.addEventListener('click', () => {
      // CHG-3188 — when the email is locked, "Skip" must NOT discard the
      // account email (that would orphan the Donor and break the account link).
      // Preserve the locked identity; only clear the optional name/phone.
      // CHG-3229 — when NOT locked, "Skip" means "I won't share my details", so
      // it MUST mark the donation anonymous. Previously it left anonymous=false
      // with empty email/phone, which the backend rejects with a 422
      // (anonymous-or-contact rule) at the gateway/offline submit — a dead end.
      // Anonymous is the only valid no-contact outcome, and it matches the
      // donor's intent in clicking Skip.
      this.donorInfo = emailLocked
        ? { name: '', email: (this.config.defaultDonorEmail || '').trim(), phone: '', anonymous: false }
        : { name: '', email: '', phone: '', anonymous: true };
      this.dedication = { honoree: '', notifyEmail: '' };
      this.dedicationEnabled = false;
      this.donorComment = '';
      this.goTo('payment');
    });
    el.querySelector('#dk-donor-next')?.addEventListener('click', () => {
      if (pledgeFlow && this.pledgeSubmitting) return;
      // ITER-906207 / CHG-3475: strict country-aware validation. A national
      // number uses the donor-selected country; a leading `+` overrides it.
      // Store only E.164 so every downstream payment/record path is canonical.
      const phoneInput = el.querySelector<HTMLInputElement>('#dk-phone');
      const phoneCountryInput = el.querySelector<HTMLSelectElement>('#dk-phone-country');
      const phoneError = el.querySelector<HTMLElement>('#dk-phone-error');
      const phoneValue = (phoneInput?.value ?? '').trim();
      const selectedCountry = resolvePhoneCountry(phoneCountryInput?.value, this.phoneCountry);
      const normalizedPhone = phoneValue ? normalizePhoneInput(phoneValue, selectedCountry) : null;
      if (phoneValue && !normalizedPhone) {
        if (phoneError) {
          phoneError.textContent = phoneValidationMessage(selectedCountry, 'en');
          phoneError.classList.remove('dk-hidden');
        }
        phoneInput?.setAttribute('aria-invalid', 'true');
        phoneInput?.focus();
        return;
      }
      if (normalizedPhone) {
        this.phoneCountry = normalizedPhone.country;
        if (phoneInput) phoneInput.value = normalizedPhone.e164;
      }
      if (phoneError) {
        phoneError.textContent = '';
        phoneError.classList.add('dk-hidden');
      }
      phoneInput?.removeAttribute('aria-invalid');
      saveDonor();
      const donorError = el.querySelector<HTMLElement>('#dk-donor-error');
      const emailInput = el.querySelector<HTMLInputElement>('#dk-email');
      emailInput?.removeAttribute('aria-invalid');
      if (!this.donorInfo.anonymous && !isValidDonorEmailSyntax(this.donorInfo.email)) {
        if (donorError) {
          donorError.textContent = this.vocab('donor_email_invalid', 'Enter a valid email address to continue.');
          donorError.classList.remove('dk-hidden');
        }
        emailInput?.setAttribute('aria-invalid', 'true');
        emailInput?.focus();
        return;
      }
      if (!this.donorInfo.anonymous && emailRequired && !this.donorInfo.email.trim()) {
        if (donorError) {
          donorError.textContent = this.vocab(
            'donor_email_required',
            'Enter your email address to continue.',
          );
          donorError.classList.remove('dk-hidden');
        }
        el.querySelector<HTMLInputElement>('#dk-email')?.focus();
        return;
      }
      if (!this.donorInfo.anonymous && phoneRequired && !this.donorInfo.phone.trim()) {
        if (donorError) {
          donorError.textContent = this.vocab(
            'donor_phone_required',
            'Enter your phone number to continue.',
          );
          donorError.classList.remove('dk-hidden');
        }
        el.querySelector<HTMLInputElement>('#dk-phone')?.focus();
        return;
      }
      // CHG-3229 — enforce the backend's UNIVERSAL anonymous-or-contact rule for
      // EVERY flow before advancing, not just the pledge path. Both gateway
      // PublicDonationCreate and offline ManualDonationSubmit reject a
      // non-anonymous donation that carries no email/phone with a 422
      // ("Non-anonymous donations require donor_email or donor_phone"). Catching
      // it here — with an actionable message at the field that fixes it — avoids
      // the dead-end 422 a donor otherwise hit at the payment/offline submit
      // (bug: POST /api/v1/donations/manual 422). The donor may go anonymous OR
      // supply at least one of email / phone (email-only and phone-only are each
      // valid — we never demand both channels).
      const hasChannel = !!this.donorInfo.email.trim() || !!this.donorInfo.phone.trim();
      if (!this.donorInfo.anonymous && !hasChannel) {
        if (donorError) {
          // Translatable, receipt-framed copy (donate-page v3 adopted the
          // `donor_contact_required` vocab key; the English default keeps the
          // historical wording for non-v3 orgs that don't override it).
          donorError.textContent = this.donorContactRequiredMessage();
          donorError.classList.remove('dk-hidden');
        }
        (el.querySelector<HTMLInputElement>('#dk-email') || el.querySelector<HTMLInputElement>('#dk-phone'))?.focus();
        return;
      }
      if (donorError) {
        donorError.textContent = '';
        donorError.classList.add('dk-hidden');
      }
      // CHG-3027 / PLG-R01a — on a pledge path, submit the pledge BEFORE
      // advancing to payment (the identity decision is now validated above).
      if (pledgeFlow) {
        const terminal = this.isNoPaymentPledge();
        void this.submitPledge().then((created) => {
          if (created) this.goTo(terminal ? 'confirm' : 'payment');
        });
        return;
      }
      this.goTo('payment');
    });
    this.notifyResize();
  }

  private async renderPaymentInto(el: HTMLElement): Promise<void> {
    // Delegate to the existing renderPayment but target el instead of this.container
    const origContainer = this.container;
    this.container = el;
    await this.renderPaymentOriginal();
    this.container = origContainer;
    // donate-page v3: payment/offline panels re-render async (Stripe element,
    // method toggle, offline form), so a JS content-wrapper is fragile. The
    // pinning is done purely in CSS instead (.dk-form-v3 .dk-step-payment /
    // .dk-step-offline → flex column, last child pinned via margin-top:auto,
    // body scrolls) which survives every re-render. See checkout.css.
    this.notifyResize();
  }

  // ── Full-page step renderers (loading + confirm use full container) ──

  private renderLoading(): void {
    this.container.innerHTML = `
      <div class="dk-loading">
        <div class="dk-spinner"></div>
        <p>Loading…</p>
      </div>`;
  }

  private async renderPaymentOriginal(): Promise<void> {
    const totalAmount = this.getTotalAmount();
    const amountMinorUnits = this.toMinorUnits(totalAmount);
    const designationId = this.selectedDesignation?.id ?? '';
    const isRecurring = this.selectedFrequency !== 'one-time';
    const frequencyLabel = isRecurring ? ` / ${this.selectedFrequency}` : '';

    // CHG-1657 (W3.2) — client mirror of the server-side render-
    // constraints gate (ref:tier-3-calculation/render-constraints-
    // resolution; enforced server-side at
    // src/api/app/routers/public_donations.py per CHG-1459).
    //
    // We mirror to avoid wasting a round-trip on a request the server
    // will reject. The server remains authoritative; this is UX, not
    // security. See feedback_fe_be_validation_must_match.
    const gateError = validateSubmission({
      envelope: this.envelope,
      designationId: designationId || null,
      campaignId: this.effectiveCampaignId() ?? null,
      amountMinorUnits,
    });
    if (gateError) {
      this.renderSubmissionGateError(gateError);
      return;
    }

    const hasStripe = this.paymentMethods?.stripe ?? !!this.config.stripePublishableKey;
    const hasPayPal = this.paymentMethods?.paypal ?? false;

    const feeNote = this.coverFeesChecked && this.config.coverFeesEnabled
      ? ` (includes ${this.fmtCurrency(this.computeFeeAmount(this.selectedAmount))} processing fee)`
      : '';

    // donate-page v3: the Back button moves into the bottom actions row paired
    // with the pay button (mock parity), so payment matches every other step's
    // [Back] [Continue] layout. Non-v3 keeps the historical top Back link.
    const payV3 = this.config.formStyle === 'v3';
    const payButton = `<button class="${this.config.buttonStyle === 'outlined' ? 'dk-btn-primary dk-btn-outlined' : 'dk-btn-primary'}" id="dk-pay-btn" disabled><span class="dk-spinner dk-spinner-sm"></span> Loading payment form…</button>`;
    this.container.innerHTML = `
      <div class="dk-step dk-step-payment">
        <h3 class="dk-step-title">Payment</h3>
        <p class="dk-summary">
          <strong>${this.fmtCurrency(totalAmount)}${frequencyLabel}</strong>
          ${this.selectedDesignation ? `to <em>${this.esc(this.selectedDesignation.name)}</em>` : ''}
          ${feeNote ? `<br><span style="font-size:0.8em">${feeNote}</span>` : ''}
        </p>
        ${(() => {
          const visibleMethods = this.paymentMethods?.methods?.filter(m => m.type !== 'manual') ?? [];
          // CHG-0956 / ITER-D1 — disable-with-tooltip pattern. The
          // pure helper at ./payment-method-filter computes the
          // (disabled, tooltip, active) tuple per row; this render
          // call wraps the tuples in HTML. Cascade-level ALL-none
          // case is handled upstream (frequency step disappears
          // entirely per ref:tier-5-cross-screen/widget-frequency-step),
          // so by the time we reach this branch at least one method
          // supports recurring.
          //
          // Spec contract: ref:tier-5-cross-screen/widget-payment-step
          // method_picker.filter_rules.disable-non-recurring-when-recurring-chosen
          // CHG-MANUALPAY — always render the method picker (even with a single
          // method) so the donor makes an explicit choice and offline shows as a
          // peer option, rather than silently proceeding to a default gateway.
          if (visibleMethods.length >= 1) {
            const entries = computeMethodPickerEntries(
              visibleMethods,
              this.selectedFrequency,
              this.selectedPaymentMethod ?? null,
            );
            // CHG — payment method picker as an accessible radio group
            // (donor request). Each method is a <label> wrapping a real
            // <input type="radio">, giving the choice native radio
            // semantics (arrow-key nav, screen-reader "N of M selected",
            // single-select) instead of the prior look-alike <button>s.
            // data-method stays on the input so the existing
            // provider-switch wiring (and its tests, which select by
            // [data-method] and .click() the control) keep working.
            return '<div class="dk-payment-methods" role="radiogroup" aria-label="' + this.esc(this.vocab('payment_method_label', 'Payment method')) + '">' + entries.map(({ method: m, disabled, tooltip, active }) => {
              const activeClass = active ? ' dk-active' : '';
              const disabledClass = disabled ? ' dk-disabled' : '';
              const titleAttr = tooltip ? ` title="${this.esc(tooltip)}"` : '';
              const rid = `dk-pm-${this.esc(m.provider_key)}`;
              // "Pay by <platform label>", or '' when it would repeat the title.
              // Derivation lives in ./payment-method-filter so it is
              // unit-testable without a DOM.
              const payBy = payByLine(m, this.vocab('payment_method_pay_by', 'Pay by'));
              return `<label class="dk-payment-method-btn${activeClass}${disabledClass}" for="${rid}"${titleAttr}>` +
                `<input type="radio" class="dk-payment-method-radio" name="dk-payment-method" id="${rid}" value="${this.esc(m.provider_key)}" data-method="${this.esc(m.provider_key)}" data-disabled="${disabled}"${active ? ' checked' : ''}${disabled ? ' disabled aria-disabled="true"' : ''} />` +
                `<span class="dk-payment-method-icon" aria-hidden="true">${this.getProviderIcon(m.provider_key, m.display_name, m.icon)}</span>` +
                `<span class="dk-payment-method-text">` +
                  `<span class="dk-payment-method-name">${this.esc(m.display_name)}</span>` +
                  // Names the instrument when the title is the org's own
                  // wording. Suppressed when it would repeat the title — which
                  // is every org that never renamed the method.
                   (payBy ? `<span class="dk-payment-method-sub">${this.esc(payBy)}</span>` : '') +
                `</span>` +
                `</label>`;
            }).join('') + '</div>';
          }
          return '';
        })()}
        <div id="dk-payment-element"></div>
        <p class="dk-error dk-hidden" id="dk-payment-error"></p>
        ${payV3
          ? `<div class="dk-actions">${this.v3ActionButtons('dk-pay-back', payButton)}</div>`
          : payButton}
      </div>`;

    // CHG-3225 (fix #264) — for non-v3 the payment step renders no "← Back"
    // button; back-nav is via the top stepper's completed pips (consistent with
    // every other step). The Stripe-element teardown the old button performed on
    // the way out now lives in goTo() so leaving payment via a pip still
    // destroys the mounted element.
    // donate-page v3 — the v3 form does carry a bottom "← Back" (#dk-pay-back)
    // in the pinned actions row (mock parity); wire it to return to the donor
    // step. We destroy the mounted Stripe element here too so the back path is
    // robust regardless of goTo() internals.
    this.container.querySelector<HTMLButtonElement>('#dk-pay-back')?.addEventListener('click', () => {
      if (this.paymentElement) {
        this.paymentElement.destroy();
        this.paymentElement = null;
      }
      this.goTo('donor');
    });

    // Payment method selection (dynamic). CHG-0956 / ITER-D1 — ignore
    // disabled rows (donor picked recurring but this adapter doesn't
    // support it); the radio stays in the group for screen readers but
    // never advances. CHG — the picker is now a radio group, so we wire
    // the native `change` event on each radio (a label click / arrow-key
    // selection both fire it). A `.click()` on a disabled radio is a
    // no-op in the browser, and the guard below is belt-and-suspenders.
    this.container.querySelectorAll<HTMLInputElement>('.dk-payment-method-radio').forEach((radio) => {
      radio.addEventListener('change', () => {
        if (radio.disabled || radio.dataset['disabled'] === 'true') return;
        const method = radio.value;
        if (method === this.selectedPaymentMethod) return;
        this.selectedPaymentMethod = method;
        // #246 — reset so the next createPaymentIntent mints a fresh key
        // for this provider instead of replaying the default-provider intent.
        this.donationAttemptKey = '';
        if (this.paymentElement) {
          this.paymentElement.destroy();
          this.paymentElement = null;
        }
        this.render();
      });
    });

    // CHG-0956 / ITER-D1 — auto-correct selectedPaymentMethod when
    // the donor flips frequency AFTER picking a method. Helper is
    // pure (see payment-method-filter.ts) so this site just calls
    // it and snaps the field if needed.
    // Skip auto-correct under v3 when nothing is selected yet — we deliberately
    // keep selection empty so the donor explicitly picks a method (no default).
    if (!(this.config.formStyle === 'v3' && !this.selectedPaymentMethod)) {
      const visibleForCorrect = this.paymentMethods?.methods?.filter(m => m.type !== 'manual') ?? [];
      const corrected = autoCorrectSelectionForRecurring(
        visibleForCorrect,
        this.selectedFrequency,
        this.selectedPaymentMethod ?? null,
      );
      if (corrected !== (this.selectedPaymentMethod ?? null) && corrected !== null) {
        this.selectedPaymentMethod = corrected;
        // CHG-3338 — the auto-correct SNAP switches provider without going
        // through the method-radio handler, so it must reset the attempt key
        // the same way (#246 / CHG-3201): replaying the previous provider's
        // key would hand the donor the OLD provider's intent. The server now
        // guards this too (replay mismatch -> cancel+recreate), but the
        // client should not knowingly emit a stale-provider replay.
        this.donationAttemptKey = '';
        if (this.paymentElement) {
          this.paymentElement.destroy();
          this.paymentElement = null;
        }
      }
    }

    // donate-page v3: no method is pre-selected — the donor must pick one. When
    // nothing is selected yet, stop after rendering the method picker (above) so
    // no gateway auto-initializes / auto-redirects. The pay button stays disabled
    // until a method is chosen (the method-click handler re-renders with it set).
    if (this.config.formStyle === 'v3' && !this.selectedPaymentMethod) {
      const payBtn = this.container.querySelector<HTMLButtonElement>('#dk-pay-btn');
      if (payBtn) {
        payBtn.disabled = true;
        payBtn.textContent = this.vocab('payment_pick_method', 'Choose a payment method above');
      }
      return;
    }

    // CHG-MANUALPAY — offline is a peer payment method: selecting it shows the
    // account-details + transaction-reference panel (no gateway flow).
    const selectedEntry = this.paymentMethods?.methods?.find(
      m => m.provider_key === this.selectedPaymentMethod,
    );
    if (selectedEntry?.type === 'offline' || this.selectedPaymentMethod === 'offline') {
      if (this.paymentElement) { this.paymentElement.destroy(); this.paymentElement = null; }
      this.renderOfflinePanel();
      return;
    }

    // Determine which payment flow to initialize
    if (this.selectedPaymentMethod === 'sandbox') {
      this.initSandboxPayment(amountMinorUnits, designationId);
      return;
    }

    const usePayPal = hasPayPal && (!hasStripe || this.selectedPaymentMethod === 'paypal');

    if (usePayPal) {
      await this.initPayPalPayment(amountMinorUnits, designationId, isRecurring, frequencyLabel);
    } else {
      await this.initStripePayment(amountMinorUnits, designationId, isRecurring, frequencyLabel);
    }
  }

  // #258 — break gateway redirects out of the embedded cross-origin iframe.
  // Not-framed (top-level donate page): window.location.assign unchanged.
  // Same-origin embed: window.top.location.assign directly.
  // Cross-origin embed: postMessage directive to the loader; the loader (embed.ts)
  // listens for { type: 'ag-widget-redirect', url } and calls window.location.assign
  // on the host page so the gateway loads as a full top-level document.
  private _navigateOut(url: string): void {
    let framed = true;
    try { framed = window.self !== window.top; } catch { framed = true; }
    if (!framed) { window.location.assign(url); return; }
    try {
      if (window.top) { window.top.location.assign(url); return; }
    } catch { /* cross-origin parent — fall through to postMessage */ }
    try { window.parent.postMessage({ type: 'ag-widget-redirect', url }, '*'); } catch { /* non-fatal */ }
  }

  // CHG-3340 — _armRedirectConfirm (CHG-MANUALPAY) retired: redirect
  // providers no longer have a pre-created gateway URL to arm at render.
  // armDeferredRedirectPay keeps its explicit-confirm semantics (the donor
  // still reviews amount + method and clicks Confirm & pay) but performs
  // the create AND the navigation inside the click.

  private async initStripePayment(
    amountMinorUnits: number,
    designationId: string,
    isRecurring: boolean,
    frequencyLabel: string
  ): Promise<void> {
    // CHG-3340 (QA #302) — ZERO server requests at render / method switch.
    // The PaymentIntent / RecurringDonation is created ONLY on the donor's
    // explicit Confirm & pay click:
    //   * redirect providers (bKash / Nagad / SSLCommerz …): the pay button
    //     arms immediately; the click creates the intent and navigates to
    //     the returned gateway URL in the same gesture;
    //   * Stripe (inline card): the Payment Element mounts in DEFERRED mode
    //     (mode/amount/currency — no client_secret, no server call); the
    //     click runs elements.submit() → create → confirmPayment.
    // Method switching is therefore purely visual. The previous eager create
    // here produced one orphaned payment_intents row per switch (QA #302)
    // and fed the wrong-gateway replay class (#246 / CHG-3338).
    const selectedEntry = this.paymentMethods?.methods?.find(
      m => m.provider_key === this.selectedPaymentMethod,
    );
    if (selectedEntry?.type === 'redirect') {
      this.armDeferredRedirectPay(amountMinorUnits, designationId, isRecurring, frequencyLabel);
      return;
    }
    await this.initDeferredStripeElements(amountMinorUnits, designationId, isRecurring, frequencyLabel);
  }

  /** CHG-3340 — the recurring create, verbatim payload from the pre-deferral
   *  flow (CHG-0047 intent_key / DRIFT-1203 campaign / CHG-1049 donor triplet
   *  / BUG-141 provider_key semantics unchanged); invoked ONLY from the
   *  Confirm & pay click handlers. */
  private createRecurringForCheckout(amountMinorUnits: number, designationId: string) {
    return createRecurringDonation({
          organization_id: this.config.organizationId,
          widget_id: this.config.widgetId,
          designation_id: designationId || undefined,
          // DRIFT-1203 (CHG-3142) — attribute recurring campaign gifts. Same
          // canonical source the one-time createPaymentIntent calls use
          // (selectedCampaign ?? config.campaignId); undefined when not a
          // campaign widget.
          campaign_id: this.effectiveCampaignId() ?? undefined,
          amount_minor_units: amountMinorUnits,
          currency: this.config.currency,
          frequency: this.selectedFrequency,
          intent_key: this.selectedIntentKey || undefined,
          donate_page_context: this.buildDonatePageContext(designationId),
          // CHG-1049 (S6 of donor consolidation program) — donor wire-
          // payload triplet via canonical helper. Pre-S6 this site
          // dropped donor_phone on the wire so phone-only donors
          // couldn't subscribe to recurring giving.
          ...(this.donorInfo.anonymous
            ? {}
            : donorContactPayload({
                name: this.donorInfo.name,
                email: this.donorInfo.email,
                phone: this.donorInfo.phone,
              })),
          // BUG-141 (CHG-1014) — always send the donor's selected
          // provider_key, including "stripe". The historic guard
          // `selectedPaymentMethod !== 'stripe' ? ... : undefined`
          // assumed Stripe was the org's only/default provider; when an
          // org has BOTH Stripe and a redirect-flow provider active
          // (e.g. SSLCommerz), omitting provider_key on Stripe-selected
          // requests fell through to ProviderResolver.get_default_config
          // on the server (public_payments.py / recurring_donations.py
          // — both endpoints had the same fallback) which returned
          // whichever provider the org had marked default — i.e. the
          // donor's Stripe selection was silently overridden.
          provider_key: this.selectedPaymentMethod || undefined,
        });
  }

  /** CHG-3340 — sessionStorage return payload for a recurring redirect hop
   *  (same shape the pre-deferral _armRedirectConfirm call persisted). */
  private storeRecurringReturnPayload(rec: { provider_key?: string; recurring_donation_id?: string }): void {
    try {
      sessionStorage.setItem('ag_recurring_return', JSON.stringify({
        provider: rec.provider_key,
        recurring_donation_id: rec.recurring_donation_id,
        amount: this.selectedAmount,
        org_id: this.config.organizationId,
        widget_id: this.config.widgetId,
      }));
    } catch { /* non-fatal */ }
  }

  /** CHG-3340 — the one-time PaymentIntent create, verbatim payload from the
   *  pre-deferral flow (event/session attribution, page-context lock, donor
   *  identity, return_url and stable-key semantics unchanged); invoked ONLY
   *  from the Confirm & pay click handlers. */
  private createIntentForCheckout(amountMinorUnits: number, designationId: string) {
    this.checkoutAttemptId ||= generateIdempotencyKey();
    return createPaymentIntent({
          amount_minor_units: amountMinorUnits,
          currency: this.config.currency,
          source: this.config.source,
          organization_id: this.config.organizationId,
          widget_id: this.config.widgetId,
          designation_id: designationId || undefined,
          campaign_id: this.effectiveCampaignId() ?? undefined,
          checkout_attempt_id: this.checkoutAttemptId,
          retry_of_payment_intent_id: this.retryOfPaymentIntentId || undefined,
          pledge_contribution_token:
            this.config.pledgeContribution?.token || undefined,
          // CHG-2702 (DP-R2, SESSION) — session attribution for webhook-first
          // card donations from SESSION donate pages. undefined for
          // non-SESSION contexts (effectiveSessionId returns undefined when
          // config.sessionId is unset), so this is a no-op everywhere except
          // giving-session pages. Persisted into PI metadata; the webhook then
          // records Donation.session_id and the session-tally path bumps the
          // GivingSession counters.
          session_id: this.effectiveSessionId(),
          // BUG-141 (CHG-1014) — see the matching comment on the
          // createRecurringDonation call above. Always send the donor's
          // chosen provider_key; omitting it falls through to the
          // server-side default-provider resolver and overrides an
          // explicit Stripe selection when SSLCommerz (or any other
          // provider) is the org's default.
          provider_key: this.selectedPaymentMethod || undefined,
          // CHG-1355 (BUG-151) — pass the clean page URL as return_url.
          // The backend stores it in intent_metadata; the webhook redirect
          // handler reads it back and appends ?payment={status}&intent={id}.
          // CHG-3240 — when the checkout runs inside the embed iframe,
          // window.location.href is the API-hosted /form URL, not the page the
          // donor is on. Resolve the host (embedding) page instead.
          return_url: this.donorReturnUrl,
          // CHG-1782 — page context so PI-create can enforce the
          // designation/campaign lock before it lands in intent_metadata.
          donate_page_context: this.buildDonatePageContext(designationId),
          // CHG-1215 (BUG-148 + BUG-151) — pass donor identity at
          // create-intent time so async reconcilers (Stripe webhook,
          // SSLCommerz/bKash redirect finalization) can resolve donor
          // even when the provider envelope doesn't carry donor info
          // back. The widget ALSO sends donor info on the follow-up
          // recordDonation() call; both paths land at the same
          // canonical Donation row.
          donor_name: this.donorInfo.anonymous ? undefined : this.donorInfo.name || undefined,
          donor_email: this.donorInfo.anonymous ? undefined : this.donorInfo.email || undefined,
          donor_phone: this.donorInfo.anonymous ? undefined : this.donorInfo.phone || undefined,
          donor_phone_country: this.donorInfo.anonymous ? undefined : this.phoneCountry,
          anonymous: this.donorInfo.anonymous,
          // CHG-2813 (DRIFT-1084a) — stable per-attempt key so a retry of THIS
          // create dedupes at Stripe instead of double-charging.
    }, this.donationAttemptKey);
  }

  /** CHG-3340 — sessionStorage return payload for a one-time redirect hop
   *  (same shape the pre-deferral _armRedirectConfirm call persisted). */
  private storePaymentReturnPayload(
    pi: { provider_key?: string; payment_intent_id?: string },
    amountMinorUnits: number,
    designationId: string,
  ): void {
    try {
      sessionStorage.setItem('ag_payment_return', JSON.stringify({
        provider: pi.provider_key,
        payment_intent_id: pi.payment_intent_id,
        amount: this.selectedAmount,
        total_amount_minor_units: amountMinorUnits,
        currency: this.config.currency,
        cover_fees_checked: this.coverFeesChecked,
        org_id: this.config.organizationId,
        widget_id: this.config.widgetId,
        campaign_id: this.effectiveCampaignId() ?? undefined,
        designation_id: designationId || undefined,
        checkout_attempt_id: this.checkoutAttemptId || undefined,
        retry_of_payment_intent_id: this.retryOfPaymentIntentId || undefined,
        // CHG-3442 — preserve the donor's attribution decision across the
        // provider round-trip. Keep PII in tab-scoped sessionStorage only;
        // never place it in the donor-back URL or server-rendered bootstrap.
        donor: this.donorInfo.anonymous
          ? { name: '', email: '', phone: '', anonymous: true }
          : {
              name: this.donorInfo.name,
              email: this.donorInfo.email,
              phone: this.donorInfo.phone,
              anonymous: false,
            },
      }));
    } catch { /* non-fatal */ }
  }

  /** CHG-3340 (QA #302) — redirect providers: NOTHING is created at render.
   *  The pay button arms immediately; the click mints the per-attempt key,
   *  creates the intent/recurring row, persists the return payload, and
   *  navigates to the gateway — all in one gesture (window navigation after
   *  an await inside a click handler is not popup-blocked). */
  private armDeferredRedirectPay(
    amountMinorUnits: number,
    designationId: string,
    isRecurring: boolean,
    frequencyLabel: string,
  ): void {
    const payBtn = this.container.querySelector<HTMLButtonElement>('#dk-pay-btn');
    if (!payBtn) return;
    // Clear the "Loading payment form…" placeholder — there is no inline
    // form for a redirect provider; the donor reviews amount + method and
    // confirms.
    const paymentEl = this.container.querySelector<HTMLElement>('#dk-payment-element');
    if (paymentEl) paymentEl.innerHTML = '';
    const displayAmount = this.getTotalAmount();
    const label = isRecurring
      ? `Subscribe ${this.fmtCurrency(displayAmount)}${frequencyLabel}`
      : `Confirm & pay ${this.fmtCurrency(displayAmount)}`;
    payBtn.disabled = false;
    payBtn.textContent = label;
    payBtn.addEventListener('click', async () => {
      const errEl = this.container.querySelector<HTMLElement>('#dk-payment-error');
      payBtn.disabled = true;
      payBtn.innerHTML = '<span class="dk-spinner dk-spinner-sm"></span> Redirecting…';
      errEl?.classList.add('dk-hidden');
      try {
        // CHG-2813 — stable per-attempt key, now minted at CLICK time (the
        // first server interaction of the attempt). Retries of the SAME
        // attempt reuse it (dedup); the CHG-3338 server replay guard covers
        // any parameter drift under a reused key.
        this.donationAttemptKey ||= generateIdempotencyKey();
        if (isRecurring) {
          const rec = await this.createRecurringForCheckout(amountMinorUnits, designationId);
          if (!rec.redirect_url) {
            throw new Error('No payment method available for recurring donations.');
          }
          this.storeRecurringReturnPayload(rec);
          this._navigateOut(rec.redirect_url);
        } else {
          const pi = await this.createIntentForCheckout(amountMinorUnits, designationId);
          this.paymentIntentId = pi.payment_intent_id;
          if (!pi.redirect_url) {
            throw new Error('No payment method available for this organization.');
          }
          this.storePaymentReturnPayload(pi, amountMinorUnits, designationId);
          this._navigateOut(pi.redirect_url);
        }
      } catch (err) {
        // BUG-137 — surface the backend message verbatim (the create now
        // happens at click time, so the error surfaces here too).
        if (errEl) {
          errEl.textContent = (err instanceof Error && err.message)
            ? err.message
            : 'Unable to start payment. Please try again.';
          errEl.classList.remove('dk-hidden');
        }
        payBtn.disabled = false;
        payBtn.textContent = label;
      }
    });
  }

  /** CHG-3340 (QA #302) — Stripe inline card in DEFERRED-INTENT mode: the
   *  Payment Element mounts from local state only (mode/amount/currency —
   *  no client_secret, no server call). The Confirm & pay click validates
   *  the form (elements.submit()), creates the intent/recurring row, then
   *  confirms against the returned client_secret. */
  private async initDeferredStripeElements(
    amountMinorUnits: number,
    designationId: string,
    isRecurring: boolean,
    frequencyLabel: string,
  ): Promise<void> {
    try {
      // Load Stripe (CDN script — not a request to our API). Pass
      // ``stripeAccount`` when the org uses Stripe Connect so the deferred
      // confirm resolves the PI under the connected account context.
      const stripeOpts = this.config.stripeConnectAccountId
        ? { stripeAccount: this.config.stripeConnectAccountId }
        : undefined;
      this.stripe = await loadStripe(this.config.stripePublishableKey, stripeOpts);
      if (!this.stripe) throw new Error('Stripe failed to load');

      // donate-page v3: theme the Stripe Payment Element to the brand tokens so
      // the card fields match the v3 card instead of Stripe's default styling.
      // Inert (undefined) for non-v3 → Stripe keeps its default appearance.
      const appearance = this.buildV3StripeAppearance();
      // Deferred-intent Elements: server PIs are bare card-only creates (no
      // automatic_payment_methods), so paymentMethodTypes pins ['card'] — an
      // automatic-methods deferred element could render wallets/Link whose
      // confirm the card-only PI would then reject.
      const elements = this.stripe.elements({
        mode: isRecurring ? ('subscription' as const) : ('payment' as const),
        amount: amountMinorUnits,
        currency: (this.config.currency || 'USD').toLowerCase(),
        paymentMethodTypes: ['card'],
        ...(appearance ? { appearance } : {}),
      });
      this.paymentElement = elements.create('payment');
      this.paymentElement.mount('#dk-payment-element');

      const payBtn = this.container.querySelector<HTMLButtonElement>('#dk-pay-btn')!;
      const displayAmount = this.getTotalAmount();
      const donateText = this.config.vocabulary?.donate_button || 'Donate';
      const btnLabel = isRecurring
        ? `Subscribe ${this.fmtCurrency(displayAmount)}${frequencyLabel}`
        : `${donateText} ${this.fmtCurrency(displayAmount)}`;

      this.paymentElement.on('ready', () => {
        payBtn.disabled = false;
        payBtn.innerHTML = btnLabel;
      });

      payBtn.addEventListener('click', async () => {
        const errEl = this.container.querySelector<HTMLElement>('#dk-payment-error')!;
        payBtn.disabled = true;
        payBtn.innerHTML = `<span class="dk-spinner dk-spinner-sm"></span> Processing…`;
        errEl.classList.add('dk-hidden');

        if (!this.stripe) return;

        // 1. Validate the card form BEFORE anything is created server-side
        //    (deferred-intent contract: submit() must precede confirm).
        const { error: submitError } = await elements.submit();
        if (submitError) {
          // Stripe renders the validation error inside the element.
          payBtn.disabled = false;
          payBtn.innerHTML = btnLabel;
          return;
        }

        // 2. Create the intent / recurring row — the FIRST and ONLY server
        //    call of this attempt (CHG-3340 / QA #302).
        let clientSecret: string;
        try {
          this.donationAttemptKey ||= generateIdempotencyKey();
          if (isRecurring) {
            const rec = await this.createRecurringForCheckout(amountMinorUnits, designationId);
            if (rec.redirect_url) {
              // Server-side default resolution can still pick a redirect
              // provider; honor it in the same click gesture.
              this.storeRecurringReturnPayload(rec);
              this._navigateOut(rec.redirect_url);
              return;
            }
            if (!rec.client_secret) {
              throw new Error('No payment method available for recurring donations.');
            }
            clientSecret = rec.client_secret;
          } else {
            const pi = await this.createIntentForCheckout(amountMinorUnits, designationId);
            this.paymentIntentId = pi.payment_intent_id;
            if (pi.redirect_url) {
              this.storePaymentReturnPayload(pi, amountMinorUnits, designationId);
              this._navigateOut(pi.redirect_url);
              return;
            }
            if (!pi.client_secret) {
              throw new Error('No payment method available for this organization.');
            }
            clientSecret = pi.client_secret;
          }
        } catch (err) {
          // BUG-137 — backend message verbatim, now at click time (the
          // accepted CHG-3340 tradeoff: unconfigured-org errors surface on
          // Confirm & pay instead of at render).
          errEl.textContent = (err instanceof Error && err.message)
            ? err.message
            : 'Unable to start payment. Please try again.';
          errEl.classList.remove('dk-hidden');
          payBtn.disabled = false;
          payBtn.innerHTML = btnLabel;
          return;
        }

        // 3. Confirm against the freshly-created intent's secret.
        const { error } = await this.stripe.confirmPayment({
          elements,
          clientSecret,
          redirect: 'if_required',
        });

        if (error) {
          errEl.textContent = error.message ?? 'Payment failed. Please try again.';
          errEl.classList.remove('dk-hidden');
          payBtn.disabled = false;
          payBtn.innerHTML = btnLabel;
          return;
        }

        // Poll payment intent status to confirm 3DS / async payment completion
        if (this.paymentIntentId) {
          let confirmed = false;
          for (let poll = 0; poll < 10; poll++) {
            try {
              const piStatus = await fetchPaymentIntentStatus(
                this.paymentIntentId,
                this.config.organizationId,
              );
              if (piStatus.status === 'succeeded') {
                this.applyConfirmedPaymentAmount(piStatus);
                confirmed = true;
                break;
              }
              if (piStatus.status === 'canceled' || piStatus.status === 'refunded') {
                errEl.textContent = 'Payment was not completed. Please try again.';
                errEl.classList.remove('dk-hidden');
                payBtn.disabled = false;
                payBtn.innerHTML = btnLabel;
                return;
              }
            } catch { /* polling error — retry */ }
            await new Promise(r => setTimeout(r, 1000));
          }
          if (!confirmed) {
            console.warn('[DonationCheckout] payment intent status not confirmed after polling');
            // Proceed anyway — Stripe confirmPayment returned no error
          }
        }

        // Record donation (one-time only — recurring is already recorded server-side)
        if (!isRecurring) {
          try {
            // CHG-1049 (S6) — consistency migration: was already
            // sending the triplet correctly, switched to donorContactPayload
            // so the canonical-input doctrine (Rule 15a/16) is honored
            // uniformly across all widget submit paths.
            const donationResult = await recordDonation({
              organization_id: this.config.organizationId,
              widget_id: this.config.widgetId,
              designation_id: designationId || undefined,
              campaign_id: this.effectiveCampaignId() ?? undefined,
              session_id: this.config.sessionId,
              amount_minor_units: amountMinorUnits,
              currency: this.config.currency,
              source: this.config.source,
              ...(this.donorInfo.anonymous
                ? {}
                : donorContactPayload({
                    name: this.donorInfo.name,
                    email: this.donorInfo.email,
                    phone: this.donorInfo.phone,
                  })),
              anonymous: this.donorInfo.anonymous,
              payment_intent_id: this.paymentIntentId,
              pledge_contribution_token:
                this.config.pledgeContribution?.token || undefined,
              metadata: this.buildDonationMetadata(),
              donate_page_context: this.buildDonatePageContext(designationId),
            });
            // Set returning-donor recognition cookie if token was minted
            if (donationResult && donationResult.recognition_token) {
              try {
                document.cookie = 'ag_donor=' + donationResult.recognition_token
                  + ';path=/donate;max-age=31536000;SameSite=Lax';
              } catch { /* cookie write failure is non-fatal */ }
            }
          } catch {
            // Non-fatal: payment already succeeded
          }
        }

        this.goTo('confirm');
      });
    } catch (err) {
      console.error('[DonationCheckout] stripe payment setup error', err);
      const errEl = this.container.querySelector<HTMLElement>('#dk-payment-error');
      if (errEl) {
        // BUG-137 — apiFetch (api.ts) now throws ApiError whose `message`
        // carries the backend's `detail` field verbatim (e.g. "Amount must
        // convert to at least 50 cents. ৳25.00 converts to approximately
        // $0.20." or "Online payments are not yet configured for this
        // organization. Please contact them directly."). Surface that so
        // donors and org admins can see the actual root cause instead of a
        // generic "Please try again" that's misleading when the root cause
        // is below-minimum amount or missing bKash credentials. Fall back
        // to the prior wording only if the error chain didn't propagate a
        // meaningful message (e.g. a Stripe Elements JS error).
        const msg = (err instanceof Error && err.message)
          ? err.message
          : 'Unable to initialize payment. Please try again.';
        errEl.textContent = msg;
        errEl.classList.remove('dk-hidden');
      }
      // BUG-137 — also stop the "Loading payment form…" spinner. Prior
      // behavior left the spinner mounted in the pay button label even
      // after the error banner rendered, which compounded donor confusion
      // (issue #137 lists this as the "UI stuck on Loading payment form…"
      // symptom). The spinner lives inside the disabled #dk-pay-btn
      // button label (see step-payment template above); clearing the
      // button's inner HTML is what actually stops the visible spin.
      const paymentEl = this.container.querySelector<HTMLElement>('#dk-payment-element');
      if (paymentEl) {
        paymentEl.innerHTML = '';
      }
      const payBtn = this.container.querySelector<HTMLButtonElement>('#dk-pay-btn');
      if (payBtn) {
        payBtn.disabled = true;
        // Replace "Loading payment form…" spinner with a static dash so the
        // button is visibly halted but still occupies the layout slot.
        payBtn.innerHTML = '—';
      }
    }
  }

  private initSandboxPayment(amountMinorUnits: number, designationId: string): void {
    // CHG-2813 (DRIFT-1084a) — mint the STABLE per-attempt idempotency key
    // ONCE for this charge. The pay button below re-runs createPaymentIntent on
    // every click; without a stable key a second click (or a network-blip
    // retry) would mint a SECOND PaymentIntent. Cleared on #dk-restart so a
    // genuinely new gift gets a fresh key.
    this.donationAttemptKey ||= generateIdempotencyKey();
    const paymentEl = this.container.querySelector<HTMLElement>('#dk-payment-element')!;
    const payBtn = this.container.querySelector<HTMLButtonElement>('#dk-pay-btn')!;
    const errEl = this.container.querySelector<HTMLElement>('#dk-payment-error')!;
    const displayAmount = this.getTotalAmount();
    const donateText = this.config.vocabulary?.donate_button || 'Donate';

    paymentEl.innerHTML = `
      <div class="dk-sandbox-form">
        <div class="dk-sandbox-badge">Test Mode</div>
        <div class="dk-card-row">
          <div class="dk-card-field dk-card-field-number">
            <label class="dk-card-label">Card number</label>
            <div class="dk-card-input-wrap">
              <input type="text" id="dk-card-number" class="dk-card-input" placeholder="1234 1234 1234 1234"
                     maxlength="19" inputmode="numeric" autocomplete="cc-number">
              <span class="dk-card-brand" id="dk-card-brand"></span>
            </div>
          </div>
        </div>
        <div class="dk-card-row dk-card-row-split">
          <div class="dk-card-field">
            <label class="dk-card-label">Expiry</label>
            <input type="text" id="dk-card-expiry" class="dk-card-input" placeholder="MM / YY"
                   maxlength="7" inputmode="numeric" autocomplete="cc-exp">
          </div>
          <div class="dk-card-field">
            <label class="dk-card-label">CVC</label>
            <input type="text" id="dk-card-cvc" class="dk-card-input" placeholder="123"
                   maxlength="4" inputmode="numeric" autocomplete="cc-csc">
          </div>
        </div>
        <div class="dk-card-row">
          <div class="dk-card-field">
            <label class="dk-card-label">Cardholder name</label>
            <input type="text" id="dk-card-name" class="dk-card-input" placeholder="Full name on card"
                   autocomplete="cc-name">
          </div>
        </div>
      </div>`;

    const numberInput = paymentEl.querySelector<HTMLInputElement>('#dk-card-number')!;
    const expiryInput = paymentEl.querySelector<HTMLInputElement>('#dk-card-expiry')!;
    const cvcInput = paymentEl.querySelector<HTMLInputElement>('#dk-card-cvc')!;
    const brandEl = paymentEl.querySelector<HTMLElement>('#dk-card-brand')!;

    // Card number formatting + brand detection
    numberInput.addEventListener('input', () => {
      let v = numberInput.value.replace(/\D/g, '');
      if (v.length > 16) v = v.slice(0, 16);
      numberInput.value = v.replace(/(\d{4})(?=\d)/g, '$1 ');
      const first = v.charAt(0);
      const firstTwo = v.slice(0, 2);
      if (first === '4') brandEl.textContent = 'Visa';
      else if (['51','52','53','54','55'].includes(firstTwo) || (parseInt(firstTwo) >= 22 && parseInt(firstTwo) <= 27)) brandEl.textContent = 'MC';
      else if (['34','37'].includes(firstTwo)) { brandEl.textContent = 'Amex'; cvcInput.maxLength = 4; }
      else if (['60','65'].includes(firstTwo) || v.startsWith('6011')) brandEl.textContent = 'Disc';
      else brandEl.textContent = '';
    });

    expiryInput.addEventListener('input', () => {
      let v = expiryInput.value.replace(/\D/g, '');
      if (v.length > 4) v = v.slice(0, 4);
      if (v.length >= 3) v = v.slice(0, 2) + ' / ' + v.slice(2);
      expiryInput.value = v;
    });

    cvcInput.addEventListener('input', () => {
      cvcInput.value = cvcInput.value.replace(/\D/g, '');
    });

    const luhnValid = (num: string): boolean => {
      const digits = num.replace(/\D/g, '');
      if (digits.length < 13) return false;
      let sum = 0;
      let alt = false;
      for (let i = digits.length - 1; i >= 0; i--) {
        let n = parseInt(digits[i], 10);
        if (alt) { n *= 2; if (n > 9) n -= 9; }
        sum += n;
        alt = !alt;
      }
      return sum % 10 === 0;
    };

    payBtn.disabled = false;
    payBtn.innerHTML = `${donateText} ${this.fmtCurrency(displayAmount)}`;

    payBtn.addEventListener('click', async () => {
      errEl.classList.add('dk-hidden');
      const cardNum = numberInput.value.replace(/\s/g, '');
      const expiry = expiryInput.value.replace(/\s/g, '');
      const cvc = cvcInput.value;
      if (!luhnValid(cardNum)) {
        errEl.textContent = 'Please enter a valid card number.';
        errEl.classList.remove('dk-hidden');
        return;
      }
      if (!/^\d{2}\/\d{2}$/.test(expiry)) {
        errEl.textContent = 'Please enter a valid expiry (MM/YY).';
        errEl.classList.remove('dk-hidden');
        return;
      }
      if (cvc.length < 3) {
        errEl.textContent = 'Please enter a valid CVC.';
        errEl.classList.remove('dk-hidden');
        return;
      }

      payBtn.disabled = true;
      payBtn.innerHTML = '<span class="dk-spinner dk-spinner-sm"></span> Processing\u2026';

      try {
        this.checkoutAttemptId ||= generateIdempotencyKey();
        const pi = await createPaymentIntent({
          amount_minor_units: amountMinorUnits,
          currency: this.config.currency,
          source: this.config.source,
          organization_id: this.config.organizationId,
          widget_id: this.config.widgetId,
          designation_id: designationId || undefined,
          campaign_id: this.effectiveCampaignId() ?? undefined,
          checkout_attempt_id: this.checkoutAttemptId,
          retry_of_payment_intent_id: this.retryOfPaymentIntentId || undefined,
          // CHG-2702 (DP-R2, SESSION) — session attribution on the sandbox
          // submit path too, so dev/staging session-page donations record
          // Donation.session_id + update the GivingSession counters
          // identically to production (the PI-metadata path the webhook reads).
          session_id: this.effectiveSessionId(),
          pledge_contribution_token:
            this.config.pledgeContribution?.token || undefined,
          provider_key: 'sandbox',
          donate_page_context: this.buildDonatePageContext(designationId),
          // CHG-1215 — pass donor identity at create-intent time; see
          // the matching comment on the sibling Stripe path above.
          donor_name: this.donorInfo.anonymous ? undefined : this.donorInfo.name || undefined,
          donor_email: this.donorInfo.anonymous ? undefined : this.donorInfo.email || undefined,
          donor_phone: this.donorInfo.anonymous ? undefined : this.donorInfo.phone || undefined,
          donor_phone_country: this.donorInfo.anonymous ? undefined : this.phoneCountry,
          anonymous: this.donorInfo.anonymous,
          // CHG-2813 (DRIFT-1084a) — stable per-attempt key so a re-click /
          // retry of THIS create dedupes instead of minting a second PI.
        }, this.donationAttemptKey);
        this.paymentIntentId = pi.payment_intent_id;

        try {
          // CHG-1049 (S6) — same consistency migration as the
          // sibling Stripe-Elements happy-path above.
          const donationResult = await recordDonation({
            organization_id: this.config.organizationId,
            widget_id: this.config.widgetId,
            designation_id: designationId || undefined,
            campaign_id: this.effectiveCampaignId() ?? undefined,
            session_id: this.config.sessionId,
            amount_minor_units: amountMinorUnits,
            currency: this.config.currency,
            source: this.config.source,
            ...(this.donorInfo.anonymous
              ? {}
              : donorContactPayload({
                  name: this.donorInfo.name,
                  email: this.donorInfo.email,
                  phone: this.donorInfo.phone,
                })),
            anonymous: this.donorInfo.anonymous,
            payment_intent_id: this.paymentIntentId,
            pledge_contribution_token:
              this.config.pledgeContribution?.token || undefined,
            metadata: this.buildDonationMetadata(),
            donate_page_context: this.buildDonatePageContext(designationId),
          });
          if (donationResult?.recognition_token) {
            try {
              document.cookie = 'ag_donor=' + donationResult.recognition_token
                + ';path=/donate;max-age=31536000;SameSite=Lax';
            } catch { /* non-fatal */ }
          }
        } catch { /* payment already succeeded — non-fatal */ }

        this.goTo('confirm');
      } catch (err) {
        // BUG-137 — surface backend `detail` instead of the static
        // "Payment failed. Please try again." Same rationale as the inline
        // Stripe-elements error handler above.
        const msg = (err instanceof Error && err.message)
          ? err.message
          : 'Payment failed. Please try again.';
        errEl.textContent = msg;
        errEl.classList.remove('dk-hidden');
        payBtn.disabled = false;
        payBtn.innerHTML = `${donateText} ${this.fmtCurrency(displayAmount)}`;
      }
    });
  }

  private async initPayPalPayment(
    amountMinorUnits: number,
    designationId: string,
    isRecurring: boolean,
    frequencyLabel: string
  ): Promise<void> {
    const payBtn = this.container.querySelector<HTMLButtonElement>('#dk-pay-btn')!;
    const paymentElementDiv = this.container.querySelector<HTMLElement>('#dk-payment-element')!;

    // Hide the Stripe payment element container — PayPal uses redirect flow
    paymentElementDiv.innerHTML = `
      <div class="dk-paypal-info">
        <p>You will be redirected to PayPal to complete your ${isRecurring ? 'subscription' : 'donation'}.</p>
      </div>`;

    const displayAmount = this.getTotalAmount();
    const donateText = this.config.vocabulary?.donate_button || 'Donate';
    const btnLabel = isRecurring
      ? `Subscribe ${this.fmtCurrency(displayAmount)}${frequencyLabel} with PayPal`
      : `${donateText} ${this.fmtCurrency(displayAmount)} with PayPal`;

    payBtn.disabled = false;
    payBtn.innerHTML = btnLabel;
    payBtn.style.backgroundColor = '#0070BA';

    payBtn.addEventListener('click', async () => {
      const errEl = this.container.querySelector<HTMLElement>('#dk-payment-error')!;
      payBtn.disabled = true;
      payBtn.innerHTML = `<span class="dk-spinner dk-spinner-sm"></span> Redirecting to PayPal…`;
      errEl.classList.add('dk-hidden');

      try {
        if (isRecurring) {
          // CHG-1049 (S6) — REMOVED the 'anonymous@example.com'
          // literal-email fallback. Pre-S6 every PayPal-subscription
          // donor whose email was empty got their record corrupted by
          // a fake email that subsequent identity resolution (and any
          // future cross-org Account claim) couldn't tell from a real
          // one. donorContactPayload omits empty fields instead.
          // Bi-channel invariant (at least one of email/phone) is
          // already enforced by the renderDonorInto pre-flight + the
          // backend find_or_create guard.
          const result = await createPayPalSubscription({
            organization_id: this.config.organizationId,
            widget_id: this.config.widgetId,
            amount_minor_units: amountMinorUnits,
            currency: this.config.currency,
            frequency: this.selectedFrequency,
            designation_id: designationId || undefined,
            campaign_id: this.effectiveCampaignId() ?? undefined,
            anonymous: this.donorInfo.anonymous,
            donate_page_context: this.buildDonatePageContext(designationId),
            ...(this.donorInfo.anonymous
              ? {}
              : donorContactPayload({
                  name: this.donorInfo.name,
                  email: this.donorInfo.email,
                  phone: this.donorInfo.phone,
                })),
          });
          this._navigateOut(result.approve_url);
        } else {
          // CHG-1049 (S6) — donor wire-payload triplet via helper.
          const result = await createPayPalOrder({
            organization_id: this.config.organizationId,
            widget_id: this.config.widgetId,
            amount_minor_units: amountMinorUnits,
            currency: this.config.currency,
            source: this.config.source,
            designation_id: designationId || undefined,
            campaign_id: this.effectiveCampaignId() ?? undefined,
            session_id: this.effectiveSessionId(),
            anonymous: this.donorInfo.anonymous,
            donate_page_context: this.buildDonatePageContext(designationId),
            ...(this.donorInfo.anonymous
              ? {}
              : donorContactPayload({
                  name: this.donorInfo.name,
                  email: this.donorInfo.email,
                  phone: this.donorInfo.phone,
                })),
          });
          // Save checkout state for donation recording after PayPal redirect return.
          // CHG-1049 (S6) — stash the canonical triplet so the
          // post-redirect /donations POST carries donor_phone too.
          try {
            sessionStorage.setItem('ag_paypal_return', JSON.stringify({
              organization_id: this.config.organizationId,
              widget_id: this.config.widgetId,
              designation_id: designationId || undefined,
              campaign_id: this.effectiveCampaignId() ?? undefined,
              session_id: this.config.sessionId || undefined,
              amount_minor_units: amountMinorUnits,
              currency: this.config.currency,
              source: this.config.source,
              // CHG-1782 — carry the page context across the PayPal
              // redirect so the post-return /donations POST can satisfy
              // the backend's required-context gate for public sources.
              donate_page_context: this.buildDonatePageContext(designationId),
              ...(this.donorInfo.anonymous
                ? {}
                : donorContactPayload({
                    name: this.donorInfo.name,
                    email: this.donorInfo.email,
                    phone: this.donorInfo.phone,
                  })),
              anonymous: this.donorInfo.anonymous,
            }));
          } catch { /* sessionStorage write failure is non-fatal */ }
          this._navigateOut(result.approve_url);
        }
      } catch (err) {
        console.error('[DonationCheckout] paypal order error', err);
        errEl.textContent = 'Unable to create PayPal order. Please try again.';
        errEl.classList.remove('dk-hidden');
        payBtn.disabled = false;
        payBtn.innerHTML = btnLabel;
      }
    });
  }

  /** Inline brand mark for one payment method — see ./provider-marks.
   *  `icon` is the server-declared ADAPTER_METADATA filename off
   *  PaymentMethodEntry and is the spec's source of truth for the row mark
   *  (ref:tier-5-experience/widget-payment-step picker_ux.rendering).
   *  Previously an inline map of generic monochrome glyphs that covered 7 of
   *  ~22 registered providers and returned '' for the rest (offline /
   *  sslcommerz / paystation / alpha / sandbox rendered a blank span). */
  private getProviderIcon(key: string, displayName?: string, icon?: string | null): string {
    return providerMark(key, displayName, icon);
  }

  private renderDegradedMode(branding: OrgBranding, hasManual: boolean): void {
    const orgName = branding.name || this.config.orgName || 'this organization';
    // BUG-479 (HIGH/SECURITY): do NOT render the org's contact_email or
    // phone in the public degraded-mode fallback. Previously the org
    // admin's contact_email was emitted as a clickable mailto: link to
    // ANY visitor of ANY embedded widget — bots scrape it and
    // competitors poach. The org name in the message body is enough;
    // visitors who already know the org have their own existing
    // channels to reach them. The backend public branding endpoint has
    // also been updated to not return contact_email/phone (defense in
    // depth — even if a cached bundle still references the fields,
    // they will be null).

    this.container.innerHTML = `
      <div class="dk-degraded-mode">
        <div style="font-size:2rem;margin-bottom:0.75rem">&#128591;</div>
        <h3 style="font-size:1rem;font-weight:600;color:var(--dk-text);margin:0 0 0.5rem">${hasManual ? 'Online payments are being set up' : 'Donations temporarily unavailable'}</h3>
        <p style="font-size:0.9rem;color:var(--dk-text-muted);line-height:1.5;margin:0 0 1rem">
          ${hasManual
            ? `To donate to ${this.esc(orgName)}, please contact the organization directly.`
            : `Online payments are not yet configured for ${this.esc(orgName)}. Please check back soon.`}
        </p>
      </div>`;
    void branding;
    this.notifyResize();
  }

  private async handleProviderReturn(): Promise<void> {
    this.container.innerHTML = `
      <div class="dk-step dk-step-payment">
        <h2 class="dk-title">Completing Payment</h2>
        <div class="dk-loading"><div class="dk-spinner"></div><p>Verifying your payment...</p></div>
        <p class="dk-error dk-hidden" id="dk-payment-error"></p>
      </div>`;

    let attempts = 0;
    const poll = async (): Promise<void> => {
      attempts++;
      try {
        // CHG-1367 (BUG-151): organizationId is required by the status endpoint
        // to prevent cross-tenant enumeration; missing it caused every poll to 400.
        const data = await fetchPaymentIntentStatus(this.paymentIntentId, this.config.organizationId);
        this.applyPaymentReturnAllocationState(data);
        if (data.status === 'succeeded') {
          this.applyConfirmedPaymentAmount(data);
          this.goTo('confirm');
          postCheckoutReturnConsumed();
          return;
        }
        if (data.status === 'canceled' || data.status === 'refunded') {
          this.showPaymentFailed(data.status);
          postCheckoutReturnConsumed();
          return;
        }
      } catch { /* ignore, keep polling */ }

      if (attempts >= 20) {
        const el = this.container.querySelector('.dk-step')!;
        el.innerHTML = `
          <h2 class="dk-title">Payment is being processed</h2>
          <p class="dk-subtitle">
            Please check your payment app to confirm the donation.
            You will receive a receipt once it is confirmed.
          </p>
          <button class="dk-btn-primary" id="dk-check-again">Check Again</button>`;
        el.querySelector('#dk-check-again')?.addEventListener('click', () => window.location.reload());
        return;
      }

      setTimeout(poll, 3000);
    };

    setTimeout(poll, 2000);
  }

  private restorePaymentReturnState(
    intentReturn: string,
    options: { applyConfirmedTotal: boolean },
  ): void {
    let savedReturn: string | null = null;
    try {
      savedReturn = sessionStorage.getItem('ag_payment_return');
    } catch {
      return;
    }
    if (!savedReturn) return;

    try {
      const state = JSON.parse(savedReturn) as PaymentReturnState;
      sessionStorage.removeItem('ag_payment_return');
      if (state.payment_intent_id !== intentReturn) return;

      this.applyPaymentReturnAttemptState(state);

      if (options.applyConfirmedTotal) {
        this.applyConfirmedPaymentAmount({
          amount_minor_units: state.total_amount_minor_units,
          currency: state.currency,
        });
      }
    } catch {
      // Non-fatal. The status endpoint remains authoritative for success.
    }
  }

  private applyConfiguredPaymentReturn(
    intentReturn: string,
    options: { applyConfirmedTotal: boolean },
  ): boolean {
    const state = this.config.paymentReturn;
    if (!state || state.intent !== intentReturn) return false;

    const currency = typeof state.currency === 'string' ? state.currency.trim().toUpperCase() : '';
    if (/^[A-Z]{3}$/.test(currency)) {
      this.config.currency = currency;
    }
    this.applyPaymentReturnAllocationState(state);

    if (options.applyConfirmedTotal) {
      const amountMinor = Number(state.amount_minor_units);
      if (!Number.isFinite(amountMinor) || amountMinor <= 0) return false;
      this.applyConfirmedPaymentAmount({
        amount_minor_units: state.amount_minor_units,
        currency: state.currency,
      });
    }
    return true;
  }

  private applyPaymentReturnAttemptState(state: PaymentReturnState): void {
    if (typeof state.amount === 'number' && Number.isFinite(state.amount) && state.amount > 0) {
      this.selectedAmount = state.amount;
    }
    if (typeof state.cover_fees_checked === 'boolean') {
      this.coverFeesChecked = state.cover_fees_checked;
    }
    const currency = typeof state.currency === 'string' ? state.currency.trim().toUpperCase() : '';
    if (/^[A-Z]{3}$/.test(currency)) {
      this.config.currency = currency;
    }
    this.applyPaymentReturnAllocationState(state);
    this.applyPaymentReturnDonorState(state.donor);
  }

  /** Restore only a target that is still present in the freshly loaded page
   * options. A context lock always wins over browser/server retry metadata. */
  private applyPaymentReturnAllocationState(state: {
    designation_id?: string | null;
    campaign_id?: string | null;
    checkout_attempt_id?: string | null;
    retry_of_payment_intent_id?: string | null;
  }): void {
    if (this.effectiveContextLock) {
      this.seedContextLockedSelection();
    } else {
      const campaignId = typeof state.campaign_id === 'string' ? state.campaign_id : '';
      const designationId = typeof state.designation_id === 'string' ? state.designation_id : '';
      const campaign = campaignId
        ? this.campaignOptions.find(option => option.id === campaignId) ?? null
        : null;
      const designation = designationId
        ? this.designations.find(option => option.id === designationId) ?? null
        : null;
      if (campaign) {
        this.selectedCampaign = campaign;
        this.selectedDesignation = null;
        this.setSelectedCascade(campaign.cascade);
      } else if (designation) {
        this.selectedDesignation = designation;
        this.selectedCampaign = null;
        const option = this.designationOptions.find(item => item.id === designation.id);
        this.setSelectedCascade(option ? option.cascade : null);
      } else if (campaignId || designationId) {
        // Never silently substitute a current/default allocation when the
        // donor's returned target no longer exists in the fresh page options.
        this.selectedDesignation = null;
        this.selectedCampaign = null;
        this.setSelectedCascade(null);
      }
    }

    const attemptId = typeof state.checkout_attempt_id === 'string'
      ? state.checkout_attempt_id.trim()
      : '';
    if (attemptId) this.checkoutAttemptId = attemptId;
  }

  private hasValidDonationAllocation(): boolean {
    if (this.effectiveContextLock) this.seedContextLockedSelection();
    return !!(this.selectedDesignation?.id || this.effectiveCampaignId());
  }

  private applyPaymentReturnDonorState(donor: PaymentReturnState['donor']): void {
    if (!donor) return;

    // An authenticated/locked checkout must keep the server-seeded account
    // identity even if browser storage is stale or has been tampered with.
    const lockedEmail = this.config.lockDonorEmail
      ? (this.config.defaultDonorEmail || '').trim()
      : '';
    if (lockedEmail) {
      this.donorInfo = {
        name: (this.config.defaultDonorName || '').trim(),
        email: lockedEmail,
        phone: (this.config.defaultDonorPhone || '').trim(),
        anonymous: false,
      };
      return;
    }

    if (
      typeof donor.name !== 'string'
      || typeof donor.email !== 'string'
      || typeof donor.phone !== 'string'
      || typeof donor.anonymous !== 'boolean'
    ) {
      return;
    }

    if (donor.anonymous) {
      // Do not retain contact fields that are irrelevant to an explicitly
      // anonymous attempt, and never restore anonymous intent when the current
      // page policy forbids it.
      if (!this.anonymousAllowed()) return;
      this.donorInfo = { name: '', email: '', phone: '', anonymous: true };
      return;
    }

    const restored = {
      name: donor.name.trim().slice(0, 255),
      email: donor.email.trim().slice(0, 320),
      phone: donor.phone.trim().slice(0, 50),
      anonymous: false,
    };
    // Name alone is not a valid receipt/contact channel. If storage is partial
    // or corrupted, leave donorInfo untouched so retryPaymentAfterFailure()
    // routes through the donor step instead of creating an effectively
    // anonymous PaymentIntent.
    if (!restored.email && !restored.phone) return;
    this.donorInfo = restored;
  }

  private applyConfirmedPaymentAmount(data: {
    amount_minor_units?: number | null;
    currency?: string | null;
  }): void {
    const currency = typeof data.currency === 'string' ? data.currency.trim().toUpperCase() : '';
    if (/^[A-Z]{3}$/.test(currency)) {
      this.config.currency = currency;
    }

    const amountMinor = Number(data.amount_minor_units);
    if (!Number.isFinite(amountMinor) || amountMinor <= 0) return;

    const amountMajor = this.toMajorUnits(amountMinor);
    this.confirmedTotalAmount = amountMajor;

    if (!this.selectedAmount || this.selectedAmount <= 0) {
      this.selectedAmount = amountMajor;
      this.coverFeesChecked = false;
    }

    this.emitGiftChange();
  }

  private showPaymentFailed(reason: string): void {
    // Redirect-provider status values can originate in the donor-return query
    // string. Never echo an unrecognized value into this innerHTML sink.
    const reasonCopy: Record<string, string> = {
      cancel: 'canceled',
      canceled: 'canceled',
      cancelled: 'canceled',
      fail: 'not completed',
      failed: 'not completed',
      failure: 'not completed',
      refunded: 'refunded',
    };
    const normalizedReason = (reason || '').trim().toLowerCase();
    const phrase = Object.prototype.hasOwnProperty.call(reasonCopy, normalizedReason)
      ? reasonCopy[normalizedReason]
      : 'not completed';
    const el = this.container.querySelector('.dk-step') || this.container;
    el.innerHTML = `
      <div class="dk-payment-failed" role="alert" aria-live="polite">
        <h2 class="dk-title">Payment Not Completed</h2>
        <p class="dk-subtitle">Your payment was ${phrase}. No charge has been made.</p>
        <button class="dk-btn-primary" id="dk-try-again" type="button">Try Again</button>
      </div>`;
    el.querySelector('#dk-try-again')?.addEventListener('click', () => this.retryPaymentAfterFailure());
  }

  private retryPaymentAfterFailure(): void {
    if (this.paymentIntentId) this.retryOfPaymentIntentId = this.paymentIntentId;
    this.paymentIntentId = '';
    this.donationAttemptKey = '';
    if (this.paymentElement) {
      this.paymentElement.destroy();
      this.paymentElement = null;
    }
    this.stripe = null;
    const hasDonorDecision = this.donorInfo.anonymous
      || !!this.donorInfo.email.trim()
      || !!this.donorInfo.phone.trim();
    if (!this.hasValidDonationAllocation()) {
      this.goTo('designation');
      return;
    }
    this.goTo(hasDonorDecision ? 'payment' : 'donor');
  }

    private async handlePayPalReturn(token: string): Promise<void> {
    this.container.innerHTML = `
      <div class="dk-step dk-step-payment">
        <h2 class="dk-title">Completing Payment</h2>
        <div class="dk-loading">
          <div class="dk-spinner"></div>
          <p>Capturing your PayPal payment…</p>
        </div>
        <p class="dk-error dk-hidden" id="dk-payment-error"></p>
      </div>`;

    try {
      const captureResult = await capturePayPalOrder(token);
      // Clean up the URL params
      const cleanUrl = window.location.href.split('?')[0];
      window.history.replaceState({}, document.title, cleanUrl);

      // Record donation in the database using saved checkout state
      try {
        const savedState = sessionStorage.getItem('ag_paypal_return');
        if (savedState) {
          const state = JSON.parse(savedState);
          sessionStorage.removeItem('ag_paypal_return');
          // CHG-1049 (S6) — donor wire-payload triplet via the stash;
          // pre-S6 only donor_name + donor_email were carried through
          // the PayPal redirect, so phone-only donors hit the backend
          // bi-channel guard on /donations after PayPal approval. The
          // stash now carries donor_phone too (see ag_paypal_return
          // setItem above).
          const donationResult = await recordDonation({
            organization_id: state.organization_id,
            widget_id: state.widget_id,
            designation_id: state.designation_id,
            campaign_id: state.campaign_id,
            session_id: state.session_id,
            amount_minor_units: state.amount_minor_units,
            currency: state.currency,
            // CHG-0463 (CHG-0438 Step 4 cleanup) — channel removed.
            source: state.source ?? state.channel,
            payment_method: 'paypal',
            donor_name: state.donor_name,
            donor_email: state.donor_email,
            donor_phone: state.donor_phone,
            donor_phone_country: state.donor_phone_country,
            anonymous: state.anonymous,
            payment_provider: 'paypal',
            payment_provider_id: token,
            metadata: this.buildDonationMetadata(),
            donate_page_context: state.donate_page_context,
          });
          // Set returning-donor recognition cookie if token was minted
          if (donationResult && donationResult.recognition_token) {
            try {
              document.cookie = 'ag_donor=' + donationResult.recognition_token
                + ';path=/donate;max-age=31536000;SameSite=Lax';
            } catch { /* cookie write failure is non-fatal */ }
          }
        }
      } catch {
        // Non-fatal: PayPal capture already succeeded
        console.warn('[DonationCheckout] paypal donation recording failed (non-fatal)');
      }

      this.goTo('confirm');
    } catch (err) {
      console.error('[DonationCheckout] paypal capture error', err);
      const errEl = this.container.querySelector<HTMLElement>('#dk-payment-error');
      if (errEl) {
        errEl.textContent = 'Failed to complete PayPal payment. Please contact support.';
        errEl.classList.remove('dk-hidden');
      }
      // Hide the spinner
      const loadingEl = this.container.querySelector<HTMLElement>('.dk-loading');
      if (loadingEl) loadingEl.style.display = 'none';
    }
  }

  // CHG-MANUALPAY — offline payment panel: show the org's account details, then
  // collect the donor's reported transaction reference + payer name and submit a
  // pending-review donation (no gateway involved).
  private renderOfflinePanel(): void {
    const accounts = this.manualAccounts;
    const total = this.getTotalAmount();
    const publicMethodName = (
      this.paymentMethods?.methods?.find(
        (method) => method.provider_key === this.selectedPaymentMethod,
      )?.display_name || 'Pay offline'
    ).trim();
    const accountOptions = accounts
      .map((a) => `<option value="${a.id}">${this.esc(a.label)} — ${this.esc(a.account_number)}</option>`)
      .join('');
    // CHG-3293 (issue #287) — instructions are admin-entered free text; render with
    // white-space:pre-line so donor-facing line breaks survive (HTML would else collapse \n).
    const detailsFor = (a: ManualPaymentAccountPublic) => `
      <div class="dk-offline-account" style="border:1px solid rgba(0,0,0,0.12);border-radius:8px;padding:12px;margin:8px 0;font-size:0.9em">
        <div><span style="display:inline-block;vertical-align:middle;margin-right:6px" aria-hidden="true">${this.getProviderIcon(a.method)}</span><strong>${this.esc(a.label)}</strong> <span style="opacity:0.7">(${this.esc(a.method)})</span></div>
        <div>Account: <strong>${this.esc(a.account_number)}</strong></div>
        ${a.account_name ? `<div>Name: ${this.esc(a.account_name)}</div>` : ''}
        ${a.instructions ? `<div style="margin-top:6px;opacity:0.85;white-space:pre-line">${this.esc(a.instructions)}</div>` : ''}
      </div>`;

    // donate-page v3: the offline panel uses the same bottom [← Back] [Submit]
    // actions row as every other step (mock parity), instead of a top Back link
    // + a standalone submit. Non-v3 keeps the historical top Back + plain submit.
    const offV3 = this.config.formStyle === 'v3';
    const offSubmitBtn = `<button class="${this.config.buttonStyle === 'outlined' ? 'dk-btn-primary dk-btn-outlined' : 'dk-btn-primary'}" id="dk-offline-submit">Submit for verification</button>`;
    this.container.innerHTML = `
      <div class="dk-step dk-step-offline">
        <h3 class="dk-step-title">${this.esc(publicMethodName || 'Pay offline')}</h3>
        ${offV3 ? '' : '<button class="dk-back dk-back-sub" data-target="payment">← Choose a different method</button>'}
        <p class="dk-summary">Send <strong>${this.fmtCurrency(total)}</strong> to one of the accounts below, then enter your transaction details so we can verify it.</p>
        ${accounts.length > 1 ? `
          <label class="dk-field-label" for="dk-offline-account">Account you paid to</label>
          <div class="dk-select-wrap">
            <select id="dk-offline-account">${accountOptions}</select>
          </div>` : ''}
        <div id="dk-offline-details">${accounts[0] ? detailsFor(accounts[0]) : ''}</div>
        <label class="dk-field-label" for="dk-offline-txn">Transaction ID</label>
        <input id="dk-offline-txn" class="dk-input" type="text" placeholder="e.g. TXN9F2A7C" autocomplete="off" />
        <label class="dk-field-label" for="dk-offline-payer">Your ${this.esc(accounts[0]?.method || 'account')} name / number</label>
        <input id="dk-offline-payer" class="dk-input" type="text" placeholder="Name or number you paid from" autocomplete="off" />
        <p class="dk-error dk-hidden" id="dk-offline-error"></p>
        ${offV3
          ? `<div class="dk-actions">${this.v3ActionButtons('dk-offline-back', offSubmitBtn)}</div>`
          : offSubmitBtn}
      </div>`;

    // Back handler — bound to both the historical top link (.dk-back, non-v3)
    // and the v3 bottom actions-row Back (#dk-offline-back).
    const offlineBack = (): void => {
      // donate-page v3: no method is pre-selected, so just CLEAR the selection
      // and return to the payment picker — the donor re-picks (no auto-select of
      // another provider, no auto-redirect). Consistent with the v3 picker-only
      // payment step.
      if (this.config.formStyle === 'v3') {
        this.selectedPaymentMethod = '';
        this.goTo('payment');
        return;
      }
      // CHG-MANUALPAY (non-v3) — offline is the selected method, so returning to
      // the payment step as-is would just re-render this panel. Snap selection to
      // the first non-offline method (if any) so the donor lands back on the
      // gateway picker; if offline is the only method, step back to donor.
      const others = (this.paymentMethods?.methods ?? []).filter(
        m => m.type !== 'manual' && m.type !== 'offline' && m.provider_key !== 'offline',
      );
      if (others.length > 0) {
        this.selectedPaymentMethod = others[0]!.provider_key;
        this.goTo('payment');
      } else {
        this.goTo('donor');
      }
    };
    this.container.querySelector<HTMLButtonElement>('.dk-back')?.addEventListener('click', offlineBack);
    this.container.querySelector<HTMLButtonElement>('#dk-offline-back')?.addEventListener('click', offlineBack);

    const sel = this.container.querySelector<HTMLSelectElement>('#dk-offline-account');
    const detailsEl = this.container.querySelector<HTMLDivElement>('#dk-offline-details');
    sel?.addEventListener('change', () => {
      const a = accounts.find((x) => x.id === sel.value);
      if (a && detailsEl) detailsEl.innerHTML = detailsFor(a);
    });

    const errEl = this.container.querySelector<HTMLParagraphElement>('#dk-offline-error');
    const submitBtn = this.container.querySelector<HTMLButtonElement>('#dk-offline-submit');
    submitBtn?.addEventListener('click', async () => {
      const accountId = sel?.value || accounts[0]?.id;
      const txn = this.container.querySelector<HTMLInputElement>('#dk-offline-txn')?.value.trim() || '';
      const payer = this.container.querySelector<HTMLInputElement>('#dk-offline-payer')?.value.trim() || '';
      const account = accounts.find((x) => x.id === accountId);
      if (!account || txn.length < 2 || !payer) {
        if (errEl) { errEl.textContent = 'Please enter your transaction ID and payer name.'; errEl.classList.remove('dk-hidden'); }
        return;
      }
      // Pre-submit contact guard (v3) — a backstop for the donor-skip path: the
      // server rejects a non-anonymous donation with no email/phone (422
      // "Non-anonymous donations require donor_email or donor_phone"). The
      // universal donor-step guard (CHG-3229) already catches this when the donor
      // step runs; this v3-gated backstop covers the cases where it was skipped,
      // with a friendly message instead of a raw 422. v3-gated so non-opted
      // embeds stay byte-identical (inertness).
      if (this.config.formStyle === 'v3'
          && !this.donorInfo.anonymous && !this.donorInfo.email.trim() && !this.donorInfo.phone.trim()) {
        if (errEl) {
          errEl.textContent = this.anonymousAllowed()
            ? 'Please add your email or phone on the previous step so we can send your receipt, or choose to give anonymously.'
            : 'Please add your email or phone on the previous step so we can send your receipt.';
          errEl.classList.remove('dk-hidden');
        }
        return;
      }
      submitBtn.disabled = true;
      submitBtn.textContent = 'Submitting…';
      try {
        await submitManualDonation({
          organization_id: this.config.organizationId,
          designation_id: this.selectedDesignation?.id ?? null,
          campaign_id: this.effectiveCampaignId() ?? null,
          // CHG-3337 — session/event attribution for offline claims (mirrors
          // the online createPaymentIntent payload; undefined outside
          // session/event donate pages). Recorded at submission — the ONLY
          // moment the context exists — so an admin approval can flow the
          // gift into session counters/live feeds like an online donation.
          session_id: this.effectiveSessionId(),
          source: this.config.source,
          donate_page_context: this.buildDonatePageContext(
            this.selectedDesignation?.id,
          ),
          amount_minor_units: this.toMinorUnits(total),
          currency: this.config.currency || 'USD',
          manual_account_id: account.id,
          payment_method: account.method,
          transaction_ref: txn,
          payer_name: payer,
          donor_name: this.donorInfo.name || null,
          donor_email: this.donorInfo.email || null,
          donor_phone: this.donorInfo.phone || null,
          donor_phone_country: this.donorInfo.phone ? this.phoneCountry : null,
          anonymous: this.donorInfo.anonymous,
        });
        this.renderOfflineThankYou();
      } catch (e) {
        const detail = e instanceof Error ? e.message : 'Submission failed';
        if (errEl) { errEl.textContent = detail; errEl.classList.remove('dk-hidden'); }
        submitBtn.disabled = false;
        submitBtn.textContent = 'Submit for verification';
      }
    });
  }

  private renderOfflineThankYou(): void {
    const total = this.getTotalAmount();

    // donate-page v3: the offline thank-you uses the SAME Done-step design as the
    // gateway confirm (seal, eyebrow, receipt box, share, restart) so the last
    // step is consistent — only the wording reflects "awaiting verification"
    // (the gift isn't confirmed until the team verifies the transfer). Non-v3
    // keeps the historical plain ⏳ panel.
    if (this.config.formStyle === 'v3') {
      const first = (!this.donorInfo.anonymous && this.donorInfo.name.trim())
        ? this.esc(this.donorInfo.name.trim().split(/\s+/)[0]!)
        : '';
      const receiptLine = this.donorInfo.anonymous
        ? this.esc(this.vocab('confirm_receipt_anon', 'sent to you'))
        : (this.donorInfo.email
            ? `${this.esc(this.vocab('confirm_receipt_to', 'sent to'))} ${this.esc(this.donorInfo.email)}`
            : (this.donorInfo.phone
                ? `${this.esc(this.vocab('confirm_receipt_to', 'sent to'))} ${this.esc(this.donorInfo.phone)}`
                : this.esc(this.vocab('confirm_receipt_anon', 'sent to you'))));
      const desigName = this.selectedDesignation?.name || this.selectedCampaign?.name || '';
      this.container.innerHTML = `
        <div class="dk-step dk-step-confirm dk-step-confirm-pending">
          <div class="dk-confirm-seal dk-confirm-seal-pending" aria-hidden="true">⏳</div>
          <div class="dk-step-eyebrow dk-confirm-eyebrow">${this.esc(this.vocab('offline_eyebrow', 'Payment submitted'))}</div>
          <h2 class="dk-title dk-confirm-title">${first ? `${this.esc(this.vocab('confirm_thanks_named', 'Thank you,'))} ${first}` : this.esc(this.vocab('offline_thanks', 'Thank you for your gift'))}</h2>
          <p class="dk-confirm-msg">${this.esc(this.vocab('offline_msg', "We've received your payment details and will confirm your gift once our team verifies the transaction."))}</p>
          ${this.renderSessionConfirmProof(true)}
          <div class="dk-confirm-receipt">
            <div class="dk-confirm-rline"><span class="dk-confirm-rkey">${this.esc(this.vocab('confirm_you_gave', 'You gave'))}</span><span class="dk-confirm-rval dk-confirm-rnum">${this.fmtCurrency(total)}</span></div>
            <div class="dk-confirm-rline"><span class="dk-confirm-rkey">${this.esc(this.vocab('confirm_frequency', 'Frequency'))}</span><span class="dk-confirm-rval">${this.esc(this.frequencyLabel(this.selectedFrequency))}</span></div>
            ${desigName ? `<div class="dk-confirm-rline"><span class="dk-confirm-rkey">${this.esc(this.vocab('confirm_designation', 'Designation'))}</span><span class="dk-confirm-rval">${this.esc(desigName)}</span></div>` : ''}
            <div class="dk-confirm-rline"><span class="dk-confirm-rkey">${this.esc(this.vocab('offline_status', 'Status'))}</span><span class="dk-confirm-rval">${this.esc(this.vocab('offline_status_val', 'Awaiting verification'))}</span></div>
            <div class="dk-confirm-rline"><span class="dk-confirm-rkey">${this.esc(this.vocab('confirm_receipt', 'Receipt'))}</span><span class="dk-confirm-rval">${receiptLine}</span></div>
          </div>
          ${this.config.showShareButtons !== false ? this.successSharePromptHtml(true) : ''}
          <button class="dk-confirm-restart" id="dk-restart">↺ ${this.esc(this.vocab('confirm_restart', 'Make another donation'))}</button>
          <p class="dk-confirm-restart-unavailable" id="dk-restart-unavailable" role="status" hidden>${this.esc(this.vocab('confirm_restart_unavailable', 'This giving session is not accepting another donation right now.'))}</p>
        </div>`;
      // Wire share buttons + restart (same handlers the gateway confirm uses).
      if (this.config.showShareButtons !== false) {
        this.renderShareButtons();
        this.wireSuccessSharePrompt();
      } else {
        document.body?.classList.remove('ag-checkout-success-share-active');
      }
      this.wireContributionRestart();
      return;
    }

    this.container.innerHTML = `
      <div class="dk-step dk-step-confirm">
        <div class="dk-confirm-icon" style="font-size:2.5em">⏳</div>
        <h2 class="dk-title">Awaiting verification</h2>
        <p class="dk-summary">Thank you! We've received your ${this.fmtCurrency(total)} offline payment details.
        ${this.donorInfo.email && !this.donorInfo.anonymous ? `We'll email <strong>${this.esc(this.donorInfo.email)}</strong> once it's verified.` : `It will appear once our team verifies the transaction.`}</p>
        ${this.renderSessionConfirmProof(true)}
      </div>`;
  }

  // CHG-3188 — guard so onSuccess fires exactly once per donation even if the
  // confirm step re-renders (async campaign-progress/share/upsell re-renders).
  private onSuccessFired = false;

  private renderConfirm(): void {
    const msg = this.config.thankYouMessage;
    const pledgeMode = this.isNoPaymentPledge() && !!this.pledgeId;

    // CHG-3188 (Donate-Now S5) — signal a completed donation to the host so an
    // authenticated Donate-Now hand-off can re-derive the session (the account
    // just became a real donor server-side) and route to /my-giving. Fired
    // before the body renders, exactly once; errors in the host callback must
    // not break the thank-you screen.
    if (!pledgeMode && !this.onSuccessFired) {
      this.onSuccessFired = true;
      try {
        this.config.onSuccess?.();
      } catch {
        /* host callback must never break the confirmation UI */
      }
    }

    // Impact statement HTML
    let impactHtml = '';
    if (!pledgeMode && this.config.showImpactStatement && this.config.amountImpacts?.length) {
      const amountMinorUnits = this.toMinorUnits(this.selectedAmount);
      // Find exact match or closest lower impact
      const sorted = [...this.config.amountImpacts].sort((a, b) => b.amount_minor_units - a.amount_minor_units);
      const match = sorted.find(i => i.amount_minor_units <= amountMinorUnits);
      if (match) {
        impactHtml = `
          <div class="dk-impact-card">
            <span class="dk-impact-check">&#10003;</span>
            <span class="dk-impact-text">Your ${this.fmtCurrency(this.selectedAmount)} ${this.esc(match.label)}</span>
          </div>`;
      }
    }

    // donate-page v3: the mock's Done step \u2014 circular seal, "Gift confirmed"
    // eyebrow, "Thank you{, name}" title, a structured receipt box (You gave /
    // Frequency / Designation / Receipt), the share caption + icon row, and a
    // "\u21ba Make another donation" restart. Same hooks (#dk-share-buttons,
    // #dk-restart, #dk-campaign-progress) so the rest of this method is shared.
    let html: string;
    if (this.config.formStyle === 'v3') {
      const first = (!this.donorInfo.anonymous && this.donorInfo.name.trim())
        ? this.donorInfo.name.trim().split(/\s+/)[0]!
        : '';
      const receiptLine = this.donorInfo.anonymous
        ? this.esc(this.vocab('confirm_receipt_anon', 'sent to you'))
        : (this.donorInfo.email
            ? `${this.esc(this.vocab('confirm_receipt_to', 'sent to'))} ${this.esc(this.donorInfo.email)}`
            : (this.donorInfo.phone
                ? `${this.esc(this.vocab('confirm_receipt_to', 'sent to'))} ${this.esc(this.donorInfo.phone)}`
                : this.esc(this.vocab('confirm_receipt_anon', 'sent to you'))));
      const desigName = this.selectedDesignation?.name || this.selectedCampaign?.name || '';
      const dueDateDisplay = this.pledgeDueDate
        ? this.formatPledgeDate(this.pledgeDueDate)
        : '';
      const reminderLine = this.pledgeNextReminderAt
        ? `${this.vocab('confirm_pledge_remind_on', "We'll remind you on")} ${this.formatPledgeDate(this.pledgeNextReminderAt)}`
        : this.vocab('confirm_pledge_remind_generic', "We'll remind you before it is due.");
      const eyebrow = pledgeMode
        ? this.vocab('confirm_eyebrow_pledge', 'Pledge submitted')
        : this.vocab('confirm_eyebrow', 'Gift confirmed');
      const title = pledgeMode
        ? (first
            ? `${this.vocab('confirm_thanks_named', 'Thank you,')} ${first}`
            : this.vocab('confirm_thanks_pledge', 'Thank you for your pledge'))
        : (first
            ? `${this.vocab('confirm_thanks_named', 'Thank you,')} ${first}`
            : this.vocab('confirm_thanks', 'Thank you for your gift'));
      const bodyMessage = pledgeMode
        ? this.vocab(
            'confirm_msg_pledge',
            "Your pledge has been recorded. Nothing has been charged today.",
          )
        : msg;
      html = `
      <div class="dk-step dk-step-confirm">
        <div class="dk-confirm-seal" aria-hidden="true">\u2726</div>
        <div class="dk-step-eyebrow dk-confirm-eyebrow">${this.esc(eyebrow)}</div>
        <h2 class="dk-title dk-confirm-title">${this.esc(title)}</h2>
        <p class="dk-confirm-msg">${this.esc(bodyMessage)}</p>
        ${impactHtml}
        ${pledgeMode ? '' : this.renderSessionConfirmProof(false)}
        <div class="dk-confirm-receipt">
          <div class="dk-confirm-rline"><span class="dk-confirm-rkey">${this.esc(pledgeMode ? this.vocab('confirm_you_pledged', 'You pledged') : this.vocab('confirm_you_gave', 'You gave'))}</span><span class="dk-confirm-rval dk-confirm-rnum">${this.fmtCurrency(this.getTotalAmount())}</span></div>
          <div class="dk-confirm-rline"><span class="dk-confirm-rkey">${this.esc(pledgeMode ? this.vocab('confirm_pledge_cadence', 'Schedule') : this.vocab('confirm_frequency', 'Frequency'))}</span><span class="dk-confirm-rval">${this.esc(this.frequencyLabel(this.selectedFrequency))}</span></div>
          ${desigName ? `<div class="dk-confirm-rline"><span class="dk-confirm-rkey">${this.esc(this.vocab('confirm_designation', 'Designation'))}</span><span class="dk-confirm-rval">${this.esc(desigName)}</span></div>` : ''}
          ${pledgeMode && dueDateDisplay ? `<div class="dk-confirm-rline"><span class="dk-confirm-rkey">${this.esc(this.selectedFrequency === 'one-time' ? this.vocab('confirm_pledge_due', 'Giving by') : this.vocab('confirm_pledge_until', 'Until'))}</span><span class="dk-confirm-rval">${this.esc(dueDateDisplay)}</span></div>` : ''}
          ${pledgeMode
            ? `<div class="dk-confirm-rline"><span class="dk-confirm-rkey">${this.esc(this.vocab('confirm_pledge_reminder', 'Reminder'))}</span><span class="dk-confirm-rval">${this.esc(reminderLine)}</span></div>`
            : `<div class="dk-confirm-rline"><span class="dk-confirm-rkey">${this.esc(this.vocab('confirm_receipt', 'Receipt'))}</span><span class="dk-confirm-rval">${receiptLine}</span></div>`}
        </div>
        ${pledgeMode ? `<p class="dk-confirm-pledge-note">${this.esc(this.vocab('confirm_pledge_note', 'Nothing has been charged today.'))}</p>` : '<div id="dk-campaign-progress"></div>'}
        ${!pledgeMode && this.config.showShareButtons !== false ? this.successSharePromptHtml(false) : ''}
        ${pledgeMode ? '' : '<div id="dk-recurring-upsell"></div>'}
        <button class="dk-confirm-restart" id="dk-restart">\u21ba ${this.esc(pledgeMode ? this.vocab('confirm_restart_pledge', 'Give or pledge again') : this.vocab('confirm_restart', 'Make another donation'))}</button>
        <p class="dk-confirm-restart-unavailable" id="dk-restart-unavailable" role="status" hidden>${this.esc(this.vocab('confirm_restart_unavailable', 'This giving session is not accepting another donation right now.'))}</p>
      </div>`;
    } else {
      html = `
      <div class="dk-step dk-step-confirm">
        <div class="dk-checkmark">\u2713</div>
        <h2 class="dk-title">${this.esc(pledgeMode ? this.vocab('confirm_thanks_pledge', 'Thank you for your pledge') : 'Thank you!')}</h2>
        <p class="dk-confirm-msg">${this.esc(pledgeMode ? this.vocab('confirm_msg_pledge', 'Your pledge has been recorded. Nothing has been charged today.') : msg)}</p>
        ${
          !pledgeMode && this.donorInfo.email && !this.donorInfo.anonymous
            ? `<p class="dk-receipt-note">A receipt will be sent to <strong>${this.esc(this.donorInfo.email)}</strong></p>`
            : ''
        }
        <p class="dk-confirm-amount">${this.esc(pledgeMode ? this.vocab('confirm_you_pledged', 'You pledged') : '')}${pledgeMode ? ' ' : ''}${this.fmtCurrency(this.getTotalAmount())}${this.selectedDesignation ? ` to ${this.esc(this.selectedDesignation.name)}` : ''}</p>
        ${pledgeMode && this.pledgeDueDate ? `<p class="dk-confirm-msg">${this.esc(this.vocab('confirm_pledge_due', 'Giving by'))} ${this.esc(this.formatPledgeDate(this.pledgeDueDate))}</p>` : ''}
        ${pledgeMode ? '' : this.renderSessionConfirmProof(false)}
        ${impactHtml}
        ${pledgeMode ? '' : '<div id="dk-campaign-progress"></div>'}
        ${!pledgeMode && this.config.showShareButtons !== false ? this.successSharePromptHtml(false) : ''}
        ${pledgeMode ? '' : '<div id="dk-recurring-upsell"></div>'}
        <button class="dk-btn-secondary" id="dk-restart">${this.esc(pledgeMode ? this.vocab('confirm_restart_pledge', 'Give or pledge again') : 'Make another donation')}</button>
        <p class="dk-confirm-restart-unavailable" id="dk-restart-unavailable" role="status" hidden>${this.esc(this.vocab('confirm_restart_unavailable', 'This giving session is not accepting another donation right now.'))}</p>
      </div>`;
    }
    this.container.innerHTML = html;

    // Confetti animation
    if (!pledgeMode && this.config.showConfetti) {
      const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (!prefersReducedMotion) {
        const confettiContainer = document.createElement('div');
        confettiContainer.className = 'dk-confetti-container';
        this.container.appendChild(confettiContainer);
        const colors = ['#0F766E', '#14B8A6', '#F59E0B', '#EC4899', '#8B5CF6'];
        for (let i = 0; i < 50; i++) {
          const piece = document.createElement('div');
          piece.className = 'dk-confetti-piece';
          piece.style.left = Math.random() * 100 + '%';
          piece.style.animationDelay = Math.random() * 2 + 's';
          piece.style.backgroundColor = colors[Math.floor(Math.random() * colors.length)];
          // Randomize shape: some circles, some rectangles
          if (Math.random() > 0.5) {
            piece.style.borderRadius = '50%';
          }
          confettiContainer.appendChild(piece);
        }
        setTimeout(() => confettiContainer.remove(), 4000);
      }
    }

    // Campaign progress is a campaign-surface affordance. SESSION confirmations
    // render their own session-scoped proof above, so do not mix in the backing
    // campaign thermometer under "Added to the live session total".
    const isSessionContext = (this.config.pageContextType || '').toUpperCase() === 'SESSION';
    if (!pledgeMode && isSessionContext) {
      this.renderSessionConfirmProgress();
    }
    if (!pledgeMode && this.config.campaignId && !isSessionContext) {
      this.renderCampaignProgress(this.config.campaignId);
    }
    // CHG-1362 (BUG-155): also refresh the server-rendered page-level
    // progress block (donate_page.html / split.html etc.) so Stripe
    // inline payments update the background progress bar without a reload.
    if (!pledgeMode && typeof (window as any).__agRefreshCampaignProgress === 'function') {
      (window as any).__agRefreshCampaignProgress();
    }

    // Share buttons (only if showShareButtons is not explicitly false)
    if (!pledgeMode && this.config.showShareButtons !== false) {
      this.renderShareButtons();
      this.wireSuccessSharePrompt();
    } else {
      document.body?.classList.remove('ag-checkout-success-share-active');
    }

    // Recurring upsell (only if showRecurringUpsell and this was a one-time donation)
    if (!pledgeMode && this.config.showRecurringUpsell && this.selectedFrequency === 'one-time') {
      this.renderRecurringUpsell();
    }

    this.wireContributionRestart();

    // A/B variant conversion tracking
    if (!pledgeMode && this.config.pageId) {
      trackFunnel(this.config.pageId, {
        event_type: 'donation_complete',
        metadata: {
          variant_id: this.config.variantId,
          amount_minor_units: this.toMinorUnits(this.getTotalAmount()),
          frequency: this.selectedFrequency,
          // CHG-0171 W4 — capture effective cascade state at the
          // moment of conversion for funnel debugging. Also keeps
          // TypeScript noUnusedLocals satisfied for the W4 helpers
          // that'll get real consumers in W4b (pledge flow + UI
          // cascade preview).
          cascade: this._effectiveStateForDebug(),
        },
      }).catch(() => {});
    }
  }

  /**
   * Hosted SESSION lifecycle bridge. A successful provider return remains a
   * receipt after pause/end, but it cannot become a back door into a new
   * checkout attempt. Ordinary embeds never call this method and retain the
   * default accepting state.
   */
  public setContributionAcceptance(accepting: boolean): void {
    this.contributionsAccepted = accepting === true;
    this.syncContributionRestartAvailability();
  }

  private syncContributionRestartAvailability(): void {
    const restart = this.rootContainer.querySelector<HTMLButtonElement>('#dk-restart');
    const unavailable = this.rootContainer.querySelector<HTMLElement>('#dk-restart-unavailable');
    if (restart) {
      restart.disabled = !this.contributionsAccepted;
      restart.hidden = !this.contributionsAccepted;
      restart.setAttribute('aria-disabled', this.contributionsAccepted ? 'false' : 'true');
    }
    if (unavailable) unavailable.hidden = this.contributionsAccepted;
  }

  private wireContributionRestart(): void {
    this.syncContributionRestartAvailability();
    this.rootContainer.querySelector('#dk-restart')?.addEventListener('click', () => {
      this.restartContribution();
    });
  }

  private restartContribution(): void {
    if (!this.contributionsAccepted) return;
    this.resetDonationState();
    this.restartToFirstStep();
  }

  private successSharePromptHtml(pending: boolean): string {
    const title = this.vocab('success_share_title', 'Invite others to give');
    const desc = pending
      ? this.vocab('success_share_pending_desc', 'Share this campaign while your payment is awaiting verification.')
      : this.vocab('success_share_desc', 'Let friends and family join this campaign.');
    const amountLabel = this.vocab('success_share_include_amount', 'Include my gift amount');

    return `
      <div class="dk-success-share" role="dialog" aria-labelledby="dk-success-share-title" aria-describedby="dk-success-share-desc">
        <button type="button" class="dk-success-share-dismiss" id="dk-share-dismiss" aria-label="${this.esc(this.vocab('success_share_dismiss', 'Dismiss share prompt'))}">&times;</button>
        <div class="dk-success-share-eyebrow">${this.esc(this.vocab('confirm_share_caption', 'Share this page'))}</div>
        <h3 id="dk-success-share-title">${this.esc(title)}</h3>
        <p id="dk-success-share-desc">${this.esc(desc)}</p>
        <label class="dk-success-share-amount" for="dk-share-include-amount">
          <input type="checkbox" id="dk-share-include-amount" />
          <span>${this.esc(amountLabel)}</span>
        </label>
        <div id="dk-share-buttons"></div>
      </div>`;
  }

  private successShareMessage(includeAmount: boolean): string {
    const orgName = this.config.orgName || 'a great cause';
    const total = this.getTotalAmount();
    if (includeAmount && total > 0) {
      return `I just donated ${this.fmtCurrency(total)} to ${orgName}!`;
    }
    return `I just donated to ${orgName}!`;
  }

  private wireSuccessSharePrompt(): void {
    const prompt = this.container.querySelector<HTMLElement>('.dk-success-share');
    const hostBody = document.body;
    if (!prompt) {
      hostBody?.classList.remove('ag-checkout-success-share-active');
      return;
    }
    hostBody?.classList.add('ag-checkout-success-share-active');
    const hidePrompt = () => {
      prompt.hidden = true;
      hostBody?.classList.remove('ag-checkout-success-share-active');
    };
    this.container.querySelector('#dk-share-dismiss')?.addEventListener('click', hidePrompt);
    this.container.querySelector<HTMLInputElement>('#dk-share-include-amount')?.addEventListener('change', () => {
      this.renderShareButtons();
    });
  }

  /**
   * Reset all donation-scoped state so a second gift via the #dk-restart
   * "Make another donation" button starts clean WITHOUT a full page refresh
   * (BUG-199 / #199). A page refresh re-runs init() — which is why refreshing
   * "fixed" the stale-state symptom; this helper makes the in-place restart
   * equivalent. The prior handler reset only core fields (designation / amount
   * / frequency / donor / dedication / comment / PaymentIntent + idempotency
   * key) and left entity, recurring-intent, fee-cover, pledge, and Stripe
   * state stale — so a second donation inherited the first one's campaign,
   * cover-fees toggle, pledge ids, and a Payment Element bound to the
   * already-consumed clientSecret.
   */
  private resetDonationState(): void {
    document.body?.classList.remove('ag-checkout-success-share-active');
    // Entity selection (CHG-0171 W4) — designation + campaign + cascade.
    this.selectedDesignation = null;
    this.selectedCampaign = null;
    this.selectedCascade = null;
    this.selectedAmount = 0;
    this.confirmedTotalAmount = null;
    this.selectedFrequency = 'one-time';
    this.selectedIntentKey = null;
    this.donorIntent = null;
    this.intentVetoNotice = '';
    // CHG-3188 — a genuinely new gift re-arms the one-shot onSuccess signal.
    this.onSuccessFired = false;
    // CHG-3188 — when the email is locked (authenticated Donate-Now hand-off),
    // a second gift via "Make another donation" must KEEP the account email
    // (and stay non-anonymous) so it cannot orphan a Donor. Otherwise emailLocked
    // would go false (no seeded email), the field would become editable, and the
    // anonymous toggle would reappear. Re-seed the locked identity exactly like
    // the constructor's prefill block (donorPrefilled also stays true so the
    // donor step can still auto-skip). Public/portal flows (no lock) reset to
    // empty as before.
    if (this.config.lockDonorEmail && (this.config.defaultDonorEmail || '').trim()) {
      const _pn = (this.config.defaultDonorName || '').trim();
      const _pe = (this.config.defaultDonorEmail || '').trim();
      const _pp = (this.config.defaultDonorPhone || '').trim();
      this.donorInfo = { name: _pn, email: _pe, phone: _pp, anonymous: false };
      this.donorPrefilled = !!(_pn && (_pe || _pp));
    } else {
      this.donorInfo = { name: '', email: '', phone: '', anonymous: false };
    }
    this.dedication = { honoree: '', notifyEmail: '' };
    this.dedicationEnabled = false;
    this.donorComment = '';
    this.coverFeesChecked = false;
    // Pledge-step state (CHG-0932) — stale ids would thread a prior pledge
    // into the next gift's cycle-0 PaymentIntent metadata.
    this.pledgeId = null;
    this.pledgeDonorId = null;
    // CHG-3053 / DRIFT-1165 — drop the persisted-pledge material snapshot so the
    // next gift's divergence check starts clean.
    this.pledgedAmountMinorUnits = null;
    this.pledgedFrequency = null;
    this.pledgeSubmitting = false;
    this.pledgeError = null;
    this.pledgeStartDate = '';
    this.pledgeDueDate = '';
    this.pledgeNextReminderAt = '';
    // CHG-3027 / PLG-R01a — a genuinely new gift starts a new pledge attempt;
    // clear the per-attempt pledge key so it never dedupes against a prior one.
    this.pledgeAttemptKey = '';
    this.paymentIntentId = '';
    // CHG-2813 (DRIFT-1084a) — this is a genuinely NEW donation; clear the
    // per-attempt idempotency key so the next charge mints a fresh one and
    // does NOT dedupe against the gift just completed.
    this.donationAttemptKey = '';
    this.checkoutAttemptId = '';
    this.retryOfPaymentIntentId = '';
    // Tear down Stripe handles bound to the completed intent so the next gift
    // mounts a fresh Payment Element instead of reusing one wired to the prior
    // clientSecret (mirrors the goTo() leave-payment teardown — CHG-3225).
    if (this.paymentElement) {
      this.paymentElement.destroy();
      this.paymentElement = null;
    }
    this.stripe = null;
  }

  /** Route a fresh "Make another donation" to the correct first step.
   *
   *  Bug fix: resetDonationState() has just CLEARED the entity selection
   *  (selectedDesignation / selectedCampaign / cascade). On a context-locked
   *  donate page (a campaign page, or a single-designation fund page) the
   *  designation step is suppressed by render() (`designations.length <= 1 ||
   *  effectiveContextLock`), so the previous `goTo('designation')` stranded the
   *  donor on the lock banner with no way forward — the reported "Make another
   *  donation shows the campaign info instead of the fund picker" defect. We now
   *  mirror init()'s initial-step logic: re-seed the locked entity + cascade and
   *  jump straight to amount when the picker is hidden; only open the designation
   *  picker when it is actually shown (multi-designation, unlocked page). */
  private restartToFirstStep(): void {
    if (this.effectiveContextLock) {
      this.seedContextLockedSelection();
      this.goTo(this.intentStepAvailable() ? 'intent' : 'amount');
      return;
    }
    if (this.designations.length === 1) {
      const first = this.designations[0]!;
      this.selectedDesignation = first;
      const opt = this.designationOptions.find(o => o.id === first.id);
      this.setSelectedCascade(opt ? opt.cascade : null);
      this.goTo(this.intentStepAvailable() ? 'intent' : 'amount');
      return;
    }
    this.goTo(this.intentStepAvailable() ? 'intent' : 'designation');
  }

  /** donate-page v3 — build a Stripe Elements `appearance` from the resolved
   *  --dk-* brand tokens on the mounted container, so the Payment Element's card
   *  fields match the v3 card (accent, radius, fonts, colors) instead of Stripe's
   *  default light styling that clashes with the brand. Reads computed styles so
   *  per-brand token overrides flow through. PURE PRESENTATION — does not touch
   *  the PaymentIntent, amount, currency, or confirmation logic. Returns
   *  undefined for non-v3 so the Element keeps Stripe's default look (inertness).
   *  (Closes the layout_shell spec's "themes Stripe Elements via its appearance
   *  API — NET-NEW wiring" item for the v3 step flow.) */
  private buildV3StripeAppearance(): Record<string, unknown> | undefined {
    if (this.config.formStyle !== 'v3') return undefined;
    if (typeof getComputedStyle !== 'function') return undefined;
    const cs = getComputedStyle(this.rootContainer);
    const tok = (name: string, fallback: string): string => {
      const v = cs.getPropertyValue(name).trim();
      return v || fallback;
    };
    const accent = tok('--dk-accent', '#0F766E');
    const bg = tok('--dk-bg', '#FFFFFF');
    const text = tok('--dk-text', '#1E293B');
    const muted = tok('--dk-text-muted', '#64748B');
    const border = tok('--dk-border', '#E2E8F0');
    const danger = tok('--dk-error', '#DC2626');
    const radius = tok('--dk-radius', '8px');
    const font = tok('--dk-font', 'Inter, system-ui, -apple-system, sans-serif');
    return {
      theme: 'stripe',
      variables: {
        colorPrimary: accent,
        colorBackground: bg,
        colorText: text,
        colorTextSecondary: muted,
        colorTextPlaceholder: muted,
        colorDanger: danger,
        fontFamily: font,
        borderRadius: radius,
        spacingUnit: '4px',
      },
      rules: {
        '.Input': {
          border: `1.5px solid ${border}`,
          boxShadow: 'none',
          padding: '12px 14px',
        },
        // Keep the focus indicator simple (border recolor only) — Stripe's
        // appearance rules parse a limited CSS subset, so we avoid color-mix()
        // / alpha-hex which a brand token might not satisfy.
        '.Input:focus': {
          border: `1.5px solid ${accent}`,
        },
        '.Label': {
          color: muted,
          fontWeight: '500',
        },
      },
    };
  }

  private async renderCampaignProgress(campaignId: string): Promise<void> {
    const el = this.container.querySelector<HTMLElement>('#dk-campaign-progress');
    if (!el) return;
    try {
      const apiUrl = (typeof window !== 'undefined' && (window as any).__DONATION_KIOSK_API_URL__) || window.location.origin;
      const resp = await fetch(`${apiUrl}/api/v1/campaigns/${encodeURIComponent(campaignId)}/public-progress`);
      if (!resp.ok) return;
      const data = await resp.json();
      const current = data.raised_minor_units || 0;
      const goal = data.goal_amount_minor_units || 0;
      const donorCount = data.donor_count || 0;
      const pct = goal > 0 ? Math.min(100, Math.round((current / goal) * 100)) : 0;
      el.innerHTML = `
        <div style="margin:12px 0;">
          <div style="background:var(--dk-border,#e5e7eb);border-radius:9999px;height:8px;overflow:hidden;width:100%;">
            <div style="background:var(--dk-accent,#0F766E);height:100%;border-radius:9999px;width:${pct}%;transition:width 0.5s;"></div>
          </div>
          <p style="font-size:12px;color:var(--dk-text-muted,#6b7280);margin-top:4px;text-align:center;">
            ${donorCount > 0 ? `You're one of ${donorCount} donors!` : `${pct}% of goal reached`}
          </p>
        </div>`;
    } catch {
      // Silently fail — progress display is optional enhancement
    }
  }

  private renderShareButtons(): void {
    const el = this.container.querySelector<HTMLElement>('#dk-share-buttons');
    if (!el) return;
    // Use shareUrl config (set by donate page or widget embed) to avoid sharing the iframe API URL
    const rawUrl = cleanDonationShareUrl(this.config.shareUrl || window.location.href);
    const shareUrl = encodeURIComponent(rawUrl);
    const includeAmount = Boolean(this.container.querySelector<HTMLInputElement>('#dk-share-include-amount')?.checked);
    const shareMessage = this.successShareMessage(includeAmount);
    const shareText = encodeURIComponent(shareMessage);
    const shareCopyText = `${shareMessage} ${rawUrl}`;
    const shareFullText = encodeURIComponent(shareCopyText);
    const shareEmailBody = encodeURIComponent(`${shareMessage}\n\n${rawUrl}`);

    // donate-page v3: round colored icon buttons (mock parity) — WhatsApp,
    // Facebook, X, Email, Copy — with brand colours from .dk-share-icon CSS.
    if (this.config.formStyle === 'v3') {
      const ico: Record<string, string> = {
        wa: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12.04 2c-5.46 0-9.91 4.45-9.91 9.91 0 1.75.46 3.45 1.32 4.95L2 22l5.25-1.38a9.9 9.9 0 0 0 4.79 1.22h.01c5.46 0 9.91-4.45 9.91-9.91 0-2.65-1.03-5.14-2.9-7.01A9.82 9.82 0 0 0 12.04 2zm5.52 13.99c-.25.7-1.45 1.34-1.99 1.39-.54.05-1.04.24-3.5-.73-2.94-1.16-4.83-4.15-4.98-4.34-.15-.2-1.2-1.6-1.2-3.05 0-1.45.76-2.16 1.03-2.46.27-.3.59-.37.79-.37.2 0 .39.002.56.01.18.008.42-.069.66.5.25.59.84 2.04.91 2.19.07.15.12.32.02.52-.1.2-.15.32-.3.5-.15.17-.31.39-.45.52-.15.15-.3.31-.13.6.17.3.76 1.25 1.63 2.02 1.12.99 2.06 1.3 2.36 1.45.3.15.47.12.64-.07.17-.2.74-.86.94-1.16.2-.3.39-.25.66-.15.27.1 1.7.8 1.99.95.3.15.5.22.57.35.07.12.07.72-.18 1.42z"/></svg>',
        fb: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M13.5 21v-8h2.7l.4-3.1h-3.1V7.92c0-.9.25-1.51 1.54-1.51h1.65V3.64c-.29-.04-1.27-.12-2.42-.12-2.4 0-4.04 1.46-4.04 4.15v2.33H7.5V13h2.73v8h3.27z"/></svg>',
        x: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M17.53 3H20.5l-6.49 7.41L21.75 21h-6.02l-4.71-6.16L5.62 21H2.65l6.95-7.94L2.25 3h6.17l4.26 5.63L17.53 3zm-1.06 16.2h1.64L7.6 4.7H5.84l10.63 14.5z"/></svg>',
        em: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M3 5h18c.55 0 1 .45 1 1v12c0 .55-.45 1-1 1H3c-.55 0-1-.45-1-1V6c0-.55.45-1 1-1zm9 6.88 7.4-4.88H4.6L12 11.88zM4 8.3V17h16V8.3l-7.45 4.92a1 1 0 0 1-1.1 0L4 8.3z"/></svg>',
        cp: '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M10.6 13.4a3.5 3.5 0 0 0 5 0l3-3a3.54 3.54 0 0 0-5-5l-1.4 1.4 1.42 1.42 1.4-1.4a1.54 1.54 0 1 1 2.18 2.18l-3 3a1.54 1.54 0 0 1-2.18 0 1 1 0 1 0-1.42 1.4zm2.8-2.8a3.5 3.5 0 0 0-5 0l-3 3a3.54 3.54 0 1 0 5 5l1.4-1.4-1.42-1.42-1.4 1.4a1.54 1.54 0 0 1-2.18-2.18l3-3a1.54 1.54 0 0 1 2.18 0 1 1 0 0 0 1.42-1.4z"/></svg>',
      };
      el.innerHTML = `
        <div class="dk-share-row">
          <a class="dk-share-icon dk-share-wa" href="https://wa.me/?text=${shareFullText}" target="_blank" rel="noopener" aria-label="Share on WhatsApp" title="WhatsApp">${ico['wa']}</a>
          <a class="dk-share-icon dk-share-fb" href="https://www.facebook.com/sharer/sharer.php?u=${shareUrl}" target="_blank" rel="noopener" aria-label="Share on Facebook" title="Facebook">${ico['fb']}</a>
          <a class="dk-share-icon dk-share-x" href="https://x.com/intent/tweet?text=${shareText}&url=${shareUrl}" target="_blank" rel="noopener" aria-label="Share on X" title="X">${ico['x']}</a>
          <a class="dk-share-icon dk-share-em" href="mailto:?subject=${shareText}&body=${shareEmailBody}" aria-label="Share by email" title="Email">${ico['em']}</a>
          <button class="dk-share-icon dk-share-cp" id="dk-copy-link" aria-label="Copy share text" title="Copy share text">${ico['cp']}</button>
        </div>`;
      const copyOk = '<svg viewBox="0 0 24 24" aria-hidden="true"><path d="M9.55 17.6 4.4 12.45l1.42-1.42 3.73 3.73 8.23-8.23 1.42 1.42z"/></svg>';
      const copyBtn = el.querySelector<HTMLElement>('#dk-copy-link');
      const copyOrig = copyBtn?.innerHTML ?? '';
      copyBtn?.addEventListener('click', () => {
        navigator.clipboard.writeText(shareCopyText).then(() => {
          if (copyBtn) {
            copyBtn.innerHTML = copyOk;
            copyBtn.classList.add('dk-share-cp-ok');
            setTimeout(() => { copyBtn.innerHTML = copyOrig; copyBtn.classList.remove('dk-share-cp-ok'); }, 1500);
          }
        });
      });
      return;
    }

    const btnStyle = 'display:inline-flex;align-items:center;gap:4px;padding:6px 12px;border-radius:6px;font-size:12px;font-weight:500;text-decoration:none;border:1px solid var(--dk-border,#e5e7eb);color:var(--dk-text,#374151);cursor:pointer;background:transparent;';
    el.innerHTML = `
      <div style="margin:12px 0;display:flex;gap:8px;flex-wrap:wrap;justify-content:center;">
        <a href="https://wa.me/?text=${shareFullText}" target="_blank" rel="noopener" style="${btnStyle}">WhatsApp</a>
        <a href="https://www.facebook.com/sharer/sharer.php?u=${shareUrl}" target="_blank" rel="noopener" style="${btnStyle}">Facebook</a>
        <a href="https://x.com/intent/tweet?text=${shareText}&url=${shareUrl}" target="_blank" rel="noopener" style="${btnStyle}">X</a>
        <a href="mailto:?subject=${shareText}&body=${shareEmailBody}" style="${btnStyle}">Email</a>
        <button id="dk-copy-link" style="${btnStyle}">Copy Link</button>
      </div>`;
    el.querySelector('#dk-copy-link')?.addEventListener('click', () => {
      navigator.clipboard.writeText(shareCopyText).then(() => {
        const btn = el.querySelector<HTMLElement>('#dk-copy-link');
        if (btn) { btn.textContent = 'Copied!'; setTimeout(() => { btn.textContent = 'Copy Link'; }, 2000); }
      });
    });
  }

  private renderRecurringUpsell(): void {
    const el = this.container.querySelector<HTMLElement>('#dk-recurring-upsell');
    if (!el) return;
    const monthlyAmount = this.fmtCurrency(this.selectedAmount);
    el.innerHTML = `
      <div style="margin:12px 0;padding:12px;border:1px solid var(--dk-border,#e5e7eb);border-radius:8px;text-align:center;background:var(--dk-bg-secondary,#f9fafb);">
        <p style="font-size:13px;font-weight:500;color:var(--dk-text,#374151);margin:0 0 4px;">Make this monthly?</p>
        <p style="font-size:12px;color:var(--dk-text-muted,#6b7280);margin:0;">Give ${monthlyAmount} every month to make an even bigger impact.</p>
      </div>`;
  }

  public applyPreviewConfig(payload: { theme?: string; overrides?: Record<string, string>; config?: Partial<CheckoutConfig> }): void {
    // 1. Swap theme class first (sets base CSS variable values)
    if (payload.theme) {
      this.container.className = this.container.className.replace(/dk-theme-\w+/, `dk-theme-${payload.theme}`);
    }
    // 2. Update config and re-apply CSS-driven properties
    if (payload.config) {
      Object.assign(this.config, payload.config);
      if (this.config.fontFamily) {
        this.container.style.setProperty('--dk-font', this.config.fontFamily);
      } else {
        this.container.style.removeProperty('--dk-font');
      }
      if (this.config.borderRadius != null) {
        this.container.style.setProperty('--dk-radius', `${this.config.borderRadius}px`);
        this.container.style.setProperty('--dk-radius-lg', `${this.config.borderRadius + 4}px`);
      }
    }
    // 3. Apply explicit CSS overrides LAST — these win over everything
    if (payload.overrides) {
      Object.entries(payload.overrides).forEach(([key, value]) => {
        this.container.style.setProperty(`--dk-${key}`, value);
      });
    }
    // 4. Re-render DOM with updated config
    if (payload.config) {
      this.render();
    }
    this.notifyResize();
  }

  // ── Metadata builder ───────────────────────────────────────────────

  private buildDonationMetadata(): Record<string, unknown> | undefined {
    const meta: Record<string, unknown> = {};
    if (this.dedicationEnabled && this.dedication.honoree) {
      meta.dedication = {
        honoree: this.dedication.honoree,
        notify_email: this.dedication.notifyEmail || undefined,
      };
    }
    if (this.donorComment) {
      meta.donor_comment = this.donorComment;
    }
    return Object.keys(meta).length > 0 ? meta : undefined;
  }

  /** CHG-1782 — build the page-context envelope sent on the public
   *  payment-intent + donation calls. The backend re-resolves the render
   *  constraints from {context_type, entity_id, context_lock} and rejects
   *  an out-of-context designation. REQUIRED by the backend for the
   *  widget / donate-page sources. `designationId` is the donation's
   *  resolved designation (the locked fund on a locked page, or the
   *  donor's selection in ORG context). */
  private buildDonatePageContext(designationId?: string): DonatePageContextLite {
    const ct = (this.config.pageContextType || 'ORG').toUpperCase();
    if (ct === 'EVENT') {
      throw new Error(
        'Calendar events do not accept payments directly; use the linked giving session',
      );
    }
    const ctx: DonatePageContextLite = { context_type: ct };
    const sessionId = this.effectiveSessionId();
    let entityId: string | undefined;
    // CHG-2693 (DP-001/DP-002, EVENT) — for EVENT context the envelope's
    // entity_id is the EVENT row id (so the backend re-resolves event
    // render-constraints from it — forward-compatible with DP-003's EVENT
    // constraint resolver). The locked/selected designation rides in
    // designation_id, NOT entity_id. Pre-CHG-2693 an EVENT page set
    // entity_id to the selected designation (wrong; would 404 the event
    // lookup once DP-003 lands) or to the lock id.
    //
    // CHG-2702 (DP-001/DP-002, SESSION) — same shape for SESSION context: the
    // envelope's entity_id is the giving-session row id (forward-compatible
    // with DP-004's SESSION constraint resolver). The locked/selected
    // designation rides in designation_id, never entity_id. This envelope is
    // advisory at /donations/public (extra='ignore'); the load-bearing
    // attribution is the top-level PI-request session_id threaded above.
    if (ct === 'SESSION' && sessionId) {
      entityId = sessionId;
    } else if ((ct === 'PROGRAM' || ct === 'PROJECT') && this.config.pageContextEntityId) {
      entityId = this.config.pageContextEntityId;
    } else if (ct === 'CAMPAIGN') {
      // CHG-3001 (issue #196) — for CAMPAIGN context the envelope's entity_id
      // must be the CAMPAIGN row id so the server re-resolves CAMPAIGN render
      // constraints from it (resolve_render_constraints does `Campaign.id ==
      // entity_id`). This branch is ordered BEFORE the effectiveContextLock
      // branch on purpose: once a campaign with a linked designation seeds a
      // designation context_lock (the fund-lock fix), the lock branch below
      // would otherwise overwrite entity_id with the designation id and the
      // server would 404 the campaign lookup. The locked/selected designation
      // still rides in designation_id + context_lock (mirrors EVENT/SESSION).
      entityId = this.selectedCampaign?.id ?? this.config.campaignId ?? undefined;
    } else if (this.effectiveContextLock) {
      entityId = this.effectiveContextLock.id;
    } else if (designationId) {
      entityId = designationId;
    } else if (this.config.designationIds && this.config.designationIds.length === 1) {
      entityId = this.config.designationIds[0];
    }
    if (entityId) ctx.entity_id = entityId;
    // CHG-2702 — surface session_id explicitly. The authoritative attribution
    // is the top-level PI-request session_id.
    if (sessionId) ctx.session_id = sessionId;
    // Resolved designation: the donor-selected / locked fund. For an
    // EVENT with a designation lock this is the locked designation.
    const resolvedDesignationId =
      designationId ??
      (this.effectiveContextLock?.kind === 'designation'
        ? this.effectiveContextLock.id
        : undefined);
    if (resolvedDesignationId) ctx.designation_id = resolvedDesignationId;
    const campaignId = this.effectiveCampaignId();
    if (campaignId) ctx.campaign_id = campaignId;
    if (this.effectiveContextLock) {
      ctx.context_lock = {
        kind: this.effectiveContextLock.kind as 'designation' | 'campaign',
        id: this.effectiveContextLock.id,
      };
    }
    return ctx;
  }

  // ── Utilities ──────────────────────────────────────────────────────

  // CHG-3220 / ITER-905957 — per-currency minor<->major conversion bound to the
  // donor's ACTIVE currency. Replaces the legacy fixed `* 100` / `/ 100` used
  // everywhere in the checkout so zero-decimal (JPY/KRW) and three-decimal
  // (BHD/KWD) currencies mint the correct wire `amount_minor_units` instead of
  // 100x-overcharging / 10x-undercharging. Mirrors src/api/app/core/currency.py.
  private currencyCode(): string {
    return this.config.currency || 'USD';
  }

  private currencyExp(): number {
    return currencyExponent(this.currencyCode());
  }

  /** Donor-entered MAJOR amount -> integer MINOR-unit wire amount. */
  private toMinorUnits(amountMajor: number): number {
    return majorToMinorUnits(amountMajor, this.currencyCode());
  }

  /** MINOR-unit amount -> MAJOR value for display / fmtCurrency. */
  private toMajorUnits(amountMinor: number): number {
    return minorUnitsToMajor(amountMinor, this.currencyCode());
  }

  /** Round a MAJOR amount to the active currency's decimal places (fee math). */
  private roundMajor(amountMajor: number): number {
    const factor = 10 ** this.currencyExp();
    return Math.round(amountMajor * factor) / factor;
  }

  private computeFeeAmount(baseAmount: number): number {
    const pct = (this.config.coverFeesPercent ?? 3.0) / 100;
    const flatFee = this.toMajorUnits(this.config.coverFeesFlatMinorUnits ?? 30);
    return this.roundMajor(baseAmount * pct + flatFee);
  }

  private getTotalAmount(): number {
    if (this.confirmedTotalAmount !== null) {
      return this.confirmedTotalAmount;
    }
    if (this.coverFeesChecked && this.config.coverFeesEnabled) {
      return this.roundMajor(this.selectedAmount + this.computeFeeAmount(this.selectedAmount));
    }
    return this.selectedAmount;
  }

  private esc(str: string): string {
    return str
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  private fmtCurrency(amount: number): string {
    // CHG-3220 — cap fraction digits at the currency's exponent so zero-decimal
    // currencies (JPY/KRW) never render a spurious ".00" (Intl would otherwise
    // force 2 here) and three-decimal currencies (BHD/KWD) can show all 3.
    // minimumFractionDigits stays 0 so whole amounts render without trailing
    // zeros (e.g. "$5", not "$5.00") — preserving the existing widget UX.
    return new Intl.NumberFormat(this.config.locale || 'en-US', {
      style: 'currency',
      currency: this.currencyCode(),
      minimumFractionDigits: 0,
      maximumFractionDigits: this.currencyExp(),
    }).format(amount);
  }

  // CHG-M — vocabulary lookup with English fallback. The backend
  // serves an open-ended `vocabulary_overlay` dict from
  // /api/v1/public/brand/{org_id}; CHG-0155 P6 wired the widget to
  // merge it into config.vocabulary. This helper is the only place
  // the widget should read from config.vocabulary so the fallback
  // behavior is consistent across every user-facing string.
  // Reserved keys used by the checkout widget (all optional):
  //   donate_button                 CTA copy (pre-CHG-M)
  //   frequency_question            "How often?" picker heading
  //   frequency_step_label          progress-strip label for the frequency step
  //   frequency_one_time            picker label + summary + intent-card label
  //   frequency_monthly             picker label + summary line
  //   frequency_quarterly           picker label + summary line
  //   frequency_one_time_description intent-card subtitle for the one-time option
  //   frequency_most_impact         badge text on the recommended option
  //   frequency_recurring_benefit   "why monthly" microcopy under the cadence picker
  //   continue_button               picker confirm button
  //   payment_method_label          aria-label for the payment-method radio group
  //   payment_method_pay_by         second-line prefix before the platform method name
  //   --- donate-page v3 (CHG-3241) ---
  //   back_button                   v3 step "Back" action label (arrow is decorative)
  //   amount_step_eyebrow           amount-step eyebrow ("Your gift")
  //   amount_step_title             amount-step title ("Choose your gift")
  //   amount_step_sub               amount-step sub line
  //   amount_other_label            "Other amount" custom-input label
  //   donor_step_title              v3 grid donor-step title ("Your details")
  //   donor_contact_required        contact-or-anonymous guard message
  //   payment_pick_method           disabled pay-button label when no method picked
  //   confirm_eyebrow               v3 Done eyebrow ("Gift confirmed")
  //   confirm_thanks                v3 Done title ("Thank you, {first}")
  //   confirm_restart               "Make another donation" restart button
  //   offline_eyebrow               offline thank-you eyebrow ("Payment submitted")
  //   offline_status_val            offline status row value ("Awaiting verification")
  private vocab(key: string, fallback: string): string {
    const v = this.config.vocabulary?.[key];
    return typeof v === 'string' && v.length > 0 ? v : fallback;
  }

  // CHG-M — canonical frequency-label resolver. Maps the legacy
  // selectedFrequency enum to a donor-facing string via vocabulary +
  // English defaults. Replaces the previous
  // `charAt(0).toUpperCase() + slice(1)` trick which broke for
  // non-English labels (e.g. "মাসিক" → "মাসিক" unchanged, but
  // "monthly" with a custom label "Gift a month" would lose capital).
  private frequencyLabel(freq: 'one-time' | 'monthly' | 'quarterly' | 'annual'): string {
    switch (freq) {
      case 'one-time':
        return this.vocab('frequency_one_time', 'One-time');
      case 'monthly':
        return this.vocab('frequency_monthly', 'Monthly');
      case 'quarterly':
        return this.vocab('frequency_quarterly', 'Quarterly');
      case 'annual':
        return this.vocab('frequency_annual', 'Yearly');
    }
  }

  private getCurrencySymbol(): string {
    try {
      const parts = new Intl.NumberFormat(this.config.locale || 'en-US', {
        style: 'currency',
        currency: this.config.currency || 'USD',
        currencyDisplay: 'narrowSymbol',
      }).formatToParts(0);
      const sym = parts.find(p => p.type === 'currency');
      return sym?.value || (this.config.currency || 'USD') + ' ';
    } catch {
      return (this.config.currency || 'USD') + ' ';
    }
  }
}

// Expose globally so form.html and donate-page/checkout.js can access it
// after loading this file as a plain <script> (not ES module import)
(globalThis as typeof globalThis & { DonationCheckout: typeof DonationCheckout }).DonationCheckout =
  DonationCheckout;
